import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MapContainer, TileLayer, Marker, GeoJSON, useMap, LayersControl, LayerGroup, Popup } from 'react-leaflet';
import L from 'leaflet';
import { getParcel, getScores, getFeatures, getParcelMaxRecord, startDeepResearch, createBookmark, checkBookmark, deleteBookmark, createDownvote, checkDownvote, deleteDownvote, getNearbyMines, getCommodityScores, getParcelMiningClaims, getListingHistory, getListingMedia, getElevationProfile, getElevationGrid, getComparableParcels, getDashboardNews, getBuildableRetreatScreen, recordView, authHeaders } from '../services/api';
import ParcelTerrain3D from '../components/ParcelTerrain3D';
import LocalErrorBoundary from '../components/LocalErrorBoundary';
import InfoHint from '../components/InfoHint';
import DeepRecordSection from '../components/DeepRecord';
import DataSourcesAttribution from '../components/DataSourcesAttribution';
import { safeUrl } from '../services/urlSafety';
import { safeStopMap } from '../utils/leafletTeardown';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import ReportViewer from '../components/ReportViewer';
import FlagDataModal from '../components/FlagDataModal';
import { listDataFlagsForParcel } from '../services/flagsApi';
import useSortableTable from '../hooks/useSortableTable';
import SortableHeader from '../components/SortableHeader';
import useComparePins from '../hooks/useComparePins';
import CompareButton from '../components/CompareButton';
import { ownerColor, ownerStroke } from '../utils/ownerColor';
import { rescaleByPercentile } from '../utils/hazardPercentiles';
import {
  toDisplayPercent,
  mineralModelApplies,
  NOT_SCORED_HINT,
  NOT_SCORED_LABEL,
} from '../utils/scoreDisplay';
import { offeredAcreageInfo, offeredPricePerAcre, parcelLevelAcres } from '../utils/offeredAcreage';
import { trimExcerptToWords } from '../utils/snippet';
import GroundStabilityCard from '../components/GroundStabilityCard';
import ListingImageLightbox from '../components/ListingImageLightbox';
import PLACEHOLDER_IMG from '../utils/placeholderImage';
import { parcelPath } from '../utils/parcelPath';

/* ---------- TabNav for ParcelDetail v2 (#147 slice 1) ----------
 *
 * Non-invasive jump nav over the 24 h3 sections of the parcel detail
 * page. The full v2 ask (#147) is a 6-category restructure with a
 * compare drawer; this slice just adds the navigation so the user
 * can leap straight to Minerals / Hazards / Documents without
 * scrolling past the other 4,000px of content first.
 *
 * Tabs point at six anchor h3s via id="tab-{name}". Sticky-positioned
 * so it stays visible as the user scrolls. URL hash drives the
 * highlighted-active tab on mount.
 *
 * No content was reorganized — the 24 sections render in the same
 * order they always have. This is pure navigation.
 */
const PARCEL_DETAIL_TABS = [
  { id: 'tab-overview',   label: 'Overview' },
  { id: 'tab-investment', label: 'Investment' },
  { id: 'tab-land',       label: 'Land' },
  { id: 'tab-minerals',   label: 'Minerals' },
  { id: 'tab-hazards',    label: 'Hazards' },
  { id: 'tab-documents',  label: 'Docs' },
];

function ParcelDetailTabNav({ activeTab, onTabChange }) {
  return (
    <div
      className="parcel-detail-tab-nav"
      style={{
        background: '#fff',
        borderBottom: '1px solid #e1e4e8',
        padding: '6px 0',
        margin: 0,
        display: 'flex',
        gap: 4,
        overflowX: 'auto',
      }}
      role="navigation"
      aria-label="Parcel detail sections"
    >
      {PARCEL_DETAIL_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onTabChange(tab.id)}
          aria-pressed={activeTab === tab.id}
          style={{
            padding: '6px 14px',
            border: '1px solid transparent',
            background: activeTab === tab.id ? '#1976d2' : 'transparent',
            color: activeTab === tab.id ? '#fff' : '#1976d2',
            cursor: 'pointer',
            borderRadius: 4,
            fontWeight: activeTab === tab.id ? 600 : 500,
            fontSize: '0.85rem',
            whiteSpace: 'nowrap',
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- Hero card — sticky summary strip (#147 slice 3) ----------
 *
 * Always-visible compact strip that stays at the top of the viewport
 * as the user scrolls. Shows address, price, acres, score, and the
 * two most-used actions (Bookmark + Compare) so the user never needs
 * to scroll back to the top to act on a parcel.
 *
 * Rendered inside a sticky wrapper together with ParcelDetailTabNav
 * so both elements travel as a unit.
 */
function ParcelDetailHeroCard({ parcel, score, bookmarkStatus, onBookmark, isCompared, onCompareToggle }) {
  if (!parcel) return null;
  const price = parcel.listing_price;
  // #1546: the acreage on this card is the OFFERED tract's, not the
  // parcel's. When the offer could not be resolved the card says so
  // rather than showing the parent's number, which is not what is on
  // offer.
  const acreInfo = offeredAcreageInfo(parcel);
  const acres = acreInfo.acres;
  const address = parcel.address || parcel.parcel_id || '';
  const oScore = score?.score != null ? Math.round(score.score) : null;

  return (
    <div
      className="parcel-hero-card"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '7px 14px',
        background: '#1a1a2e',
        borderBottom: '1px solid #2d2d4e',
        flexWrap: 'wrap',
        minHeight: 44,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {address}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexShrink: 0 }}>
        {price != null && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700, color: '#FFD700', fontSize: '0.92rem' }}>{fmt$(price)}</div>
            <div style={{ color: '#9ca3af', fontSize: '0.65rem' }}>Price</div>
          </div>
        )}
        {(acres != null || acreInfo.unresolved) && (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{ fontWeight: 700, color: acres != null ? '#e2e8f0' : '#fbbf24', fontSize: '0.92rem' }}
              title={acreInfo.conflictLabel || undefined}
            >
              {acres != null ? fmtNum(acres) : 'Unresolved'}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.65rem' }}>Acres</div>
          </div>
        )}
        {oScore != null && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700, color: '#60a5fa', fontSize: '0.92rem' }}>{oScore}</div>
            <div style={{ color: '#9ca3af', fontSize: '0.65rem' }}>Score</div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button
          type="button"
          className="btn btn-outline"
          onClick={onBookmark}
          style={{
            padding: '3px 10px', fontSize: '0.78rem',
            ...(bookmarkStatus === 'added' ? { borderColor: '#10b981', color: '#10b981' } : { borderColor: '#6b7280', color: '#9ca3af' }),
          }}
        >
          {bookmarkStatus === 'added' ? '★ Saved' : '☆ Save'}
        </button>
        <button
          type="button"
          className="btn btn-outline"
          onClick={onCompareToggle}
          aria-pressed={isCompared}
          style={{
            padding: '3px 10px', fontSize: '0.78rem',
            ...(isCompared ? { borderColor: '#1976d2', color: '#1976d2', fontWeight: 600 } : { borderColor: '#6b7280', color: '#9ca3af' }),
          }}
        >
          {isCompared ? 'Comparing' : 'Compare'}
        </button>
      </div>
    </div>
  );
}

/* ---------- Diamond icon for mine markers ---------- */
const _diamondIconCache = {};
function getDiamondIcon(fillColor, borderColor) {
  const key = `${fillColor}-${borderColor}`;
  if (_diamondIconCache[key]) return _diamondIconCache[key];
  const svg = `<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="10" height="10" rx="1" transform="rotate(45 7 7)"
      fill="${fillColor}" stroke="${borderColor}" stroke-width="1.5"/>
  </svg>`;
  const icon = L.divIcon({
    html: svg,
    className: '',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });
  _diamondIconCache[key] = icon;
  return icon;
}

// Exported for unit testing. NOTE: this variant uses substring
// `.includes()` matching instead of the exact-match approach in
// MapView's getMineColor. That's intentional — parcel detail data
// sometimes has "Gold-silver" or "Gold (primary)" style strings
// that wouldn't match an exact equality check. Both variants are
// context-appropriate and should NOT be consolidated.
export function getMineColor(commodity) {
  const c = (commodity || '').toLowerCase();
  if (c.includes('gold'))    return { fill: '#FFD700', border: '#B8860B' };
  if (c.includes('copper'))  return { fill: '#B87333', border: '#8B4513' };
  if (c.includes('silver'))  return { fill: '#C0C0C0', border: '#808080' };
  if (c.includes('uranium')) return { fill: '#7FFF00', border: '#228B22' };
  if (c.includes('lithium')) return { fill: '#00CED1', border: '#008B8B' };
  if (c.includes('iron'))    return { fill: '#A52A2A', border: '#800000' };
  if (c.includes('zinc'))    return { fill: '#87CEEB', border: '#4682B4' };
  if (c.includes('lead'))    return { fill: '#708090', border: '#2F4F4F' };
  return { fill: '#ff9900', border: '#cc6600' };
}

const STATUS_COLORS = {
  'Producer':      { bg: '#27ae60', text: '#fff' },
  'Past Producer': { bg: '#e67e22', text: '#fff' },
  'Prospect':      { bg: '#3498db', text: '#fff' },
  'Occurrence':    { bg: '#9b59b6', text: '#fff' },
  'Plant':         { bg: '#95a5a6', text: '#fff' },
  'Unknown':       { bg: '#bdc3c7', text: '#333' },
};

/* ---------- Federal land agency labels ---------- */
const AGENCY_LABELS = {
  BLM:  'Bureau of Land Management',
  USFS: 'US Forest Service',
  FS:   'US Forest Service',
  NPS:  'National Park Service',
  FWS:  'US Fish & Wildlife Service',
  BOR:  'Bureau of Reclamation',
  USBR: 'Bureau of Reclamation',
  BIA:  'Bureau of Indian Affairs',
  DOD:  'Dept. of Defense',
  DOE:  'Dept. of Energy',
};

const AGENCY_COLORS = {
  BLM:  '#F4A460',
  USFS: '#228B22',
  FS:   '#228B22',
  NPS:  '#6B8E23',
  FWS:  '#4682B4',
  BOR:  '#4169E1',
  USBR: '#4169E1',
  BIA:  '#CD853F',
  DOD:  '#808080',
  DOE:  '#DAA520',
};

/* ---------- unit conversion helpers ---------- */
const KM_TO_MI = 0.621371;

/* ---------- ParcelCakeView: isometric 3D prism of parcel terrain ----------
 * Exported for unit testing. Renders an SVG isometric prism using the
 * parcel polygon and elevation range. No external libraries required.
 * Returns null when elevation data is absent or range < 5 m. */
export function ParcelCakeView({ geometry, elevationRange, elevationGrid }) {
  if (!geometry || elevationRange == null || elevationRange < 2) return null;

  let coords;
  if (geometry.type === 'Polygon') {
    coords = geometry.coordinates[0];
  } else if (geometry.type === 'MultiPolygon') {
    coords = geometry.coordinates[0][0];
  } else {
    return null;
  }
  if (!coords || coords.length < 4) return null;

  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lon, lat] of coords) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  const latMid = (minLat + maxLat) / 2;
  const lonCos = Math.cos(latMid * Math.PI / 180);
  const wM = (maxLon - minLon) * lonCos * 111320;
  const hM = (maxLat - minLat) * 110540;
  const SIZE = 160;
  const larger = Math.max(wM, hM) || 1;
  const nx = (wM / larger) * SIZE;
  const ny = (hM / larger) * SIZE;
  const spanLon = maxLon - minLon || 1;
  const spanLat = maxLat - minLat || 1;

  const pts = coords.map(([lon, lat]) => [
    ((lon - minLon) / spanLon) * nx,
    ((maxLat - lat) / spanLat) * ny,
  ]);

  // Vertical exaggeration. A geometrically-honest scale (elevation /
  // horizontal-extent) collapses any "wide gentle" parcel into a
  // pancake — a 16,825-acre ranch with 12m of relief comes out at
  // 0.18 pixels of extrusion before clamping. That's true to scale
  // but useless to the user, who can't tell whether 12m is
  // meaningful or trivial.
  //
  // Switch to an absolute reference scale: at REF_RANGE meters of
  // relief the prism reaches its max height; below that, a sqrt
  // easing keeps small ranges visible without making every parcel
  // look like a mountain. Coupled with a higher minimum, even a 5m
  // ridge shows a clear 3D step.
  const REF_RANGE_M = 200;
  const MIN_H = 22;
  const MAX_H = 90;
  const ratio = Math.min(1, Math.sqrt(Math.max(0, elevationRange) / REF_RANGE_M));
  const extH = Math.max(MIN_H, MIN_H + (MAX_H - MIN_H) * ratio);

  const iso = ([x, y], z = 0) => [
    (x - y) * 0.866,
    (x + y) * 0.5 - z,
  ];

  const bot = pts.map(p => iso(p, 0));
  const top = pts.map(p => iso(p, extH));

  const all = [...bot, ...top];
  const pad = 4;
  const vxMin = Math.min(...all.map(p => p[0])) - pad;
  const vyMin = Math.min(...all.map(p => p[1])) - pad;
  const vxMax = Math.max(...all.map(p => p[0])) + pad;
  const vyMax = Math.max(...all.map(p => p[1])) + pad;

  const n = pts.length - 1;
  const sides = [];
  for (let i = 0; i < n; i++) {
    const dx = pts[i + 1][0] - pts[i][0];
    const dy = pts[i + 1][1] - pts[i][1];
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const fnx = dy / len, fny = -dx / len;
    const dot = fnx * 1 + fny * (-0.5);
    const shade = Math.min(1, Math.max(0.2, 0.5 + dot * 0.4));
    const r = Math.round(90 * shade + 40);
    const g = Math.round(70 * shade + 20);
    const b = Math.round(40 * shade + 10);
    sides.push({
      b0: bot[i], b1: bot[i + 1], t0: top[i], t1: top[i + 1],
      fill: `rgb(${r},${g},${b})`,
      depth: pts[i][0] + pts[i][1] + pts[i + 1][0] + pts[i + 1][1],
    });
  }
  sides.sort((a, b_) => b_.depth - a.depth);

  const p2s = ps => ps.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const q2s = ([b0, b1, t1, t0]) => [b0, b1, t1, t0].map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

  // Triangulated relief mesh on the top surface (#141 follow-up).
  // When elevationGrid is provided by the /elevation-grid endpoint
  // we render the top as a mesh of size×size cells, each split into
  // two triangles. The Z of each vertex is interpolated from the
  // grid's elevation values normalized into [0..extH]. Without the
  // grid the top stays flat (existing behavior).
  let topMesh = null;
  if (elevationGrid && elevationGrid.cells && elevationGrid.size >= 2) {
    const gs = elevationGrid.size;
    const eMin = elevationGrid.min_elevation_m;
    const eMax = elevationGrid.max_elevation_m;
    const eRange = Math.max(1, eMax - eMin);  // avoid divide-by-zero
    // Cells are returned row-major; build a (size)×(size) lookup
    // indexed by [row][col]. row 0 = south, col 0 = west.
    const elevByRC = Array.from({ length: gs }, () => Array(gs).fill(null));
    for (const c of elevationGrid.cells) {
      if (c.elevation_m != null) elevByRC[c.row][c.col] = c.elevation_m;
    }
    // Bbox the grid samples cover the parcel's full lat/lon extent.
    // Map (row, col) → SVG-plane (x, y) using the same bbox as the
    // bottom polygon. row=size-1 is the *north* edge (max_lat);
    // because our SVG y grows downward we flip it to (maxLat-lat).
    const cell2pt = (row, col) => {
      const fracLon = (col + 0.5) / gs;
      const fracLat = (row + 0.5) / gs;
      // mirror the bot polygon's projection
      const x = fracLon * nx;
      const y = (1 - fracLat) * ny;
      const elevM = elevByRC[row][col];
      // Per-vertex extrusion height: same vertical exaggeration as
      // the prism overall, scaled by where this cell falls in the
      // elevation range. Below-min cells (NoData) render at the
      // prism floor (no relief contribution).
      const zNorm = elevM == null ? 0 : (elevM - eMin) / eRange;
      const z = extH * zNorm;
      return { iso: iso([x, y], z), elevM, x, y };
    };
    const grid = Array.from({ length: gs }, (_, r) =>
      Array.from({ length: gs }, (_, c) => cell2pt(r, c)),
    );
    // Two triangles per cell: (r,c)–(r+1,c)–(r,c+1) and
    // (r+1,c)–(r+1,c+1)–(r,c+1). Color by mean elevation for the
    // triangle (terra-cotta low → ochre → sienna high), with a
    // light directional shade from facet normal.
    const tris = [];
    for (let r = 0; r < gs - 1; r++) {
      for (let c = 0; c < gs - 1; c++) {
        const a = grid[r][c], b = grid[r + 1][c], cc = grid[r][c + 1], d = grid[r + 1][c + 1];
        const meanZ1 = ((a.elevM ?? eMin) + (b.elevM ?? eMin) + (cc.elevM ?? eMin)) / 3;
        const meanZ2 = ((b.elevM ?? eMin) + (d.elevM ?? eMin) + (cc.elevM ?? eMin)) / 3;
        // Color ramp: low = sandy (#d4a464), high = sienna (#7a3f17).
        const colorAt = (z) => {
          const t = Math.max(0, Math.min(1, (z - eMin) / eRange));
          const r0 = 0xd4, g0 = 0xa4, b0 = 0x64;
          const r1 = 0x7a, g1 = 0x3f, b1 = 0x17;
          const R = Math.round(r0 + (r1 - r0) * t);
          const G = Math.round(g0 + (g1 - g0) * t);
          const B = Math.round(b0 + (b1 - b0) * t);
          return `rgb(${R},${G},${B})`;
        };
        // Depth = sum of (x+y) for z-sort. Triangles with greater
        // x+y are "behind" in our isometric projection — they get
        // drawn first so foreground triangles paint on top.
        const tri1Depth = a.x + a.y + b.x + b.y + cc.x + cc.y;
        const tri2Depth = b.x + b.y + d.x + d.y + cc.x + cc.y;
        tris.push({
          pts: [a.iso, b.iso, cc.iso],
          fill: colorAt(meanZ1),
          depth: tri1Depth,
        });
        tris.push({
          pts: [b.iso, d.iso, cc.iso],
          fill: colorAt(meanZ2),
          depth: tri2Depth,
        });
      }
    }
    tris.sort((a, b) => b.depth - a.depth);
    topMesh = tris;
  }

  return (
    <div style={{
      padding: '12px 14px', borderRadius: 8, marginBottom: 10,
      background: 'linear-gradient(135deg, #1a1a2e10, #1a1a2e06)',
      border: '1px solid #1a1a2e30',
    }} data-testid="parcel-cake-view">
      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#4a5568', fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>
        3D Terrain View
      </div>
      <svg
        viewBox={`${vxMin.toFixed(1)} ${vyMin.toFixed(1)} ${(vxMax - vxMin).toFixed(1)} ${(vyMax - vyMin).toFixed(1)}`}
        style={{ width: '100%', maxHeight: 200, display: 'block' }}
        aria-label="3D extruded parcel prism showing terrain relief"
      >
        <polygon points={p2s(bot)} fill="#2d1f14" />
        {sides.map(({ b0, b1, t0, t1, fill }, i) => (
          <polygon key={i} points={q2s([b0, b1, t1, t0])} fill={fill} stroke="#1a0f0a" strokeWidth="0.3" />
        ))}
        {topMesh ? (
          // Triangulated relief mesh — back-to-front painted so
          // foreground tris cover background ones.
          topMesh.map((t, i) => (
            <polygon
              key={i}
              points={p2s(t.pts)}
              fill={t.fill}
              stroke="#5a3010"
              strokeWidth="0.15"
            />
          ))
        ) : (
          // Fallback flat top when the grid endpoint hasn't returned
          // yet (or returned 404 — outside DEM coverage).
          <polygon points={p2s(top)} fill="#c8a060" stroke="#8B6914" strokeWidth="0.5" />
        )}
      </svg>
      <div style={{ fontSize: '0.7rem', color: '#888', textAlign: 'center', marginTop: 4 }}>
        Relief: {Math.round(elevationRange * 3.28084)} ft vertical
        {topMesh && (
          <span style={{ color: '#aaa', marginLeft: 6 }}>· textured from DEM</span>
        )}
      </div>
    </div>
  );
}

// safeStopMap lifted to utils/leafletTeardown (#1060) so the search-page
// map (components/MapView.js) can reuse the exact same guard. Re-exported
// here for backward compatibility with existing imports/tests.
export { safeStopMap };

/* ---------- FitBounds: auto-zoom map to GeoJSON boundary ----------
 * Exported for unit testing. Returns null (renders nothing) — the
 * component exists purely for the useEffect side effect of calling
 * map.fitBounds when a valid geojson boundary is available. */
export function FitBounds({ geojson }) {
  const map = useMap();
  useEffect(() => {
    if (!geojson) return undefined;
    try {
      const layer = L.geoJSON(geojson);
      const bounds = layer.getBounds();
      if (bounds.isValid() && map.getPane('mapPane')) {
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 17 });
      }
    } catch (e) { /* ignore invalid geojson */ }
    // Route changes can unmount the hero map while Leaflet is still
    // finishing its zoom transition. Abort any in-flight animation so
    // _onZoomTransitionEnd never reads a torn-down map pane (#814, #815).
    return () => safeStopMap(map);
  }, [geojson, map]);
  return null;
}

/* ---------- helpers ---------- */
// Exported for unit testing. Used throughout the component for
// formatting listing prices, acreage, dates, etc.
export function fmt$(v) {
  if (v == null) return null;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Number(v).toLocaleString()}`;
}
export function fmtNum(v, digits = 1) {
  if (v == null) return 'N/A';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });
}
export function fmtDate(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleDateString(); } catch { return v; }
}
// Re-exported from utils/format for backward compatibility — leaf
// modules should import directly from there to avoid dragging in
// ParcelDetail's heavy deps (Leaflet, recharts, etc.).
import { fmtDom as _fmtDom } from '../utils/format';
export const fmtDom = _fmtDom;
// Buildability confidence value -> chip color. Mirrors the palette on
// the Buildable-retreat screen (issue #106). Water also emits
// well/spring/rights/none; the positive water variants get the
// "high" tone and `none` the negative tone.
const BUILDABILITY_CONFIDENCE_TONE = {
  high: { bg: '#d1fae5', fg: '#065f46' },
  medium: { bg: '#fef3c7', fg: '#92400e' },
  low: { bg: '#fee2e2', fg: '#991b1b' },
  none: { bg: '#fee2e2', fg: '#991b1b' },
  well: { bg: '#d1fae5', fg: '#065f46' },
  spring: { bg: '#d1fae5', fg: '#065f46' },
  rights: { bg: '#d1fae5', fg: '#065f46' },
  unknown: { bg: '#e5e7eb', fg: '#374151' },
};

// Exported for unit testing. Small colored chip for a buildability
// confidence value (access / water / power / build_site).
export function BuildabilityChip({ label, value }) {
  const key = (value || 'unknown').toLowerCase();
  const tone = BUILDABILITY_CONFIDENCE_TONE[key] || BUILDABILITY_CONFIDENCE_TONE.unknown;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
      <span style={{ fontSize: '0.68rem', textTransform: 'uppercase', color: '#888', fontWeight: 700, letterSpacing: 0.4 }}>
        {label}
      </span>
      <span
        style={{
          background: tone.bg,
          color: tone.fg,
          padding: '2px 8px',
          borderRadius: 4,
          fontSize: '0.72rem',
          fontWeight: 600,
          textTransform: 'capitalize',
        }}
        data-testid={`buildability-chip-${label.toLowerCase()}`}
      >
        {value || 'unknown'}
      </span>
    </span>
  );
}

// Exported for unit testing. Renders a labeled horizontal progress
// bar for a 0-1 score value. Returns null when value is null so
// the parent can render conditionally without an extra wrapper.
export function ScoreBar({ label, value, color, scale = 'auto', notScored = false }) {
  // #1288: no model covers this parcel's state. Say "Not scored" with an
  // empty hatched track, instead of hiding the row (reads as "not loaded")
  // or drawing 0% (reads as "measured and nothing there").
  if (notScored) {
    return (
      <div style={{ marginBottom: 10 }} data-testid="score-bar-not-scored" title={NOT_SCORED_HINT}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 3 }}>
          <span>{label}</span>
          <span style={{ fontWeight: 600, fontStyle: 'italic', color: '#6b7280' }}>{NOT_SCORED_LABEL}</span>
        </div>
        <div style={{
          height: 8,
          borderRadius: 4,
          background: 'repeating-linear-gradient(-45deg, #e0e0e0, #e0e0e0 4px, #f3f3f3 4px, #f3f3f3 8px)',
        }} />
      </div>
    );
  }
  // #993: normalize to a 0-100 axis. Model outputs are 0-1 fractions
  // (mineral/value/geothermal); the geophysical feasibility scores
  // (solar/wind/hydro) are already 0-100. The old `value * 100` treated
  // every value as a fraction, so a solar_score of 85 rendered "8500%".
  const norm = toDisplayPercent(value, scale);
  if (norm == null) return null;
  const pct = Math.round(norm);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 3 }}>
        <span>{label}</span><span style={{ fontWeight: 700 }}>{pct}%</span>
      </div>
      <div style={{ height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color || '#e94560', borderRadius: 4 }} />
      </div>
    </div>
  );
}

// Module-level constant sets so they're not rebuilt every render.
// Used by formatFeatureValue inside ParcelDetail. (#81 H3)
const KM_TO_MI_FEATURE_KEYS = new Set([
  'distance_to_mine', 'distance_to_fault', 'distance_to_road',
  'distance_to_power', 'distance_to_water', 'nearest_well_km',
  'nearest_spring_km',
]);
// Synthetic-noise feature columns hidden until backed by real data
// (rng.uniform() output from generate_features.py — see #92).
const HIDDEN_FEATURE_KEYS = new Set([
  'distance_to_road', 'distance_to_power', 'distance_to_power_line',
  'distance_to_water',
  // SAR-derived ground-cover signals get their own Ground & Vegetation
  // card (#177) — don't double-render them in the raw Feature Data
  // dump where they'd be without context.
  'vegetation_density_score', 'built_up_likelihood',
  'recent_disturbance_score', 'land_cover_class', 'seasonal_snow_pct',
  // Hansen Global Forest Change rollups have their own Forest Cover
  // card on the Land tab — hiding here keeps the generic Feature Data
  // table from duplicating them.
  'forest_cover_2000_pct', 'forest_loss_pct',
  'forest_loss_year_most_recent', 'forest_loss_year_majority',
  'forest_regrowth_pct',
  // Current-canopy columns (migration 0099) also live on the Forest
  // Cover card — derived Hansen-present estimate + NLCD 2021 sample.
  'forest_cover_present_estimate_pct', 'nlcd_tcc_2021_pct',
]);
const C_TO_F_FEATURE_KEYS = new Set([
  'nearest_well_bht', 'nearest_spring_temp_c', 'avg_bht_50km',
]);

// Feature display names (imperial units). Hoisted to module scope so the
// 26-key object isn't reallocated on every render. (#perf #3)
const FEATURE_LABELS = {
  distance_to_mine: 'Dist. to Nearest Mine (mi)',
  distance_to_fault: 'Dist. to Nearest Fault (mi)',
  num_mines_5mi: 'Mines within 5 mi',
  num_claims_10mi: 'Mining Claims within 10 mi',
  geology_code: 'Geology Code',
  magnetic_anomaly_mean: 'Magnetic Anomaly (centroid nT)',
  magnetic_anomaly_max: 'Magnetic Anomaly (parcel max nT)',
  magnetic_anomaly_source: 'Magnetic Anomaly Source',
  thermal_anomaly_mean: 'Thermal Anomaly (mean)',
  thermal_anomaly_max: 'Thermal Anomaly (max)',
  elevation_mean: 'Elevation (ft)',
  slope_mean: 'Average Slope',
  aspect_mean: 'Average Aspect',
  land_cover_code: 'Land Cover Code',
  distance_to_road: 'Dist. to Nearest Road (mi)',
  distance_to_power: 'Dist. to Power Line (mi)',
  distance_to_water: 'Dist. to Water (mi)',
  precipitation_annual: 'Annual Precipitation (in)',
  temperature_mean: 'Mean Temperature (°F)',
  nearest_well_km: 'Nearest Geothermal Well (mi)',
  nearest_well_bht: 'Nearest Well BHT (°F)',
  avg_bht_50km: 'Avg BHT within 30mi (°F)',
  num_wells_50km: 'Geothermal Wells within 30mi',
  nearest_spring_km: 'Nearest Thermal Spring (mi)',
  nearest_spring_temp_c: 'Nearest Spring Temp (°F)',
  gold_mines_25mi: 'Gold Mines within 25mi',
  copper_mines_25mi: 'Copper Mines within 25mi',
  silver_mines_25mi: 'Silver Mines within 25mi',
  uranium_mines_25mi: 'Uranium Mines within 25mi',
  // SAR backscatter / polarization — feed mineral & geothermal scorers
  // and show *why* a parcel scored as it did. Temporal_std signals
  // active surface disturbance; vv/vh ratios discriminate vegetation
  // vs. bare rock vs. man-made.
  sar_vv_mean: 'SAR VV backscatter (mean dB)',
  sar_vh_mean: 'SAR VH backscatter (mean dB)',
  sar_vv_vh_ratio: 'SAR VV/VH ratio',
  sar_cross_pol_ratio: 'SAR cross-pol ratio',
  sar_vv_temporal_std: 'SAR VV temporal std',
  sar_vh_temporal_std: 'SAR VH temporal std',
  // SAR-derived ground-cover signals (#177 / migration 0092). Surfaced
  // in their own Ground & Vegetation card below, but kept labeled here
  // so the Feature Data card renders human-readable names if/when the
  // user expands the raw feature dump.
  vegetation_density_score: 'Vegetation Density (0-100)',
  built_up_likelihood: 'Built-up Likelihood (0-100)',
  recent_disturbance_score: 'Recent Disturbance (0-100)',
  land_cover_class: 'Land Cover Class',
  seasonal_snow_pct: 'Seasonal Snow (% of year)',
  // Thermal & vegetation
  aster_thermal_index_mean: 'ASTER thermal index (mean)',
  landsat_ndvi_mean: 'Landsat NDVI (mean)',
  ecostress_temperature_mean: 'ECOSTRESS surface temp (°F)',
  hydrology_zone: 'Hydrology Zone',
};

// Periodic-table tile descriptors for the Mineral Probability grid.
// Module-level so the 7-entry array isn't rebuilt on every render of
// the parcel detail page.
const COMMODITY_TILES = [
  { key: 'gold',    symbol: 'Au', number: 79, name: 'Gold',     color: '#FFD700', border: '#B8860B' },
  { key: 'copper',  symbol: 'Cu', number: 29, name: 'Copper',   color: '#B87333', border: '#8B4513' },
  { key: 'silver',  symbol: 'Ag', number: 47, name: 'Silver',   color: '#C0C0C0', border: '#808080' },
  { key: 'uranium', symbol: 'U',  number: 92, name: 'Uranium',  color: '#7FFF00', border: '#228B22' },
  { key: 'lithium', symbol: 'Li', number: 3,  name: 'Lithium',  color: '#00CED1', border: '#008B8B' },
  { key: 'ree',     symbol: 'RE', number: '*', name: 'Rare Earth', color: '#FF69B4', border: '#C71585' },
  { key: 'niobium', symbol: 'Nb', number: 41, name: 'Niobium',  color: '#9370DB', border: '#5B4B9C' },
];

// Exported for unit testing. A key/value row used throughout the
// parcel detail page. Drops rows whose value is null, empty string,
// or the literal "N/A" so callers can dump every field without
// cluttering the UI when data is partially populated.
export function InfoRow({ label, value, full }) {
  if (value == null || value === '' || value === 'N/A') return null;
  return (
    <div style={{ gridColumn: full ? '1 / -1' : undefined, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#999', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: '0.92rem', fontWeight: 500, color: '#222', wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

// Evidence age for the federal mineral-rights records (#1539). The records
// are a severance verdict; this says when a source pull last confirmed them,
// so a record from a source that has since been shut down does not read like
// one confirmed this morning. It changes no score. Exported for unit testing.
export function FederalRecordEvidenceAge({ records }) {
  if (!records || !records.observed_at) return null;
  const asOf = new Date(records.observed_at);
  const when = Number.isNaN(asOf.getTime())
    ? records.observed_at
    : asOf.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  const days = records.age_days;
  let age = '';
  if (days === 0) age = ' (today)';
  else if (days != null) age = ` (${days} day${days === 1 ? '' : 's'} ago)`;
  const budget = records.stale_after_days ?? 30;
  const superseded = (records.sources || []).some((s) => s.superseded);
  return (
    <div
      data-testid="federal-evidence-age"
      style={{ fontSize: '0.8rem', color: '#555', marginTop: 8 }}
    >
      <div>Evidence as of {when}{age}</div>
      {records.stale === true && (
        <div style={{ color: '#a85d00', fontWeight: 600 }}>
          Not re-confirmed by its source in over {budget} days. Verify it is still current.
        </div>
      )}
      {superseded && (
        <div style={{ color: '#a85d00' }}>
          Some of these records were not in their source&apos;s latest pull.
        </div>
      )}
    </div>
  );
}

// Uruguay water rights + hydrography (#1257). Hoisted out of the main
// render because the panel has real branching: DINAGUA publishes two
// permit layers and only one of them carries a padrón, so the same
// record can reach a parcel as an *entitlement* or as a *neighbour*,
// and those two must never be shown as the same thing.
export function UYWaterPanel({ water }) {
  if (!water) return null;

  const attributed = (water.nearby_features || []).filter((f) => f.padron_match);
  const nearby = (water.nearby_features || []).filter((f) => !f.padron_match);

  const fmtDistance = (m) => {
    if (m == null) return null;
    return m < 1 ? 'on parcel' : `${fmtNum(m, 0)} m`;
  };

  const renderFeature = (f, i) => {
    // Order matters: the quantities a buyer acts on come first, the
    // provenance identifiers last.
    const bits = [
      f.use,
      f.destination && f.destination !== f.use ? f.destination : null,
      f.annual_volume_m3 != null ? `${fmtNum(f.annual_volume_m3, 0)} m³/yr` : null,
      f.flow_m3_h != null ? `${fmtNum(f.flow_m3_h, 1)} m³/h` : null,
      f.depth_m != null ? `${fmtNum(f.depth_m, 0)} m deep` : null,
      f.regime,
      f.watercourse,
      f.status,
    ].filter(Boolean);
    return (
      <div
        key={`${f.source_layer}-${f.external_id}-${i}`}
        data-testid="uy-water-feature"
        style={{
          padding: '6px 0', borderBottom: '1px solid #f0f0f0',
          display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap',
        }}
      >
        <span style={{
          padding: '2px 7px', borderRadius: 4, fontSize: '0.7rem',
          fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4,
          background: '#e3f2fd', color: '#1565c0', flexShrink: 0,
        }}>{f.feature_type}</span>
        <span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#222' }}>
          {f.name || f.permit_ref || f.external_id}
        </span>
        {bits.length > 0 && (
          <span style={{ fontSize: '0.78rem', color: '#666' }}>{bits.join(' · ')}</span>
        )}
        {fmtDistance(f.distance_m) && (
          <span style={{ fontSize: '0.75rem', color: '#999', marginLeft: 'auto' }}>
            {fmtDistance(f.distance_m)}
          </span>
        )}
      </div>
    );
  };

  const sources = [...new Set((water.nearby_features || []).map((f) => f.attribution))];

  return (
    <section>
      <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
        <span>Water Rights &amp; Hydrography</span>
        <InfoHint label="About Uruguay Water Rights">
          <strong>Diligence:</strong> a permit listed under
          &quot;Recorded against this padrón&quot; is registered to this
          property in DINAGUA&apos;s in-force register. Everything under
          &quot;Within {water.nearby_radius_m} m&quot; is a neighbouring
          record: it shows water is present in the area but conveys no
          entitlement to this parcel. Verify any right directly with
          DINAGUA before relying on it. Sources: DINAGUA (permits,
          wells) and IDEuy 1:10,000 hydrography (streams, ponds).
        </InfoHint>
      </h3>
      <div className="detail-card">
        <div className="info-grid">
          <InfoRow
            label="Rights on this Padrón"
            value={water.padron_water_rights_count != null
              ? water.padron_water_rights_count : null}
          />
          <InfoRow label="Wells on Parcel" value={water.wells_on_parcel} />
          <InfoRow
            label={`Wells within ${water.nearby_radius_m} m`}
            value={water.wells_within_500m}
          />
          <InfoRow
            label="Ponds / Tajamares"
            value={water.on_parcel_pond_count != null
              ? `${water.on_parcel_pond_count}${water.on_parcel_pond_area_m2 != null
                ? ` · ${fmtNum(water.on_parcel_pond_area_m2, 0)} m²` : ''}`
              : null}
          />
          <InfoRow
            label="Nearest Stream"
            value={water.dist_nearest_stream_km != null
              ? `${fmtNum(water.dist_nearest_stream_km, 2)} km` : null}
          />
        </div>

        {attributed.length > 0 && (
          <div style={{ marginTop: 12 }} data-testid="uy-water-attributed">
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#2e7d32', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4 }}>
              Recorded against this padrón
            </div>
            {attributed.map(renderFeature)}
          </div>
        )}

        {nearby.length > 0 && (
          <div style={{ marginTop: 12 }} data-testid="uy-water-nearby">
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#999', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4 }}>
              {`Within ${water.nearby_radius_m} m · not an entitlement`}
            </div>
            {nearby.map(renderFeature)}
          </div>
        )}

        {water.nearby_unavailable && (
          <div data-testid="uy-water-unavailable" style={{ marginTop: 12, fontSize: '0.8rem', color: '#e67e22' }}>
            Water-feature lookup unavailable for this parcel. This is not
            a finding of &quot;no water&quot;.
          </div>
        )}

        {sources.length > 0 && (
          <div style={{ marginTop: 10, fontSize: '0.7rem', color: '#aaa' }}>
            {sources.join(' · ')}
          </div>
        )}
      </div>
    </section>
  );
}

// Encumbrances table, hoisted into its own component so the
// `useSortableTable` hook can be called unconditionally even though
// the encumbrances section itself is rendered behind a length>0 gate.
function EncumbrancesTable({ encumbrances }) {
  const extractors = useMemo(() => ({
    // Render-equivalent sort: type and subtype are shown joined in the
    // same cell, so sorting by the rendered string keeps the column
    // visually consistent.
    type: (e) => (e.subtype ? `${e.type} (${e.subtype})` : e.type),
    holder: (e) => e.holder,
    recorded_date: (e) => e.recorded_date,
    amount_usd: (e) => e.amount_usd,
  }), []);
  const { sortedRows, sortKey, sortDirection, requestSort } = useSortableTable(
    encumbrances, null, 'desc', extractors,
  );
  return (
    <table style={{ width: '100%', fontSize: '0.85rem' }}>
      <thead>
        <tr>
          <SortableHeader sortKey="type" activeKey={sortKey} direction={sortDirection} onSort={requestSort} style={{ textAlign: 'left' }}>Type</SortableHeader>
          <SortableHeader sortKey="holder" activeKey={sortKey} direction={sortDirection} onSort={requestSort} style={{ textAlign: 'left' }}>Holder</SortableHeader>
          <SortableHeader sortKey="recorded_date" activeKey={sortKey} direction={sortDirection} onSort={requestSort} style={{ textAlign: 'right' }} align="right">Recorded</SortableHeader>
          <SortableHeader sortKey="amount_usd" activeKey={sortKey} direction={sortDirection} onSort={requestSort} style={{ textAlign: 'right' }} align="right">Amount</SortableHeader>
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((e, i) => (
          <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
            <td style={{ padding: '4px 0' }}>
              {e.type}
              {e.subtype && <span style={{ color: '#888' }}> ({e.subtype})</span>}
            </td>
            <td>{e.holder || '—'}</td>
            <td align="right">{e.recorded_date || '—'}</td>
            <td align="right">
              {e.amount_usd != null ? `$${e.amount_usd.toLocaleString()}` : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ---------- Neighbors / Owner-portfolio cards (#703 / task #130) ----------
   Two small lazy-loaded cards that surface the surrounding parcel context
   the owner asked for in #703: "what's next-door, who owns what's nearby,
   and what else this owner has in the region." Both render compact rows
   with a clickable navigation to the neighbor's parcel page so a bundle-
   deal user can hop between them. */

function NeighborRow({ row, navigate, showDistance = true }) {
  const acres = row.area_acres != null ? row.area_acres.toFixed(1) : '?';
  return (
    <div
      className="pd-neighbor-row"
      onClick={() => navigate(parcelPath(row.parcel_id, { state: row.state, county: row.county }))}
      style={{
        display: 'grid',
        gridTemplateColumns: showDistance
          ? '60px 1fr 60px 90px'
          : '1fr 60px 90px',
        gap: 8, padding: '6px 4px',
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer', fontSize: '0.85rem',
        alignItems: 'center',
        minHeight: 44,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {showDistance && (
        <span style={{ color: '#888', fontFamily: 'ui-monospace, monospace' }}>
          {row.dist_km != null ? `${row.dist_km.toFixed(2)}km` : '—'}
        </span>
      )}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {/* Owner-color pill links this row to its polygon on the map */}
        <OwnerPill owner={row.owner} />
        <strong>{row.parcel_id}</strong>
        {row.listing_address && (
          <span style={{ color: '#666', fontSize: '0.78rem' }}>  · {row.listing_address}</span>
        )}
      </span>
      <span style={{ color: '#666', textAlign: 'right' }}>{acres}ac</span>
      <span style={{
        textAlign: 'right', fontSize: '0.78rem',
        color: row.is_listed ? '#0d8e3a' : '#999',
        fontWeight: row.is_listed ? 600 : 400,
      }}>
        {row.is_listed
          ? (row.listing_price ? `$${row.listing_price.toLocaleString()}` : 'LISTED')
          : '—'}
      </span>
    </div>
  );
}

/* #1281: parcel_id is NOT unique — the DB key is (state, county, parcel_id).
   Every panel below is fetched by bare APN, so when an APN collides the
   backend falls back to the lowest-id row, which can be a parcel in another
   state entirely. The detail payload already carries the resolved state and
   county, so pass them through and the target is pinned to the parcel the
   user is actually looking at. Omitting them keeps the old URL shape, which
   is what the backend defaults to. */
function targetScopeQuery(state, county) {
  let q = '';
  if (state) q += `&state=${encodeURIComponent(state)}`;
  if (county) q += `&county=${encodeURIComponent(county)}`;
  return q;
}

function NeighborsCard({ parcelId, state, county, navigate }) {
  const [data, setData] = useState(null);
  const [radius, setRadius] = useState(1.5);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    fetch(`/api/v1/parcels/${encodeURIComponent(parcelId)}/neighbors?radius_km=${radius}&limit=25${targetScopeQuery(state, county)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setData({ _error: err.message }); });
    return () => { cancelled = true; };
  }, [parcelId, state, county, radius]);

  return (
    <div className="detail-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3>Neighbors <span style={{ fontSize: '0.72rem', color: '#999', fontWeight: 'normal' }}>(#703)</span></h3>
        <select
          value={radius}
          onChange={(e) => setRadius(parseFloat(e.target.value))}
          style={{ fontSize: '0.82rem', padding: '2px 6px' }}
        >
          <option value={0.5}>~0.3 mi</option>
          <option value={1.5}>~1 mi</option>
          <option value={3.0}>~2 mi</option>
          <option value={8.0}>~5 mi</option>
        </select>
      </div>
      {data === null && <p style={{ color: '#999', fontSize: '0.85rem' }}>Loading…</p>}
      {data && data._error && (
        <p style={{ color: '#a33', fontSize: '0.85rem' }}>Failed to load: {data._error}</p>
      )}
      {data && !data._error && (
        <>
          <p style={{ fontSize: '0.78rem', color: '#888', margin: '0 0 8px 0' }}>
            {data.count} parcel{data.count === 1 ? '' : 's'} within {data.radius_km}km
            {data.count > 0 && data.neighbors.some(n => n.is_listed) &&
              ` · ${data.neighbors.filter(n => n.is_listed).length} currently listed`}
          </p>
          {data.count === 0 && (
            <p style={{ color: '#999', fontSize: '0.85rem' }}>No parcels within radius.</p>
          )}
          {data.neighbors.map((n, i) => (
            <NeighborRow key={`${n.parcel_id}|${n.state || ''}|${n.county || ''}|${i}`} row={n} navigate={navigate} />
          ))}
        </>
      )}
    </div>
  );
}

function OwnerPortfolioCard({ parcelId, owner, state, county, navigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    // The scope pin matters most here: the owner name that seeds the whole
    // query is read off the target row, so resolving to the wrong twin
    // returns a portfolio for a different owner in a different region.
    fetch(`/api/v1/parcels/${encodeURIComponent(parcelId)}/owner-portfolio?radius_km=80&limit=50${targetScopeQuery(state, county)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setData({ _error: err.message }); });
    return () => { cancelled = true; };
  }, [parcelId, state, county]);

  // Don't render the card at all if the owner doesn't have other parcels
  // in the region — saves screen real estate for the common case.
  if (data && !data._error && data.count === 0) return null;

  return (
    <div className="detail-card">
      <h3>Owner's Portfolio <span style={{ fontSize: '0.72rem', color: '#999', fontWeight: 'normal' }}>(within ~50mi)</span></h3>
      {data === null && <p style={{ color: '#999', fontSize: '0.85rem' }}>Loading…</p>}
      {data && data._error && (
        <p style={{ color: '#a33', fontSize: '0.85rem' }}>Failed to load: {data._error}</p>
      )}
      {data && !data._error && data.count > 0 && (
        <>
          <p style={{ fontSize: '0.78rem', color: '#888', margin: '0 0 8px 0' }}>
            <strong>{owner}</strong> owns {data.count} other parcel{data.count === 1 ? '' : 's'} in the
            region · <strong>{data.total_acres_including_target.toLocaleString()}</strong> total
            acres (incl. this one)
          </p>
          {data.parcels.map((p, i) => (
            <NeighborRow key={`${p.parcel_id}|${p.state || ''}|${p.county || ''}|${i}`} row={p} navigate={navigate} />
          ))}
        </>
      )}
    </div>
  );
}

/* ---------- Similar parcels card (#769 / INIT-recommender) ----------
   Surfaces the precomputed top-K from /parcels/{id}/similar (content-
   embedding nearest neighbors built by compute_parcel_embeddings.py +
   compute_parcel_similarities.py). Additive — mirrors the
   NeighborsCard pattern but the neighbors here are similar in feature
   space (acreage, climate, scores), not in geography. Lazy fetch on
   first render; renders nothing while loading and a "no similar yet"
   line when the recommender hasn't populated this parcel.

   "If you like that you might like this" — the last named item in the
   AI roadmap (memory project_product_vision_aggregation_to_ai). */
function SimilarRow({ row, navigate }) {
  const acres = row.area_acres != null ? row.area_acres.toFixed(1) : '?';
  const stateCounty = [row.state, row.county].filter(Boolean).join(' · ');
  // similarity is in [0, 1]; render as a 2-digit percentage so the
  // user can see "how close" without needing to know the L2 scale.
  const simPct = row.similarity != null
    ? `${(row.similarity * 100).toFixed(0)}%`
    : '—';
  return (
    <div
      className="pd-similar-row"
      onClick={() => navigate(parcelPath(row.parcel_id, { state: row.state, county: row.county }))}
      style={{
        display: 'grid',
        gridTemplateColumns: '50px 1fr 60px 90px',
        gap: 8, padding: '6px 4px',
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer', fontSize: '0.85rem',
        alignItems: 'center',
        minHeight: 44,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      title={`Similarity: ${simPct} (L2 distance ${row.score != null ? row.score.toFixed(3) : '?'})`}
    >
      <span style={{ color: '#0d8e3a', fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
        {simPct}
      </span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <strong>{row.parcel_id}</strong>
        {stateCounty && (
          <span style={{ color: '#666', fontSize: '0.78rem' }}>  · {stateCounty}</span>
        )}
      </span>
      <span style={{ color: '#666', textAlign: 'right' }}>{acres}ac</span>
      <span style={{
        textAlign: 'right', fontSize: '0.78rem',
        color: row.is_listed ? '#0d8e3a' : '#999',
        fontWeight: row.is_listed ? 600 : 400,
      }}>
        {row.is_listed
          ? (row.listing_price ? `$${row.listing_price.toLocaleString()}` : 'LISTED')
          : '—'}
      </span>
    </div>
  );
}

/* ---------- Investment thesis tags card (#771 / INIT-thesis-tagger) ----------
   Renders the multi-label thesis tags computed by
   scripts/compute_investment_thesis_tags.py. Each tag is a clickable
   chip — clicking navigates to the search page filtered to that tag.
   Tag colors use the same hue family per investment category so the
   chips read at a glance. Renders nothing while loading; renders an
   empty placeholder so users know we ran the scorer but found no tags
   (vs. "scorer hasn't run yet" — we can't distinguish in this MVP). */
const THESIS_TAG_LABELS = {
  'off-grid-homestead': { label: 'Off-grid homestead', color: '#0d8e3a', bg: '#f0fdf4', border: '#bbf7d0' },
  'commercial-timber': { label: 'Commercial timber', color: '#5a4017', bg: '#fef9c3', border: '#fef08a' },
  'solar-lease-prospect': { label: 'Solar lease prospect', color: '#854d0e', bg: '#fef3c7', border: '#fde68a' },
  'mineral-exploration': { label: 'Mineral exploration', color: '#7c2d12', bg: '#fee2e2', border: '#fecaca' },
  'hunting-recreation': { label: 'Hunting / recreation', color: '#166534', bg: '#dcfce7', border: '#86efac' },
  'agri-cash-rent': { label: 'Agri cash-rent', color: '#1e3a8a', bg: '#dbeafe', border: '#bfdbfe' },
};

function ThesisTagsCard({ parcelId, navigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/v1/parcels/${encodeURIComponent(parcelId)}/thesis_tags`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setData({ _error: err.message }); });
    return () => { cancelled = true; };
  }, [parcelId]);

  // Don't render the card at all if we got an error or zero tags —
  // keeps the page from showing an empty "Investment Thesis" header
  // for parcels that match no thesis (most urban lots).
  if (!data || data._error) return null;
  if (data.count === 0) return null;

  return (
    <div className="detail-card" style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 4px 0' }}>Investment Thesis</h3>
      <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 10px 0' }}>
        Rule-based tags from area · land use · resource scores · commodity probs.
        Click any chip to find similar opportunities.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {data.tags.map(t => {
          const meta = THESIS_TAG_LABELS[t.tag] || { label: t.tag, color: '#444', bg: '#f3f4f6', border: '#e5e7eb' };
          return (
            <span
              key={t.tag}
              onClick={() => navigate(`/search?thesis_tag=${encodeURIComponent(t.tag)}`)}
              title={`Confidence ${t.confidence}/100 — click to filter search`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                background: meta.bg,
                color: meta.color,
                border: `1px solid ${meta.border}`,
                fontSize: '0.82rem',
                fontWeight: 500,
                cursor: 'pointer',
                lineHeight: 1.4,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.97)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
            >
              {meta.label}
              <span style={{ fontSize: '0.72rem', opacity: 0.75, fontFamily: 'ui-monospace, monospace' }}>
                {t.confidence}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function SimilarParcelsCard({ parcelId, navigate }) {
  const [data, setData] = useState(null);
  // Collapsed by default — keeps the parcel-detail page from getting
  // longer for users who don't care about recommendations.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setData(null);
    fetch(`/api/v1/parcels/${encodeURIComponent(parcelId)}/similar?limit=20`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setData({ _error: err.message }); });
    return () => { cancelled = true; };
  }, [parcelId, open]);

  return (
    <div className="detail-card">
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setOpen(v => !v)}
      >
        <h3 style={{ margin: 0 }}>
          {open ? '▼' : '▶'} Similar parcels
          {' '}
          <span style={{ fontSize: '0.72rem', color: '#999', fontWeight: 'normal' }}>
            (if you like this, you might like…)
          </span>
        </h3>
      </div>
      {open && (
        <div style={{ marginTop: 8 }}>
          {data === null && (
            <p style={{ color: '#999', fontSize: '0.85rem' }}>Loading…</p>
          )}
          {data && data._error && (
            <p style={{ color: '#a33', fontSize: '0.85rem' }}>
              Failed to load: {data._error}
            </p>
          )}
          {data && !data._error && data.count === 0 && (
            <p style={{ color: '#999', fontSize: '0.85rem' }}>
              Not enough data yet — the recommender is still populating this region.
            </p>
          )}
          {data && !data._error && data.count > 0 && (
            <>
              <p style={{ fontSize: '0.78rem', color: '#888', margin: '0 0 8px 0' }}>
                {data.count} content-similar parcel{data.count === 1 ? '' : 's'}
                {data.similar.some(s => s.is_listed) &&
                  ` · ${data.similar.filter(s => s.is_listed).length} currently listed`}
              </p>
              {data.similar.map((s, i) => (
                <SimilarRow key={`${s.parcel_id}|${s.state || ''}|${s.county || ''}|${i}`} row={s} navigate={navigate} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Adjacent parcels overlay (#703 follow-up) ----------
   Loads neighboring parcels with full polygon geometry and renders
   them as filled GeoJSON polygons on the mini-map, color-coded by
   owner via `ownerColor()` from utils/ownerColor.js. Click anywhere
   on a neighbor polygon → navigates to that parcel's detail page.
   The target parcel itself is highlighted with a bold orange outline
   (rendered separately above this overlay so it stays on top). */

export function AdjacentParcelsOverlay({ parcelId, state, county, navigate, radiusKm = 1.5, limit = 50 }) {
  const map = useMap();
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    fetch(
      `/api/v1/parcels/${encodeURIComponent(parcelId)}/adjacent` +
      `?radius_km=${radiusKm}&limit=${limit}${targetScopeQuery(state, county)}`,
      { headers: authHeaders() },
    )
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { /* silent — overlay is nice-to-have */ });
    return () => { cancelled = true; };
  }, [parcelId, state, county, radiusKm, limit]);

  // Memoize the FeatureCollection so the <GeoJSON> data ref stays
  // stable across parent re-renders (otherwise react-leaflet
  // rebuilds the layer on every state change in ParcelDetail).
  const fc = useMemo(() => {
    if (!data || !data.features || data.features.length === 0) return null;
    return { type: 'FeatureCollection', features: data.features };
  }, [data]);

  const style = (feature) => {
    const owner = feature?.properties?.owner;
    return {
      color: ownerStroke(owner),
      weight: 1.5,
      fillColor: ownerColor(owner),
      fillOpacity: 0.45,
      // Listed parcels get a dashed outline so users can spot the
      // for-sale neighbors at a glance even before hovering.
      dashArray: feature?.properties?.is_listed ? '4,3' : '',
    };
  };

  const onEachFeature = (feature, layer) => {
    const p = feature?.properties || {};
    const acres = p.area_acres != null ? p.area_acres.toFixed(1) : '?';
    const dist = p.dist_km != null ? `${p.dist_km.toFixed(2)}km` : '—';
    const ownerLabel = p.owner || '<em style="color:#666">no owner data</em>';
    const listedLabel = p.is_listed
      ? `<div style="color:#0d8e3a;font-weight:600">LISTED${
          p.listing_price ? ` · $${p.listing_price.toLocaleString()}` : ''
        }</div>`
      : '';
    layer.bindTooltip(
      `<div style="font-size:0.78rem;line-height:1.35">` +
      `<div><strong>${p.parcel_id}</strong></div>` +
      `<div>${ownerLabel}</div>` +
      `<div style="color:#666">${acres}ac · ${dist}</div>` +
      listedLabel +
      `</div>`,
      { sticky: true, direction: 'top' },
    );
    layer.on('click', () => {
      if (!p.parcel_id) return;
      // Stop the hero map's current pan/zoom before route navigation
      // tears down the Leaflet pane; otherwise Leaflet can finish the
      // zoom transition against an undefined mapPane (#814, #815).
      safeStopMap(map);
      navigate(`/parcel/${encodeURIComponent(p.parcel_id)}`);
    });
  };

  if (!fc || !map.getPane('mapPane')) return null;
  // The key is derived from the parcel id + count so a parcel switch
  // (or a radius bump that changes the count) forces a remount —
  // react-leaflet's <GeoJSON> doesn't reconcile data swaps internally.
  const key = `adj-${parcelId}-${data?.count ?? 0}`;
  return (
    <GeoJSON
      key={key}
      data={fc}
      style={style}
      onEachFeature={onEachFeature}
    />
  );
}

/* ---------- Owner color pill ----------
   Tiny visual link between the cards (NeighborsCard, OwnerPortfolioCard)
   and the polygon overlay on the map: same color == same owner. */
function OwnerPill({ owner }) {
  if (!owner) return null;
  const fill = ownerColor(owner);
  const stroke = ownerStroke(owner);
  return (
    <span
      title={owner}
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: 2,
        background: fill,
        border: `1px solid ${stroke}`,
        marginRight: 5,
        verticalAlign: 'middle',
      }}
    />
  );
}

/* ---------- Deep (max) property record section (#1059) ----------
 * Extracted to components/DeepRecord.js so the on-demand research
 * view (/parcel/:id/research) and the Research Library reuse the
 * exact same grouped/attributed/confidence-labeled renderer. */

/* ---------- APN disambiguation banner (#1281, #1292) ----------
 *
 * `parcels` is UNIQUE on (state, county, parcel_id), so a bare APN can
 * name several real parcels and the backend resolves it to the lowest
 * row id. A link that carried only the APN therefore rendered a
 * DIFFERENT parcel than the row that produced it -- silently, because
 * the response looked complete.
 *
 * The backend returns `ambiguous` (every candidate, including the one
 * currently on screen) whenever the lookup matched more than one row.
 * This banner turns that into an explicit choice rather than a wrong
 * page. It renders only for an ambiguous link: once state/county are
 * in the URL the lookup resolves to one row and `ambiguous` is empty.
 */
function AmbiguityBanner({ parcelId, ambiguous, current }) {
  if (!Array.isArray(ambiguous) || ambiguous.length < 2) return null;
  const cur = current || {};
  return (
    <div
      className="pd-ambiguity-banner"
      data-testid="pd-ambiguity-banner"
      role="status"
      style={{
        background: '#fff8e1',
        border: '1px solid #f0c36d',
        borderRadius: 8,
        padding: '10px 12px',
        marginBottom: 10,
        fontSize: '0.85rem',
        color: '#4a3a10',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        This APN names {ambiguous.length} different parcels
      </div>
      <div style={{ marginBottom: 8 }}>
        The link did not say which one. Pick the parcel you meant:
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {ambiguous.map((a) => {
          // (state, county, parcel_id) is the unique key, so state +
          // county identify the candidate exactly.
          const here = a.state === cur.state && a.county === cur.county;
          return (
            <Link
              key={a.row_id ?? `${a.state}-${a.county}`}
              to={parcelPath(parcelId, { state: a.state, county: a.county })}
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: 6,
                border: here ? '1px solid #b45309' : '1px solid #e0c088',
                background: here ? '#fde9c8' : '#fffdf6',
                color: '#7c4a03',
                textDecoration: 'none',
                fontWeight: here ? 700 : 400,
              }}
            >
              {a.county || 'unknown county'}, {a.state || '??'}
              {a.area_acres != null ? ` · ${Number(a.area_acres).toFixed(2)} ac` : ''}
              {here ? ' — shown' : ''}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- main component ---------- */
function ParcelDetail() {
  const { parcelId } = useParams();
  const navigate = useNavigate();
  // The bare APN is not an identity (#1281, #1292): the same string names
  // different parcels in different counties, and the backend resolves a
  // bare APN to the lowest row id. A link therefore carries the row's
  // own state and county as query parameters, and this page forwards
  // them so the fetch resolves the parcel the link actually named.
  // Absent parameters mean the link was ambiguous — the response then
  // carries `ambiguous` and the page renders a disambiguation picker
  // instead of silently showing a different parcel.
  const [searchParams] = useSearchParams();
  const scopeState = searchParams.get('state') || '';
  const scopeCounty = searchParams.get('county') || '';
  const { user: authUser } = useAuth();
  const { isPinned: isComparePinned, toggle: toggleComparePin } = useComparePins();

  const [parcel, setParcel] = useState(null);
  const [scores, setScores] = useState(null);
  const [features, setFeatures] = useState(null);
  // Deep (max) property record — sourced public-records dossier (#1059).
  // null while loading; response object once resolved (has_max_record may
  // be false, in which case the section renders an empty state).
  const [maxRecord, setMaxRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookmarkStatus, setBookmarkStatus] = useState(null);
  // Downvote / hide state (#124). null = not hidden, 'hidden' = hidden.
  // Loaded alongside the bookmark check on parcel mount.
  const [downvoteStatus, setDownvoteStatus] = useState(null);
  const [nearbyMines, setNearbyMines] = useState(null);
  const [commodityScores, setCommodityScores] = useState(null);
  const [miningClaims, setMiningClaims] = useState(null);
  const [listingHistory, setListingHistory] = useState(null);
  const [listingMedia, setListingMedia] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  // Full-size listing-photo lightbox (#1144). null = closed; a number
  // is the index (into listingMedia.photo_urls) currently shown. The
  // ref points at the main gallery photo so focus returns there on
  // close, per WAI-ARIA dialog guidance.
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const mainPhotoRef = useRef(null);
  const [elevationProfile, setElevationProfile] = useState(null);
  const [elevationGrid, setElevationGrid] = useState(null);
  const [compsData, setCompsData] = useState(null);
  // Buildability / access extraction for this parcel (issue #106).
  // null while loading; { results: [...] } once the per-parcel
  // buildable-retreat lookup resolves.
  const [buildability, setBuildability] = useState(null);
  // Recent news scoped to the parcel's state. v1 of #98 — no
  // county-level resolution yet, so we filter on state only.
  const [stateNews, setStateNews] = useState(null);
  // In-app report viewer modal (replaces the old "Generate Report"
  // external link — the report renders inline now via an iframe).
  const [showReportViewer, setShowReportViewer] = useState(false);
  // "Flag bad data" modal + counter. The counter is rendered next
  // to the action button so a user reading the detail page can see
  // at a glance whether others have already reported issues with
  // this parcel — useful signal before they file their own.
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagCounts, setFlagCounts] = useState({ count: 0, unresolved: 0 });

  // Agent-driven public-records research (#122). null = haven't loaded
  // /research/latest yet; {} = no cached row exists; { found, fields, ... }
  // = data. researchStatus tracks the POST/poll lifecycle when the user
  // clicks "Research this parcel".
  const [research, setResearch] = useState(null);
  const [researchStatus, setResearchStatus] = useState(null); // null | 'queued' | 'running' | 'success' | 'failed'
  const [researchError, setResearchError] = useState(null);
  // research_id whose transcript is currently shown in the modal, or null
  const [showTranscript, setShowTranscript] = useState(null);
  const [transcriptData, setTranscriptData] = useState(null);
  // Hazard groups expand state (#131 / issue #704). Collapsed by
  // default — the suite went from 5 to 18 bars and was eating too
  // much real estate. Each group shows a single chip with the group's
  // max-score; click expands the per-hazard bars beneath.
  const [expandedHazardGroups, setExpandedHazardGroups] = useState({});

  // Terrain viewer mode: '3d' (WebGL ParcelTerrain3D, default) or
  // 'hillshade' (USGS-3DEP-rendered preview.png at the same size).
  // The hillshade is also the implicit fallback when the elevation
  // grid hasn't loaded or the relief is too flat for WebGL to be
  // interesting — switching the toggle just locks the user choice.
  const [terrainView, setTerrainView] = useState('3d');

  const [activeTab, setActiveTab] = useState(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash : '') || '';
    const stripped = h.startsWith('#') ? h.slice(1) : h;
    return PARCEL_DETAIL_TABS.some((t) => t.id === stripped) ? stripped : null;
  });

  const handleTabChange = (id) => {
    const next = activeTab === id ? null : id;
    setActiveTab(next);
    if (next) {
      setTimeout(() => {
        const target = document.getElementById(next);
        if (target) {
          const y = target.getBoundingClientRect().top + window.scrollY - 56;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
        try { window.history.replaceState(null, '', `#${next}`); } catch { /* ignore */ }
      }, 150);
    } else {
      try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
    }
  };

  const showTab = (tabId) => !activeTab || activeTab === tabId;

  // Debug-mode gate (Bucket 4 — raw feature/model/ingest dumps). Default
  // hidden; surfaced when the URL carries `?debug=1`. Inline read (no
  // hook) so we don't need new imports.
  const isDebug = (() => {
    try {
      return typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('debug') === '1';
    } catch {
      return false;
    }
  })();

  const didHashScroll = useRef(false);
  useEffect(() => { didHashScroll.current = false; }, [parcelId]);
  useEffect(() => {
    if (!parcel || didHashScroll.current) return;
    didHashScroll.current = true;
    const h = (typeof window !== 'undefined' ? window.location.hash : '') || '';
    const stripped = h.startsWith('#') ? h.slice(1) : h;
    if (!PARCEL_DETAIL_TABS.some((t) => t.id === stripped)) return;
    setTimeout(() => {
      const target = document.getElementById(stripped);
      if (target) {
        const y = target.getBoundingClientRect().top + window.scrollY - 56;
        window.scrollTo({ top: y, behavior: 'auto' });
      }
    }, 150);
  }, [parcel]);

  useEffect(() => {
    // Cancellation flag so in-flight enrichment responses from the
    // *previous* parcelId don't clobber the current one's state
    // when the user navigates quickly between parcels (e.g. clicking
    // through comps or using browser back/forward). Without this the
    // UI would flicker stale data from the prior parcel into the new
    // detail page.
    let cancelled = false;
    // Also reset all enrichment state so stale data from a prior
    // parcel isn't briefly visible while new requests are in flight.
    setScores(null);
    setFeatures(null);
    setMaxRecord(null);
    setNearbyMines(null);
    setCommodityScores(null);
    setMiningClaims(null);
    setListingHistory(null);
    setListingMedia(null);
    setElevationProfile(null);
    setCompsData(null);
    setBuildability(null);
    setBookmarkStatus(null);
    setDownvoteStatus(null);
    setFlagCounts({ count: 0, unresolved: 0 });

    const fetchParcelData = async () => {
      setLoading(true);
      setError(null);
      try {
        // The parcel itself is the only required fetch — everything else
        // is optional enrichment that we want to render as soon as it
        // arrives. Fire the base fetch first so a 404 still surfaces as
        // an error, then parallelize the 9 enrichment calls.
        const parcelData = await getParcel(parcelId, {
          state: scopeState,
          county: scopeCounty,
        });
        if (cancelled) return;
        setParcel(parcelData);
        // Record the view fire-and-forget — failure is non-fatal.
        recordView(parcelId).catch(() => {});

        // Fire all enrichment fetches in parallel. Each setter runs as
        // its own request resolves (rather than waiting for the slowest)
        // so UI sections light up progressively. Errors are swallowed
        // individually — any one of these can fail without blocking
        // the rest of the page. The `cancelled` guard prevents any
        // of them from clobbering state after the user has navigated
        // to a different parcel.
        const settle = (p, onFulfilled) => {
          p.then((v) => { if (!cancelled) onFulfilled(v); }).catch(() => {});
        };
        // The score tables are keyed by the APN string, which is not an
        // identity (#1281, #1292): a shared APN can serve a colliding
        // twin's numbers. Forward the same disambiguator the parcel
        // fetch used so the panels are read from the parcel the link
        // named.
        const scope = { state: scopeState, county: scopeCounty };
        settle(getScores(parcelId, scope), setScores);
        settle(getParcelMaxRecord(parcelId), setMaxRecord);
        settle(getFeatures(parcelId, scope), (fData) => {
          // API returns { parcel_id, run_id, features: {...}, generated_at }
          setFeatures(fData?.features || fData);
        });
        settle(getNearbyMines(parcelId, 50, 50), setNearbyMines);
        settle(getCommodityScores(parcelId, scope), setCommodityScores);
        settle(getParcelMiningClaims(parcelId), setMiningClaims);
        settle(getListingHistory(parcelId), setListingHistory);
        settle(getListingMedia(parcelId), setListingMedia);
        settle(getElevationProfile(parcelId, 50), setElevationProfile);
        // Adaptive grid size: target ~one sample per native DEM pixel
        // (3DEP 1-arc-second = ~30 m). Anything smaller is pure
        // bilinear smoothing; anything bigger leaves real ridges
        // unresolved on 2 km+ parcels. clamp(16, 48, ceil(w/30))
        // keeps small parcels at a baseline that still looks 3D
        // and lets big ranches go up to 48×48 (47×47 cells × 2 tris
        // = 4,418 triangles).
        const sizeForParcel = (g) => {
          try {
            const coords =
              g?.type === 'Polygon' ? g.coordinates[0]
              : g?.type === 'MultiPolygon' ? g.coordinates[0][0]
              : null;
            if (!coords || coords.length < 2) return 24;
            let mnLon = Infinity, mxLon = -Infinity, mnLat = Infinity, mxLat = -Infinity;
            for (const [lo, la] of coords) {
              if (lo < mnLon) mnLon = lo;
              if (lo > mxLon) mxLon = lo;
              if (la < mnLat) mnLat = la;
              if (la > mxLat) mxLat = la;
            }
            const latMid = (mnLat + mxLat) / 2;
            const wM = (mxLon - mnLon) * Math.cos(latMid * Math.PI / 180) * 111320;
            const hM = (mxLat - mnLat) * 110540;
            const widthM = Math.max(wM, hM);
            return Math.max(16, Math.min(48, Math.ceil(widthM / 30)));
          } catch {
            return 24;
          }
        };
        settle(getElevationGrid(parcelId, sizeForParcel(parcelData?.geometry)), setElevationGrid);
        settle(getComparableParcels(parcelId, 50, 5), setCompsData);
        // Per-parcel buildability lookup (#106). The buildable-retreat
        // screen endpoint takes a parcel_id filter that bypasses the
        // ranking thresholds, so the card shows whatever was extracted.
        settle(getBuildableRetreatScreen({ parcel_id: parcelId }), setBuildability);
        settle(checkBookmark(parcelId), (bk) => {
          if (bk?.bookmarked) setBookmarkStatus('added');
        });
        settle(checkDownvote(parcelId), (dv) => {
          if (dv?.downvoted) setDownvoteStatus('hidden');
        });
        // Flag-bad-data counter — separate fetch from the modal
        // itself so the "N flags reported" indicator is available
        // before the modal opens, and stays correct after a submit
        // (the modal's onFlagSubmitted callback re-runs this).
        settle(listDataFlagsForParcel(parcelId), (fl) => {
          setFlagCounts({
            count: fl?.count ?? 0,
            unresolved: fl?.unresolved_count ?? 0,
          });
        });
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to fetch parcel data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchParcelData();

    return () => { cancelled = true; };
  }, [parcelId, scopeState, scopeCounty]);

  // News in the parcel's state, last 30 days. Fires once parcel.state
  // is loaded so we don't chase the API with an empty filter. (#98)
  useEffect(() => {
    if (!parcel?.state) return;
    let cancelled = false;
    getDashboardNews({ state: parcel.state, days: 30, limit: 5 })
      .then(d => { if (!cancelled) setStateNews(d); })
      .catch(() => { if (!cancelled) setStateNews({ events: [] }); });
    return () => { cancelled = true; };
  }, [parcel?.state]);

  // Auto-load any cached research result on mount (#122).
  // /research/latest returns the most recent successful row or 404 — we
  // treat 404 as "no cached row" (setResearch({})), not an error.
  useEffect(() => {
    if (!parcelId) return;
    let cancelled = false;
    fetch(`/api/v1/parcels/${encodeURIComponent(parcelId)}/research/latest`, { headers: authHeaders() })
      .then(r => {
        if (r.status === 404) return null; // no cached row
        if (!r.ok) throw new Error(`research lookup ${r.status}`);
        return r.json();
      })
      .then(d => { if (!cancelled) setResearch(d ?? {}); })
      .catch(() => { if (!cancelled) setResearch({}); });
    return () => { cancelled = true; };
  }, [parcelId]);

  // Trigger an agent research run. POSTs to /research; if the response
  // says 'cached' we get the result immediately. Otherwise we poll
  // /research/<id> every 5s until success/failed (max ~10 min).
  const handleResearch = async () => {
    setResearchError(null);
    setResearchStatus('queued');
    try {
      const resp = await fetch(
        `/api/v1/parcels/${encodeURIComponent(parcelId)}/research`,
        { method: 'POST', headers: authHeaders() }
      );
      const data = await resp.json();
      if (!resp.ok) {
        setResearchStatus('failed');
        setResearchError(data.detail || `HTTP ${resp.status}`);
        return;
      }
      if (data.status === 'cached') {
        setResearchStatus('success');
        setResearch({
          research_id: data.research_id,
          found: data.found,
          confidence: data.confidence,
          source_url_used: data.source_url_used,
          structured_extract: data.structured_extract,
          completed_at: data.completed_at,
        });
        return;
      }
      // queued | in_flight — poll
      const rid = data.research_id;
      const start = Date.now();
      while (Date.now() - start < 10 * 60 * 1000) {
        await new Promise(r => setTimeout(r, 5000));
        const pollResp = await fetch(`/api/v1/parcels/research/${rid}`, { headers: authHeaders() });
        if (!pollResp.ok) continue;
        const poll = await pollResp.json();
        setResearchStatus(poll.status);
        if (poll.status === 'success') {
          setResearch(poll);
          return;
        }
        if (poll.status === 'failed') {
          setResearchError(poll.error || 'research run failed');
          return;
        }
      }
      setResearchError('polling timed out after 10 minutes');
      setResearchStatus('failed');
    } catch (err) {
      setResearchStatus('failed');
      setResearchError(err.message || 'request failed');
    }
  };

  // Lazy-load the agent's trajectory when the user opens the transcript
  // modal. Keyed on showTranscript (the research_id) so reopening a
  // different research run re-fetches.
  useEffect(() => {
    if (!showTranscript) {
      setTranscriptData(null);
      return;
    }
    let cancelled = false;
    setTranscriptData('loading');
    fetch(`/api/v1/parcels/research/${showTranscript}/transcript`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { if (!cancelled) setTranscriptData(d); })
      .catch(err => { if (!cancelled) setTranscriptData({ _error: err.message }); });
    return () => { cancelled = true; };
  }, [showTranscript]);

  const handleBookmark = async () => {
    if (!authUser) {
      navigate(`/login?return=${encodeURIComponent(`/parcel/${parcelId}`)}`);
      return;
    }
    try {
      if (bookmarkStatus === 'added') {
        await deleteBookmark(parcelId);
        setBookmarkStatus(null);
      } else {
        await createBookmark(parcelId);
        setBookmarkStatus('added');
      }
    } catch (err) {
      // Extract backend detail message if available
      const detail = err.response?.data?.detail;
      setError(detail || err.message || 'Failed to update bookmark');
    }
  };

  // Deep research (#1059) — enqueue the on-demand property-record
  // maximizer job and open the streaming research view in a NEW TAB.
  // The tab is opened synchronously inside the click handler (before
  // any await) so browser popup blockers allow it; once the enqueue
  // POST resolves we point the tab at the record's job so it can poll
  // that specific job. On failure (or if the handle is blocked) the
  // view falls back to ?autostart=1 and enqueues itself.
  const handleDeepResearch = () => {
    const w = window.open('about:blank', '_blank');
    const go = (query) => {
      const url = `/parcel/${encodeURIComponent(parcelId)}/research${query}`;
      if (w) w.location = url;
      else window.open(url, '_blank', 'noopener');
    };
    // When a record already exists the button reads "Re-run research" —
    // force a fresh run (bypass the backend fresh-record dedup) so the
    // engine actually re-runs instead of returning the cached record. The
    // first run (no record yet) uses force=false so normal navigation dedups.
    const force = Boolean(maxRecord && maxRecord.has_max_record);
    startDeepResearch(parcelId, { force })
      .then((resp) => {
        const jid = resp && resp.job_id;
        if (jid) {
          go(`?job=${encodeURIComponent(jid)}`);
        } else if (resp && (resp.status === 'done' || resp.cached) && resp.record_id) {
          // Fresh-record dedup (#1107): a recent record already exists and
          // no job was enqueued — open the record view directly rather
          // than re-triggering an enqueue that will just dedup again.
          go('');
        } else {
          go('?autostart=1');
        }
      })
      .catch(() => go('?autostart=1'));
  };

  // Hide / un-hide handler (#124). Symmetric to handleBookmark; the
  // two actions are intentionally independent (a user can have a
  // parcel both bookmarked AND hidden — the hide takes precedence
  // for map styling, but the bookmark still shows up in the saved
  // list).
  const handleDownvote = async () => {
    try {
      if (downvoteStatus === 'hidden') {
        await deleteDownvote(parcelId);
        setDownvoteStatus(null);
      } else {
        await createDownvote(parcelId);
        setDownvoteStatus('hidden');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || err.message || 'Failed to update hidden status');
    }
  };

  /* ---------- Memoized derived UI blocks ----------
   *
   * These two blocks were previously IIFEs inside the JSX that re-ran
   * their inner work (sorting, mapping, narrative-text construction) on
   * every parent re-render. The parent re-renders ~12 times during
   * initial load — once per `settle()` call from each enrichment
   * fetch — so without memoization the same sort runs over and over.
   *
   * useMemo keyed on the inputs each block actually reads, so the work
   * only repeats when the underlying data changes. (#perf #2)
   */

  // The standalone "Strongest signal" summary banner was removed in
  // #931: it re-spent the vertical space the smaller commodity meter
  // had just reclaimed. The dominant commodity is already legible from
  // the grid, and each tile's `title` exposes the same calibration hint
  // on hover.

  // "Why this parcel?" narrative — auto-generated bullet list of the
  // strongest evidence on this parcel. Pulls from parcel + features +
  // scores + commodityScores + FEMA risk.
  const whyThisParcel = useMemo(() => {
    if (!parcel) return null;
    const ms = scores?.mineral_score?.value ?? scores?.mineral_score ?? parcel.mineral_score;
    const points = [];

    // Mineral signals
    if (ms != null && ms >= 0.7) {
      points.push({ tone: 'strong', text: `Strong mineral signal (${(ms * 100).toFixed(0)}%) from the ML model.` });
    }
    // Commodity scores can come back as either a flat object (legacy)
    // or { scores: { gold: 0.9, ... } } (current).
    const csRoot = commodityScores || {};
    const cs = csRoot.scores || csRoot;
    // #1027: the endpoint returns a `percentiles` map (0-99, keyed by
    // commodity) alongside the raw probabilities. Display the percentile
    // so the "Why this parcel?" summary agrees with the uniform 0-100
    // normalization epic; fall back to the raw `prob*100` only when
    // `percentiles` is absent/empty (older cached responses).
    const csPct = csRoot.percentiles || {};
    const dispCommodityPct = (k, v) => {
      const p = csPct[k];
      return p != null ? Math.round(p) : Math.round(v * 100);
    };
    const topCommodities = Object.entries({
      gold: cs.gold ?? cs.gold_prob,
      copper: cs.copper ?? cs.copper_prob,
      silver: cs.silver ?? cs.silver_prob,
      uranium: cs.uranium ?? cs.uranium_prob,
      lithium: cs.lithium ?? cs.lithium_prob,
      ree: cs.ree ?? cs.ree_prob,
      niobium: cs.niobium ?? cs.niobium_prob,
    }).filter(([, v]) => v != null && v >= 0.5)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    if (topCommodities.length) {
      const list = topCommodities
        .map(([k, v]) => `${k} ${dispCommodityPct(k, v)}%`)
        .join(', ');
      points.push({ tone: 'strong', text: `Commodity probabilities: ${list}.` });
    }

    // Mining proximity
    const num5 = features?.num_mines_5mi;
    const dMine = features?.distance_to_mine;
    if (num5 != null && num5 >= 5) {
      points.push({ tone: 'strong', text: `${num5} mineral sites within 5 miles${dMine != null ? ` (nearest ${dMine.toFixed(1)} km)` : ''}.` });
    } else if (dMine != null && dMine <= 2) {
      points.push({ tone: 'strong', text: `Nearest known mineral site is ${dMine.toFixed(1)} km away — close.` });
    }

    // Owner signal
    const ownerCat = parcel.owner_category;
    if (ownerCat === 'mining_company' || ownerCat === 'commodity_named' || ownerCat === 'resources_company') {
      points.push({ tone: 'strong', text: `Owner appears to be a resource company (${ownerCat.replace('_', ' ')}). Often a clue someone is already aware of the deposit.` });
    }

    // Hazards (lower = better)
    const fr = parcel.fema_risk;
    if (fr) {
      if (fr.wildfire_score != null && fr.wildfire_score >= 80) {
        points.push({ tone: 'risk', text: `High wildfire loss risk (FEMA NRI ${fr.wildfire_score.toFixed(0)}/100).` });
      }
      if (fr.flood_score != null && fr.flood_score >= 90) {
        points.push({ tone: 'risk', text: `Very high flood loss risk (FEMA NRI ${fr.flood_score.toFixed(0)}/100).` });
      }
    }

    // Federal land flag
    if (parcel.federal_land_agency) {
      points.push({ tone: 'risk', text: `Sits on federal land (${parcel.federal_land_agency}). Mineral rights likely retained by the federal government.` });
    }

    // DOM / pricing.
    //
    // Cap at 730 days for the "motivated seller" insight — DOM beyond
    // ~2 years often reflects a stale listed_date from a RentCast
    // re-listing, not actual time-on-market. Calling out "3000 days"
    // as motivated-seller signal is misleading; we don't have
    // visibility into whether the listing has been continuously up
    // since 2017 or relisted last week.
    if (parcel.is_listed
        && parcel.days_on_market != null
        && parcel.days_on_market > 180
        && parcel.days_on_market <= 730) {
      points.push({
        tone: 'opportunity',
        text: `On market ${parcel.days_on_market} days — possibly motivated seller.`,
      });
    }
    if (parcel.rc_value_estimate && parcel.listing_price) {
      const ratio = parcel.listing_price / parcel.rc_value_estimate;
      if (ratio < 0.7) {
        const pct = ((1 - ratio) * 100).toFixed(0);
        points.push({ tone: 'opportunity', text: `Listed ${pct}% below RentCast AVM estimate of ${fmt$(parcel.rc_value_estimate)}.` });
      }
    }

    // Geometry / data quality issue
    if (!parcel.geometry) {
      points.push({ tone: 'caveat', text: `Parcel boundary not yet hydrated — analysis based on centroid only.` });
    }

    if (!points.length) return null;

    const toneColor = { strong: '#0e7c3a', opportunity: '#185abc', risk: '#b3261e', caveat: '#5f6368' };
    // PR-6 (#704): `pd-right-sticky` class hook — at ≥1025px desktop
    // this card is sticky in place so it stays visible while the user
    // scrolls the main content. Phone + tablet ignore the class
    // (the @media (min-width: 1025px) rule scopes the sticky CSS).
    return (
      <div className="detail-card pd-right-sticky" data-testid="pd-why-card" style={{ marginTop: 16 }}>
        <h3 style={{ display: 'flex', alignItems: 'center' }}>
          <span>Why this parcel?</span>
          <InfoHint label="About Why this parcel">
            Auto-generated summary of the strongest evidence on this parcel.
          </InfoHint>
        </h3>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.9rem', lineHeight: 1.6 }}>
          {points.map((p, i) => (
            <li key={i} style={{ color: toneColor[p.tone] || '#222' }}>{p.text}</li>
          ))}
        </ul>
      </div>
    );
  }, [parcel, scores, features, commodityScores]);

  /* ----- loading / error states ----- */
  if (loading) {
    return (
      <div className="detail-page">
        <button className="btn btn-outline" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>Back to Map</button>
        <div className="loading-container"><div className="spinner" /></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="detail-page">
        <button className="btn btn-outline" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>Back to Map</button>
        <div className="error-message">Error: {error}</div>
        <button className="btn btn-primary" onClick={() => window.location.reload()} style={{ marginTop: 8 }}>Retry</button>
      </div>
    );
  }
  if (!parcel) {
    return (
      <div className="detail-page">
        <button className="btn btn-outline" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>Back to Map</button>
        <div className="error-message">Parcel not found</div>
      </div>
    );
  }

  /* ----- derived values ----- */
  const lat = parcel.latitude || parcel.centroid?.lat;
  const lon = parcel.longitude || parcel.centroid?.lon;
  const hasCoords = lat && lon && lat !== 0 && lon !== 0;
  // #1546: the per-acre price describes the OFFER, so it divides by the
  // offered acreage. When the offered acreage could not be resolved
  // there is no per-acre figure at all, rather than one computed from
  // the parent's acreage, which is a different piece of land.
  const _offeredPpa = offeredPricePerAcre(parcel);
  const pricePerAcre = _offeredPpa != null ? fmt$(_offeredPpa) : null;
  const acreInfo = offeredAcreageInfo(parcel);
  // The county value estimate describes the holding, so it is per PARCEL
  // acre, never per offered sub-lot acre.
  const _parcelAcres = parcelLevelAcres(parcel);
  // land-ai#1487: when we last confirmed this listing at the source. Distinct
  // from listed_date, which is the MLS list date and says nothing about how
  // current our copy is. Returning null when there is no timestamp keeps
  // "never verified" visibly different from "verified today": InfoRow drops
  // null rows rather than printing a zero-day age.
  const verifiedLabel = (() => {
    if (!parcel.last_verified_at) return null;
    const when = fmtDate(parcel.last_verified_at);
    const age = parcel.days_since_verified;
    if (age == null) return when;
    return `${when} (${age}d ago)`;
  })();
  const verifiedTitle = verifiedLabel
    ? `Last confirmed at the source ${verifiedLabel}`
    : 'Never confirmed at the source';
  // Build address string, deduplicating city/state/zip that may already be in listing_address
  const addr = (() => {
    const base = parcel.listing_address || '';
    const parts = [base];
    // Only append city if not already in the address string
    if (parcel.listing_city && !base.includes(parcel.listing_city)) parts.push(parcel.listing_city);
    if (parcel.state && !base.includes(parcel.state)) parts.push(parcel.state);
    if (parcel.listing_zip && !base.includes(parcel.listing_zip)) parts.push(parcel.listing_zip);
    return parts.filter(Boolean).join(', ');
  })();

  /* ----- feature display names (imperial units) -----
   * Hoisted to module-level FEATURE_LABELS so we don't rebuild a
   * 26-key object every render. */

  // Returning null lets InfoRow drop missing rows quietly. The
  // earlier "N/A" return collided with InfoRow's null check —
  // the row vanished but the formatter signalled "render this".
  // Now: caller passes null through formatFeatureValue and the
  // single null-check in InfoRow is the source of truth.
  function formatFeatureValue(key, v) {
    if (v == null) return null;
    if (typeof v !== 'number') return String(v);
    if (KM_TO_MI_FEATURE_KEYS.has(key)) return fmtNum(v * KM_TO_MI, 2);
    if (C_TO_F_FEATURE_KEYS.has(key)) return fmtNum(v * 9 / 5 + 32, 1);
    return fmtNum(v, 2);
  }

  // Wrap raw geometry as a GeoJSON Feature so react-leaflet GeoJSON component works correctly
  const geojsonFeature = parcel.geometry ? {
    type: 'Feature',
    properties: {},
    geometry: parcel.geometry,
  } : null;

  return (
    <div className="detail-page" style={{ padding: '0.5rem 1.5rem' }}>
      <AmbiguityBanner
        parcelId={parcel.parcel_id || parcelId}
        ambiguous={parcel.ambiguous}
        current={{ state: parcel.state, county: parcel.county }}
      />
      {/* =================== Satellite Hero Map =================== */}
      {hasCoords && (
        <div className="pd-hero-map" style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', marginBottom: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
          <div className="pd-hero-map-frame" style={{ height: 420 }}>
            <MapContainer center={[lat, lon]} zoom={14} style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true} zoomControl={true}>
              <LayersControl position="topright">
                {/* ===== Base Layers ===== */}
                <LayersControl.BaseLayer checked name="Satellite">
                  <TileLayer
                    attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Topo">
                  <TileLayer
                    attribution='Tiles &copy; Esri &mdash; Source: USGS, Esri, TANA, DeLorme'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Terrain">
                  <TileLayer
                    attribution='Map data: &copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
                    url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                    maxZoom={17}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Street">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>

                {/* ===== Overlay: Nearby Mineral Sites ===== */}
                <LayersControl.Overlay checked name="Mineral Deposits">
                  <LayerGroup>
                    {nearbyMines && nearbyMines.mines && nearbyMines.mines.map((mine, idx) => {
                      if (!mine.latitude || !mine.longitude) return null;
                      const mColor = getMineColor(mine.commodity);
                      const sc = (mine.dev_status && STATUS_COLORS[mine.dev_status]) || STATUS_COLORS.Unknown;
                      const allCommodities = [mine.commodity, mine.commod2, mine.commod3].filter(Boolean);
                      return (
                        <Marker
                          key={`mine-${mine.id || idx}`}
                          position={[mine.latitude, mine.longitude]}
                          icon={getDiamondIcon(mColor.fill, mColor.border)}
                        >
                          <Popup maxWidth={280} minWidth={200}>
                            <div style={{ fontSize: '0.85rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                <div style={{ fontWeight: 700, color: '#1a1a2e', flex: 1 }}>
                                  {mine.site_name || 'Unnamed Site'}
                                </div>
                                {mine.dev_status && (
                                  <span style={{
                                    background: sc.bg, color: sc.text, fontSize: '0.65rem', fontWeight: 700,
                                    padding: '1px 6px', borderRadius: 8, whiteSpace: 'nowrap', marginLeft: 6,
                                  }}>{mine.dev_status}</span>
                                )}
                              </div>
                              <div style={{ marginBottom: 2 }}>
                                <strong>Distance:</strong> {fmtNum(mine.distance_km * KM_TO_MI, 1)} mi
                              </div>
                              {allCommodities.length > 0 && (
                                <div style={{ marginBottom: 2 }}>
                                  <svg width="10" height="10" viewBox="0 0 10 10" style={{ verticalAlign: 'middle', marginRight: 4 }}>
                                    <rect x="1" y="1" width="8" height="8" rx="1" transform="rotate(45 5 5)"
                                      fill={mColor.fill} stroke={mColor.border} strokeWidth="1" />
                                  </svg>
                                  <strong>Commodity:</strong> {allCommodities.join(', ')}
                                </div>
                              )}
                              {mine.oper_type && mine.oper_type !== 'Unknown' && (
                                <div style={{ marginBottom: 2 }}><strong>Operation:</strong> {mine.oper_type}</div>
                              )}
                              {mine.dep_type && (
                                <div style={{ marginBottom: 2 }}><strong>Deposit:</strong> {mine.dep_type}</div>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </LayerGroup>
                </LayersControl.Overlay>

                {/* ===== Overlay: Adjacent Parcels (by owner) (#703) =====
                    User-toggleable per the explicit June-2 follow-up ask
                    ("the ability to **turn on** parcel boundaries that
                    are clickable"). Default `checked` so the panel comes
                    up populated; users can hide it via the layer toggle
                    when it gets visually noisy. Rendered BEFORE the
                    target's bold gold outline (below) so the target
                    boundary still stays on top. */}
                <LayersControl.Overlay checked name="Adjacent Parcels (by owner)">
                  <LayerGroup>
                    <AdjacentParcelsOverlay
                      parcelId={parcelId}
                      state={parcel?.state}
                      county={parcel?.county}
                      navigate={navigate}
                    />
                  </LayerGroup>
                </LayersControl.Overlay>
              </LayersControl>

              {/* Credit link for the parcel data drawn on this map (#1289). */}
              <DataSourcesAttribution />

              {/* Parcel boundary */}
              {geojsonFeature && (
                <>
                  <GeoJSON key={parcelId} data={geojsonFeature}
                    style={{ color: '#FFD700', weight: 4, fillColor: '#FFD700', fillOpacity: 0.12, dashArray: '' }} />
                  <FitBounds geojson={geojsonFeature} />
                </>
              )}
              {!parcel.geometry && <Marker position={[lat, lon]} />}
            </MapContainer>
          </div>
          {/* Boundary unavailable indicator */}
          {!parcel.geometry && hasCoords && (
            <div style={{
              position: 'absolute', top: 10, left: 10, zIndex: 1000,
              background: 'rgba(0,0,0,0.55)', color: '#ccc',
              padding: '3px 10px', borderRadius: 6,
              fontSize: '0.72rem', backdropFilter: 'blur(4px)',
            }}>
              Boundary unavailable — showing approximate location
            </div>
          )}
          {/* Overlay: address + price on map. pointerEvents is off so the
              overlay does not sit on top of the map's attribution control,
              which carries the Data sources link (#1289) and the basemap
              credit links along the same bottom edge. */}
          <div className="pd-hero-overlay" style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 1000,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
            padding: '40px 20px 14px',
            color: '#fff',
            pointerEvents: 'none',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <h1 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                  {addr || `Parcel ${parcelId}`}
                </h1>
                <div className="pd-hero-meta" style={{ fontSize: '0.82rem', opacity: 0.85, marginTop: 2 }}>
                  {parcel.county && `${parcel.county} County, `}{parcel.state}
                  <span style={{ marginLeft: 10, opacity: 0.7 }}>{lat.toFixed(6)}, {lon.toFixed(6)}</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {parcel.listing_price && (
                  <div className="pd-hero-price" style={{ fontSize: '1.5rem', fontWeight: 800, color: '#FFD700', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                    {fmt$(parcel.listing_price)}
                    {pricePerAcre && <span style={{ fontSize: '0.8rem', fontWeight: 400, marginLeft: 8, color: '#ddd' }}>{pricePerAcre}/ac</span>}
                  </div>
                )}
                {parcel.is_listed && (() => {
                  // land-ai#1487: this badge used to read a flat green
                  // "ACTIVE LISTING" for any is_listed parcel. On 2026-10-02,
                  // 87.6% of active inventory had not been re-verified at the
                  // source in 44 days and every one of those rendered here as
                  // unqualified green. is_listed is unchanged; what changes is
                  // that the badge now carries the verification age when the
                  // claim is older than a week, so "listed" stops implying
                  // "confirmed today".
                  const state = parcel.verification_state;
                  const age = parcel.days_since_verified;
                  const stale = state === 'stale' || state === 'unverified';
                  const bg = state === 'unverified' ? '#95a5a6'
                    : state === 'stale' ? '#e67e22' : '#2ecc71';
                  return (
                    <span
                      title={verifiedTitle}
                      style={{ background: bg, color: '#fff', fontWeight: 700, padding: '2px 10px', borderRadius: 10, fontSize: '0.72rem', pointerEvents: 'auto' }}
                    >
                      {stale && age != null
                        ? `LISTED · UNVERIFIED ${age}d`
                        : 'ACTIVE LISTING'}
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compact action bar + stats */}
      <div className="pd-action-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
        <div className="pd-action-buttons" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={() => navigate(-1)} style={{ padding: '6px 12px', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>Back</button>
          <button className={bookmarkStatus === 'added' ? 'btn btn-secondary' : 'btn btn-primary'}
            onClick={handleBookmark} style={{ padding: '6px 12px', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
            {bookmarkStatus === 'added' ? 'Unbookmark' : 'Bookmark'}
          </button>
          {/* Hide / un-hide button (#124). When hidden, the parcel
              disappears from the map and lists by default and
              renders in red when "Show hidden" is on. Styled in
              danger-red when hidden so it's visually distinct from
              the bookmark control next to it. */}
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleDownvote}
            title={downvoteStatus === 'hidden'
              ? 'Un-hide this parcel so it reappears on the map'
              : 'Hide this parcel from your map and lists'}
            style={{
              padding: '6px 12px',
              fontSize: '0.82rem',
              whiteSpace: 'nowrap',
              ...(downvoteStatus === 'hidden'
                ? { borderColor: '#dc2626', color: '#dc2626' }
                : {}),
            }}
          >
            {downvoteStatus === 'hidden' ? 'Unhide' : 'Hide'}
          </button>
          {hasCoords && (
            <button
              type="button"
              className="btn btn-outline"
              // Pass parcel_id as `fp` (focus-parcel) so SearchPage's
              // FocusFlyer fetches the parcel polygon and fitBounds
              // to its actual boundary (#708) instead of the prior
              // centroid+z=14 which was a no-op when the user was
              // already on the map at the same location.
              onClick={() => navigate(
                `/search?c=${lat},${lon}&z=14&fp=${encodeURIComponent(parcelId)}`
              )}
              title="Open this parcel on the map"
              style={{ padding: '6px 12px', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
            >
              ◎ Locate
            </button>
          )}
          {/* Compare pin (#147 slice 2; reusable component in #789) —
              toggle membership in the global pin set; the
              ComparePinBar mounted in App.js surfaces the running set
              at the bottom of the viewport regardless of page, so the
              user can navigate freely and promote to /compare when
              ready. Uses the shared CompareButton so the visual
              contract matches PropertyCard / SavedParcels listings. */}
          <CompareButton parcelId={parcelId} variant="detail" />
          {/* Server-rendered HTML acquisition report (#101 PR1).
              Renders inline in a modal via an iframe srcDoc so the
              report's inline CSS stays sandboxed from the SPA. The
              ReportViewer dropdown lets the user pick among the
              templates the backend allowlists. */}
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setShowReportViewer(true)}
            title="Generate a server-rendered acquisition report for this parcel"
            style={{ padding: '6px 12px', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
          >
            Report
          </button>
          {/* One-page print view (#1171). Icon-only so it stays
              unobtrusive in the action bar; inline SVG matching
              lucide's Printer glyph geometry (lucide-react is not a
              project dependency — see InfoHint / ListingImageLightbox
              for the same convention). */}
          <Link
            to={`/parcel/${encodeURIComponent(parcelId)}/print`}
            className="btn btn-outline"
            title="Open a 1-page printable summary of this parcel"
            aria-label="Print one-page parcel summary"
            style={{
              padding: '6px 10px',
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" rx="1" />
            </svg>
          </Link>
          {/* Deep research (#1059) — enqueue the property-record
              maximizer and open the streaming research view in a new
              tab. Label flips to "Re-run research" once a record
              already exists for this parcel. */}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleDeepResearch}
            title="Run deep public-records research on this parcel (opens in a new tab)"
            style={{ padding: '6px 12px', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
          >
            {maxRecord && maxRecord.has_max_record ? 'Re-run research' : 'Research'}
          </button>
          {/* Flag-bad-data button. Sits at the right end of the
              action bar so it's near the other "do something with
              this parcel" affordances (bookmark / hide / report)
              rather than mixed in with navigation. The unresolved
              count indicator only renders when > 0 — for a clean
              parcel we don't want to bias the user into reporting
              something. */}
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setShowFlagModal(true)}
            title="Report bad data about this parcel"
            style={{ padding: '6px 12px', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
          >
            Flag
            {flagCounts.unresolved > 0 && (
              <span
                aria-label={`${flagCounts.unresolved} unresolved flags reported`}
                style={{
                  marginLeft: 6,
                  padding: '1px 7px',
                  background: '#fef3c7',
                  color: '#92400e',
                  border: '1px solid #fcd34d',
                  borderRadius: 10,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                }}
              >
                {flagCounts.unresolved}
              </span>
            )}
          </button>
        </div>
        <div className="pd-stat-row" style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="stat-box">
            <div className="stat-value" style={{ fontSize: '1.1rem' }}>
              {acreInfo.acres != null
                ? fmtNum(acreInfo.acres)
                : (acreInfo.unresolved ? 'Unresolved' : 'N/A')}
              {parcel.area_warning && (
                <span
                  title={parcel.area_warning}
                  style={{
                    marginLeft: 4,
                    cursor: 'help',
                    color: '#d97706',
                    fontSize: '0.8rem',
                  }}
                  aria-label="Acreage data quality warning"
                >⚠</span>
              )}
              {acreInfo.hasConflict && (
                <span
                  title={acreInfo.conflictLabel + (acreInfo.conflictReason ? `: ${acreInfo.conflictReason}` : '')}
                  style={{
                    marginLeft: 4,
                    cursor: 'help',
                    color: '#d97706',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                  }}
                  aria-label="Offered acreage differs from the parcel acreage"
                >!</span>
              )}
            </div>
            <div className="stat-label">Acres</div>
            {acreInfo.parcelAcres != null
              && (acreInfo.acres == null
                || Math.abs(acreInfo.acres - acreInfo.parcelAcres) >= 0.05) && (
              <div style={{ fontSize: '0.66rem', color: '#9ca3af', marginTop: 2 }}>
                {acreInfo.acres == null
                  ? `parcel is ${fmtNum(acreInfo.parcelAcres)} ac`
                  : `of a ${fmtNum(acreInfo.parcelAcres)} ac parcel`}
              </div>
            )}
          </div>
          {parcel.assessed_value && <div className="stat-box"><div className="stat-value" style={{ fontSize: '1.1rem' }}>{fmt$(parcel.assessed_value)}</div><div className="stat-label">Assessed</div></div>}
          {parcel.days_on_market != null && (
            <div className="stat-box">
              <div
                className="stat-value"
                style={{ fontSize: '1.1rem' }}
                title={parcel.days_on_market > 730
                  ? `${parcel.days_on_market} days — DOM beyond ~2 years often reflects a stale listed_date from a re-listing.`
                  : undefined}
              >{fmtDom(parcel.days_on_market)}</div>
              <div className="stat-label">DOM</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            {hasCoords && (
              <a className="btn btn-secondary" href={`https://maps.apple.com/?ll=${lat},${lon}&q=${lat},${lon}`}
                target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', padding: '6px 10px', fontSize: '0.78rem' }}>
                Maps
              </a>
            )}
            {hasCoords && (
              <a className="btn btn-secondary" href={`https://id.land/search?query=${lat.toFixed(6)},${lon.toFixed(6)}`}
                target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', padding: '6px 10px', fontSize: '0.78rem' }}>
                id.land
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Data-quality banner. Shown when the backend flagged the
          parcel's recorded acreage as suspect (e.g. polygon is
          actually a 36-mi² township section, not the lot listed —
          implied $/acre is impossibly low). The hover-only ⚠ icon
          on the stat box is too easy to miss for parcels this
          off, so we surface it inline as well. */}
      {parcel.area_warning && (
        <div
          role="alert"
          style={{
            margin: '0 0 12px 0',
            padding: '10px 14px',
            borderRadius: 6,
            background: '#fef3c7',
            border: '1px solid #fbbf24',
            color: '#78350f',
            fontSize: '0.85rem',
            lineHeight: 1.45,
          }}
        >
          <strong style={{ marginRight: 6 }}>⚠ Data quality:</strong>
          {parcel.area_warning}
        </div>
      )}

      {/* Usage-limitation hero banner (#153). Collects blocker-level
          and caution-level flags into one prominent warning strip so
          serious disqualifiers are impossible to miss. Shown only when
          at least one flag is present; otherwise renders nothing. */}
      {(() => {
        const blockers = [];
        const cautions = [];
        if (parcel.not_buildable)    blockers.push('Not buildable');
        if (parcel.has_lien)         blockers.push('Lien on title');
        if (parcel.restricted_use)   cautions.push('Restricted use');
        if (parcel.mineral_concern)  cautions.push('Mineral rights concern');
        if (parcel.has_easement)     cautions.push('Easement on record');
        // NLP signal — parcel listing description mentions wetlands
        if (parcel.wetlands)             cautions.push('Wetlands (mentioned)');
        // Geometric signal — USFWS NWI overlay says ≥5% of the
        // parcel is wetland (#121 C2 / migration 0080). Stronger
        // than the NLP signal because it's measured, not inferred.
        // Promote to blocker since wetlands ≥5% essentially can't
        // be built on without a Section 404 permit.
        if (parcel.has_wetlands === true) {
          const pct = parcel.wetlands_pct != null
            ? ` (${(parcel.wetlands_pct * 100).toFixed(0)}% wetland)`
            : '';
          blockers.push(`Wetlands (NWI)${pct}`);
        } else if (parcel.wetlands_pct != null && parcel.wetlands_pct > 0) {
          // 0 < wetlands_pct < 5% — under the buildability threshold
          // but worth noting.
          const pct = (parcel.wetlands_pct * 100).toFixed(0);
          cautions.push(`Minor wetland (${pct}%)`);
        }
        if (parcel.flood_mention)        cautions.push('Flood mention');
        if (parcel.development_rights)   cautions.push('Development rights mentioned');
        if (parcel.flood_score != null && parcel.flood_score > 70)
          cautions.push(`High flood risk (${Math.round(parcel.flood_score)}/100)`);
        if (parcel.wildfire_score != null && parcel.wildfire_score > 80)
          cautions.push(`High wildfire risk (${Math.round(parcel.wildfire_score)}/100)`);
        if (parcel.federal_land_agency)
          cautions.push(`Federal land (${parcel.federal_land_agency})`);
        if (parcel.insar_velocity_mm_yr != null && parcel.insar_velocity_mm_yr < -5)
          cautions.push(`Subsiding ${Math.abs(Math.round(parcel.insar_velocity_mm_yr))}mm/yr`);
        if (parcel.fault_dist_km != null && parcel.fault_dist_km < 5)
          cautions.push(`Active fault ${parcel.fault_dist_km.toFixed(1)}km away`);
        if (blockers.length === 0 && cautions.length === 0) return null;
        const isBlocker = blockers.length > 0;
        const allFlags = [...blockers, ...cautions];
        const total = allFlags.length;
        return (
          <div
            role="alert"
            style={{
              margin: '0 0 12px 0',
              padding: '10px 14px',
              borderRadius: 6,
              background: isBlocker ? '#fde8e8' : '#fef3c7',
              border: `1px solid ${isBlocker ? '#f87171' : '#fbbf24'}`,
              color: isBlocker ? '#7f1d1d' : '#78350f',
              fontSize: '0.85rem',
              lineHeight: 1.6,
            }}
          >
            <strong style={{ marginRight: 6 }}>
              {isBlocker ? '⛔' : '⚠'} {total} usage {total === 1 ? 'flag' : 'flags'}:
            </strong>
            {allFlags.map((f, i) => (
              <span key={f}>
                <span style={{
                  display: 'inline-block',
                  padding: '0px 6px',
                  borderRadius: 4,
                  background: blockers.includes(f) ? '#f87171' : '#fcd34d',
                  color: blockers.includes(f) ? '#fff' : '#78350f',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  marginLeft: i === 0 ? 2 : 6,
                }}>
                  {f}
                </span>
              </span>
            ))}
          </div>
        );
      })()}

      {/* Sticky header: hero summary strip + section tab nav (#147 slice 3).
          Wrapping both in a single sticky container ensures they travel as
          a unit — the hero card provides context while the tab nav lets
          the user jump between sections without scrolling back to the top. */}
      <div style={{ position: 'sticky', top: 0, zIndex: 950, background: '#fff' }}>
        <ParcelDetailHeroCard
          parcel={parcel}
          score={scores}
          bookmarkStatus={bookmarkStatus}
          onBookmark={handleBookmark}
          isCompared={isComparePinned(parcelId)}
          onCompareToggle={() => toggleComparePin(parcelId)}
        />
        <ParcelDetailTabNav activeTab={activeTab} onTabChange={handleTabChange} />
      </div>

      {/* =================== Main Grid =================== */}
      {/* Additive class hooks (#704 PR-5): `pd-detail-grid` /
          `pd-col-left` / `pd-col-right` let the tablet stylesheet
          re-tune the two-column shape (gap, sticky behavior, column
          ratio) without restructuring this JSX. Existing `.detail-grid`
          + `.detail-col` rules stay authoritative on phone/desktop. */}
      <div className="detail-grid pd-detail-grid">

        {/* --- Left Column --- */}
        <div className="detail-col pd-col-left">

          {/* Terrain preview — hillshade-tinted DEM with parcel
              outline. 503 if the parcel is outside USGS 3DEP
              coverage; we just hide the card silently in that case. */}
          {showTab('tab-overview') && parcel.is_listed && (
            <section>
            <div className="detail-card">
              <h3 id="tab-overview">Listing Details</h3>

              {/* Photo gallery */}
              {listingMedia?.photo_urls?.length > 0 && (() => {
                // Per-photo aesthetic scores (task #128). The backend
                // returns a parallel `photos` array of {url, score, notes}
                // — build a lookup so the gallery + selected viewer can
                // show a small score badge inline.  Fall back to an empty
                // map when the scorer hasn't run yet so the UI degrades
                // cleanly to "no chip, no badge".
                const _photoScoreMap = {};
                (listingMedia.photos || []).forEach((p) => {
                  if (p && p.url && typeof p.score === 'number') {
                    _photoScoreMap[p.url] = { score: p.score, notes: p.notes };
                  }
                });
                const _currentUrl = selectedPhoto || listingMedia.photo_urls[0];
                const _currentScore = _photoScoreMap[_currentUrl];
                const _aesthetic = listingMedia.aesthetic;
                // 0-100 -> traffic-light color; matches the resource-score
                // chips elsewhere on the page (red < 35, amber 35-65, green >65).
                const _scoreColor = (s) => {
                  if (s == null) return '#888';
                  if (s >= 65) return '#2e7d32';
                  if (s >= 35) return '#ed6c02';
                  return '#c62828';
                };
                return (
                  <div style={{ marginBottom: 16 }}>
                    {/* Parcel-level Visual appeal chip. Shows the row-level
                        aesthetic_score (which the scorer wrote as the mean
                        across sampled photos) + a note on the sample size
                        so users can tell "scored across 8 of 34 photos". */}
                    {_aesthetic && typeof _aesthetic.score === 'number' && (
                      <div
                        title={_aesthetic.notes || ''}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          marginBottom: 8,
                          padding: '4px 10px',
                          borderRadius: 999,
                          background: '#f0f4f8',
                          border: `1px solid ${_scoreColor(_aesthetic.score)}33`,
                          fontSize: '0.78rem',
                          color: '#333',
                        }}
                      >
                        <span aria-hidden="true">📸</span>
                        <span>Visual appeal:&nbsp;</span>
                        <strong style={{ color: _scoreColor(_aesthetic.score) }}>
                          {_aesthetic.score}
                        </strong>
                        {_aesthetic.n_scored > 0 && (
                          <span style={{ color: '#777', fontSize: '0.72rem' }}>
                            ({_aesthetic.n_scored}/{_aesthetic.n_photos} photos)
                          </span>
                        )}
                      </div>
                    )}
                    {/* Main photo. The container fixes a 16:9 aspect
                        so the layout doesn't reflow as different photos
                        load (Redfin returns a mix of `bigphoto` full-res
                        and `mbphotov3/genMid` thumbnails — they have
                        wildly different natural dimensions). The image
                        is positioned absolutely inside and cover-fits. */}
                    <div style={{
                      position: 'relative',
                      borderRadius: 8,
                      overflow: 'hidden',
                      marginBottom: 8,
                      background: '#f5f5f5',
                      width: '100%',
                      aspectRatio: '16 / 9',
                      maxHeight: 320,
                    }}>
                      <img
                        ref={mainPhotoRef}
                        src={_currentUrl}
                        alt="Listing"
                        role="button"
                        tabIndex={0}
                        aria-label="Open full-size photo viewer"
                        onError={(e) => {
                          // A listing photo URL that 404s / times out
                          // would otherwise show the browser's broken-
                          // image glyph (#1018). Swap to the shared SVG
                          // placeholder exactly once — the `data-fallback`
                          // flag guards against a reload loop if the
                          // placeholder itself somehow errors.
                          if (e.currentTarget.getAttribute('data-fallback')) return;
                          e.currentTarget.setAttribute('data-fallback', '1');
                          e.currentTarget.src = PLACEHOLDER_IMG;
                        }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          // #1144: open the in-app lightbox at this
                          // photo instead of dumping the raw image URL
                          // into a dead-end new browser tab.
                          const _i = listingMedia.photo_urls.indexOf(_currentUrl);
                          setLightboxIndex(_i >= 0 ? _i : 0);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            const _i = listingMedia.photo_urls.indexOf(_currentUrl);
                            setLightboxIndex(_i >= 0 ? _i : 0);
                          }
                        }}
                      />
                      {/* Per-photo score badge on the currently-viewed photo. */}
                      {_currentScore && (
                        <div
                          title={_currentScore.notes || ''}
                          style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            background: 'rgba(0,0,0,0.72)',
                            color: '#fff',
                            padding: '3px 9px',
                            borderRadius: 999,
                            fontSize: '0.72rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <span aria-hidden="true">📸</span>
                          <strong style={{ color: _scoreColor(_currentScore.score) }}>
                            {_currentScore.score}
                          </strong>
                        </div>
                      )}
                      <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem' }}>
                        {listingMedia.photo_urls.length} photo{listingMedia.photo_urls.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    {/* Thumbnail strip */}
                    {listingMedia.photo_urls.length > 1 && (
                      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                        {listingMedia.photo_urls.map((url, idx) => {
                          const thumbScore = _photoScoreMap[url];
                          return (
                            <div
                              key={idx}
                              style={{ position: 'relative', flexShrink: 0 }}
                            >
                              <img
                                src={url}
                                alt={`Photo ${idx + 1}`}
                                onError={(e) => {
                                  // Hide a thumbnail whose photo URL
                                  // 404s so the strip doesn't show a
                                  // broken-image glyph among the good
                                  // thumbs (#1018). The main gallery
                                  // still falls back to the placeholder.
                                  e.currentTarget.style.display = 'none';
                                }}
                                style={{
                                  width: 56, height: 42, objectFit: 'cover', borderRadius: 4, cursor: 'pointer',
                                  border: _currentUrl === url ? '2px solid #2196F3' : '2px solid transparent',
                                  opacity: _currentUrl === url ? 1 : 0.7,
                                }}
                                onClick={() => setSelectedPhoto(url)}
                              />
                              {/* Tiny per-thumb score so the user can spot the best photos at a glance. */}
                              {thumbScore && (
                                <div
                                  title={`Visual appeal: ${thumbScore.score}/100`}
                                  style={{
                                    position: 'absolute',
                                    bottom: 2,
                                    right: 2,
                                    background: 'rgba(0,0,0,0.78)',
                                    color: _scoreColor(thumbScore.score),
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    padding: '0 4px',
                                    borderRadius: 3,
                                    lineHeight: '12px',
                                    pointerEvents: 'none',
                                  }}
                                >
                                  {thumbScore.score}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* #1144: full-size photo lightbox with prev/next
                        navigation + keyboard control, replacing the old
                        dead-end "open raw image in a new tab" behaviour. */}
                    {lightboxIndex !== null && (
                      <ListingImageLightbox
                        photos={listingMedia.photo_urls}
                        startIndex={lightboxIndex}
                        onClose={() => setLightboxIndex(null)}
                        onNavigate={(url) => setSelectedPhoto(url)}
                        returnFocusRef={mainPhotoRef}
                      />
                    )}
                  </div>
                );
              })()}

              {/* Listing description */}
              {listingMedia?.listing_description && (
                <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #eee' }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#999', letterSpacing: 0.5, marginBottom: 6 }}>Description</div>
                  <div style={{ fontSize: '0.85rem', lineHeight: 1.55, color: '#333', whiteSpace: 'pre-wrap' }}>
                    {listingMedia.listing_description}
                  </div>
                </div>
              )}

              {/* Listing details extracted (#781 / INIT-description-ner).
                  Renders extracted structured features as compact chips
                  when an extraction is available. NULL features are
                  omitted entirely — absence of mention isn't evidence
                  of absence, so we don't surface them. */}
              {listingMedia?.description_features?.features && (() => {
                const f = listingMedia.description_features.features;
                const chips = [];
                if (f.gated_community === true) chips.push({ label: 'Gated community', tone: '#16a34a' });
                if (f.gated_community === false) chips.push({ label: 'Not gated', tone: '#64748b' });
                if (f.has_hoa === true) chips.push({ label: 'Has HOA', tone: '#f59e0b' });
                if (f.has_hoa === false) chips.push({ label: 'No HOA', tone: '#16a34a' });
                if (f.subdividable === true) chips.push({ label: 'Subdividable', tone: '#16a34a' });
                if (f.subdividable === false) chips.push({ label: 'Not subdividable', tone: '#64748b' });
                if (f.has_water_feature === true) {
                  const types = Array.isArray(f.water_features) && f.water_features.length
                    ? `Water: ${f.water_features.join(', ')}`
                    : 'Water feature';
                  chips.push({ label: types, tone: '#0284c7' });
                }
                if (typeof f.frontage_ft === 'number') {
                  chips.push({ label: `${f.frontage_ft} ft frontage`, tone: '#0284c7' });
                }
                if (typeof f.road_maintenance === 'string' && f.road_maintenance) {
                  chips.push({ label: `${f.road_maintenance.charAt(0).toUpperCase()}${f.road_maintenance.slice(1)} road`, tone: '#64748b' });
                }
                if (f.has_well === true) chips.push({ label: 'Well', tone: '#0284c7' });
                if (f.has_septic === true) chips.push({ label: 'Septic', tone: '#0284c7' });
                if (f.has_electric === true) chips.push({ label: 'Electric', tone: '#0284c7' });
                if (Array.isArray(f.view_type) && f.view_type.length > 0) {
                  chips.push({ label: `${f.view_type.join(', ')} views`, tone: '#7c3aed' });
                }
                if (typeof f.mineral_rights_mentioned === 'string' && f.mineral_rights_mentioned) {
                  chips.push({
                    label: f.mineral_rights_mentioned === 'severed' ? 'Mineral rights severed' : 'Mineral rights included',
                    tone: f.mineral_rights_mentioned === 'severed' ? '#dc2626' : '#16a34a',
                  });
                }
                if (f.livestock_friendly === true) chips.push({ label: 'Livestock friendly', tone: '#16a34a' });
                if (chips.length === 0) return null;
                return (
                  <div style={{ marginBottom: 16, padding: '10px 12px', background: '#fafbff', borderRadius: 6, border: '1px solid #e3e6f1' }}>
                    <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#999', letterSpacing: 0.5, marginBottom: 6, display: 'flex', alignItems: 'center' }}>
                      <span>Listing details extracted</span>
                      <InfoHint label="About extracted listing details">
                        Auto-extracted from the seller's free-text description
                        by Claude. Absent fields aren&apos;t evidence of absence.
                      </InfoHint>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {chips.map((chip, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: '0.72rem',
                            padding: '3px 8px',
                            borderRadius: 12,
                            background: `${chip.tone}1a`,
                            border: `1px solid ${chip.tone}55`,
                            color: chip.tone,
                            fontWeight: 500,
                          }}
                        >
                          {chip.label}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Photo summary (#783 / INIT-photo-content). Renders 5 boolean
                  aggregate chips + a clear_photo_pct chip when a classifier
                  pass has run for this listing. NULL aggregates are skipped
                  (an unclassified listing isn't evidence its gallery doesn't
                  contain the feature). Sits next to the description-features
                  card so a user can scan both signal panes in one glance. */}
              {listingMedia?.photo_content?.aggregates && (() => {
                const agg = listingMedia.photo_content.aggregates;
                const chips = [];
                if (agg.has_house_photo === true) chips.push({ label: 'House photo', tone: '#16a34a' });
                if (agg.has_water_photo === true) chips.push({ label: 'Water photo', tone: '#0284c7' });
                if (agg.has_open_field_photo === true) chips.push({ label: 'Open field', tone: '#16a34a' });
                if (agg.has_long_view_photo === true) chips.push({ label: 'Long view', tone: '#7c3aed' });
                if (agg.has_outbuildings_photo === true) chips.push({ label: 'Outbuildings', tone: '#64748b' });
                if (typeof agg.clear_photo_pct === 'number') {
                  const pct = Math.round(agg.clear_photo_pct * 100);
                  const tone = pct >= 70 ? '#16a34a' : pct >= 40 ? '#f59e0b' : '#dc2626';
                  chips.push({ label: `${pct}% clear photos`, tone });
                }
                if (chips.length === 0) return null;
                return (
                  <div style={{ marginBottom: 16, padding: '10px 12px', background: '#fafbff', borderRadius: 6, border: '1px solid #e3e6f1' }}>
                    <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#999', letterSpacing: 0.5, marginBottom: 6, display: 'flex', alignItems: 'center' }}>
                      <span>Photo summary</span>
                      <InfoHint label="About photo summary">
                        Aggregated from per-photo Claude-vision tags. Absent
                        chips aren&apos;t evidence the gallery lacks them.
                      </InfoHint>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {chips.map((chip, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: '0.72rem',
                            padding: '3px 8px',
                            borderRadius: 12,
                            background: `${chip.tone}1a`,
                            border: `1px solid ${chip.tone}55`,
                            color: chip.tone,
                            fontWeight: 500,
                          }}
                        >
                          {chip.label}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div className="info-grid">
                <InfoRow label="Price" value={fmt$(parcel.listing_price)} />
                <InfoRow label="Price/Acre" value={pricePerAcre} />
                <InfoRow label="Days on Market" value={fmtDom(parcel.days_on_market)} />
                <InfoRow label="Listed Date" value={fmtDate(parcel.listed_date)} />
                <InfoRow label="Last Verified" value={verifiedLabel} />
                <InfoRow label="Listing Type" value={parcel.listing_type} />
                <InfoRow label="Property Type" value={parcel.property_type} />
                <InfoRow label="MLS #" value={parcel.mls_number} />
                <InfoRow label="MLS Name" value={parcel.mls_name} />
                <InfoRow label="Address" value={parcel.listing_address} full />
              </div>

              {/* Agent / office info from listing media */}
              {listingMedia?.has_media && (listingMedia.listing_agent_name || listingMedia.listing_office_name) && (
                <div style={{ borderTop: '1px solid #eee', marginTop: 12, paddingTop: 12 }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#999', letterSpacing: 0.5, marginBottom: 8 }}>Listing Agent</div>
                  <div className="info-grid">
                    <InfoRow label="Agent" value={listingMedia.listing_agent_name} />
                    <InfoRow label="Phone" value={listingMedia.listing_agent_phone} />
                    <InfoRow label="Office" value={listingMedia.listing_office_name} />
                  </div>
                </div>
              )}

              {/* Source link — URL-scheme sanitized to block
                  javascript:/data: XSS via third-party listing data */}
              {safeUrl(listingMedia?.source_url) && (
                <div style={{ marginTop: 10, textAlign: 'right' }}>
                  <a href={safeUrl(listingMedia.source_url)} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: '0.75rem', color: '#2196F3' }}>
                    View on {listingMedia.source === 'realtor' ? 'Realtor.com' : listingMedia.source || 'Source'}
                  </a>
                </div>
              )}
            </div>
            </section>
          )}
          {showTab('tab-overview') && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Public-Records Research</span>
                <InfoHint label="About Public-Records Research">
                  Sends a Claude agent to find this parcel's county-assessor
                  record. Results cached for 30 days; ~90-240&nbsp;s per fresh
                  run. Experimental.
                </InfoHint>
              </h3>
            <div className="detail-card">
              <h3 style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>Public-Records Research</h3>
              {(() => {
                const r = research;
                const inProgress = researchStatus === 'queued' || researchStatus === 'running' || researchStatus === 'pending';
                const ex = r?.structured_extract;
                if (researchStatus === 'failed') {
                  return (
                    <div style={{ padding: 10, background: '#fdecea', border: '1px solid #f5c2c0', borderRadius: 4, fontSize: '0.85rem' }}>
                      <strong>Research failed.</strong>{' '}
                      <span style={{ color: '#7a2020' }}>{researchError}</span>
                      <div style={{ marginTop: 8 }}>
                        <button className="btn btn-outline" onClick={handleResearch}>Try again</button>
                      </div>
                    </div>
                  );
                }
                if (inProgress) {
                  return (
                    <div style={{ padding: 10, background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, fontSize: '0.85rem' }}>
                      <strong>Researching…</strong> {researchStatus === 'pending' ? 'queued' : researchStatus}. Polling every 5 seconds.
                    </div>
                  );
                }
                if (r && ex) {
                  const fields = ex.fields || {};
                  const fieldOrder = [
                    ['owner_name', 'Owner'],
                    ['owner_mailing_address', 'Owner mailing'],
                    ['assessed_value_total', 'Assessed total'],
                    ['land_value', 'Land value'],
                    ['building_value', 'Building value'],
                    ['tax_amount_annual', 'Annual tax'],
                    ['assessed_year', 'Assessed year'],
                    ['last_sale_date', 'Last sale'],
                    ['last_sale_price', 'Last sale price'],
                    ['lot_size_acres', 'Lot acres'],
                    ['zoning', 'Zoning'],
                    ['land_use', 'Land use'],
                    ['legal_description', 'Legal description'],
                    ['subdivision', 'Subdivision'],
                  ];
                  return (
                    <>
                      <div style={{ marginBottom: 8, fontSize: '0.85rem' }}>
                        <span style={{ color: '#666' }}>Status:</span>{' '}
                        <strong style={{ color: ex.found ? '#0d8e3a' : '#a33' }}>
                          {ex.found ? 'Found' : 'Not found'}
                        </strong>
                        {ex.confidence && (
                          <span style={{ marginLeft: 8, color: '#666' }}>
                            Confidence: <strong>{ex.confidence}</strong>
                          </span>
                        )}
                        {r.completed_at && (
                          <span style={{ marginLeft: 8, color: '#999', fontSize: '0.78rem' }}>
                            {new Date(r.completed_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {fieldOrder.map(([k, label]) => {
                        const v = fields[k];
                        if (!v || v.value == null || v.value === '') return null;
                        return (
                          <div key={k} style={{ display: 'flex', gap: 8, fontSize: '0.85rem', padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
                            <span style={{ width: 140, color: '#666' }}>{label}</span>
                            <span style={{ flex: 1 }}>
                              {typeof v.value === 'number' ? v.value.toLocaleString() : String(v.value)}
                            </span>
                          </div>
                        );
                      })}
                      {ex.notes && (
                        <div style={{ marginTop: 10, padding: 8, background: '#f8fafc', fontSize: '0.78rem', color: '#666', whiteSpace: 'pre-wrap', borderRadius: 3 }}>
                          <strong>Notes:</strong> {ex.notes}
                        </div>
                      )}
                      {(ex.source_site || r.source_url_used) && (
                        <div style={{ marginTop: 8, fontSize: '0.78rem' }}>
                          Source:&nbsp;
                          <a href={r.source_url_used || ex.source_site} target="_blank" rel="noopener noreferrer" style={{ color: '#0a58ca' }}>
                            {ex.source_site || r.source_url_used}
                          </a>
                        </div>
                      )}
                      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                        <button className="btn btn-outline" onClick={handleResearch} disabled={inProgress}>
                          Re-run research
                        </button>
                        {r.research_id && (
                          <button
                            className="btn btn-outline"
                            onClick={() => setShowTranscript(r.research_id)}
                          >
                            View transcript
                          </button>
                        )}
                      </div>
                    </>
                  );
                }
                return (
                  <div>
                    <button className="btn btn-primary" onClick={handleResearch} disabled={inProgress}>
                      Research this parcel
                    </button>
                    <p style={{ fontSize: '0.78rem', color: '#999', marginTop: 6 }}>
                      No cached research for this parcel yet.
                    </p>
                  </div>
                );
              })()}
            </div>
            </section>
          )}
          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 24 }}>Research</div>
          <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0 12px' }} aria-hidden="true" />
          {showTab('tab-overview') && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0 }}>Property Info</h3>
            <div className="detail-card">
              <div className="info-grid">
                <InfoRow label="Parcel ID" value={parcel.parcel_id} full />
                <InfoRow label="APN" value={parcel.apn} />
                <InfoRow label="Owner" value={parcel.owner} />
                {/* Owner concentration / longevity (migration 0040). When the
                    owner holds many parcels we say so; when they've held this
                    one a long time we say that too. Empty/null → row omitted
                    by InfoRow. */}
                <InfoRow
                  label="Owner Portfolio"
                  value={(() => {
                    const n = parcel.owner_parcel_count;
                    if (!n || n < 2) return null;
                    const parts = [`${n} parcels`];
                    if (parcel.owner_res_count != null || parcel.owner_com_count != null) {
                      const sub = [];
                      if (parcel.owner_res_count != null) sub.push(`${parcel.owner_res_count} res`);
                      if (parcel.owner_com_count != null) sub.push(`${parcel.owner_com_count} com`);
                      if (sub.length) parts.push(`(${sub.join(', ')})`);
                    }
                    return parts.join(' ');
                  })()}
                />
                <InfoRow
                  label="Owner Since"
                  value={parcel.ownership_start_date
                    ? fmtDate(parcel.ownership_start_date) : null}
                />
                <InfoRow label="State" value={parcel.state} />
                <InfoRow label="County" value={parcel.county} />
                <InfoRow label="Zoning" value={parcel.zoning} />
                <InfoRow label="Land Use" value={parcel.land_use_code} />
                <InfoRow label="Lot Size" value={parcel.lot_size_sqft ? `${fmtNum(parcel.lot_size_sqft, 0)} sqft` : null} />
                <InfoRow label="Subdivision" value={parcel.subdivision} full />
                <InfoRow label="Legal Desc." value={parcel.legal_description} full />
              </div>
            </div>
            </section>
          )}
          {/* Tax & Financial (with RentCast values merged in) */}
          {/* Compact mineral-probability strip — top 3 commodities at a
              glance. The full periodic-table grid still lives on the
              Minerals tab; this is the "I want to see the dials without
              clicking" surface the buyer scans early.
              Render rule: only when a non-Minerals tab is explicitly
              selected. With no tab active (default flat scroll) the
              full grid already shows in the right column, so a second
              dial set would just be visual duplication. */}
          {activeTab && activeTab !== 'tab-minerals' && commodityScores && commodityScores.scores && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Mineral Probability</span>
                <InfoHint label="About mineral probability">
                  Top-3 commodities from a LightGBM model trained on USGS
                  MRDS deposit data + geological features. The full grid
                  (12 commodities + calibration distances) lives on the
                  Minerals tab.
                </InfoHint>
              </h3>
              <div className="detail-card" data-testid="pd-mineral-strip">
                {(() => {
                  const ranked = COMMODITY_TILES
                    .map((t) => ({ ...t, prob: commodityScores.scores[t.key] }))
                    .filter((t) => t.prob != null)
                    .sort((a, b) => b.prob - a.prob)
                    .slice(0, 3);
                  if (ranked.length === 0) return (
                    <p style={{ fontSize: '0.78rem', color: '#aaa', margin: 0 }}>No mineral scores available.</p>
                  );
                  const csPct = commodityScores.percentiles || {};
                  return (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      {ranked.map(({ key, symbol, name, color, border, prob }) => {
                        // #1027: display the server-computed percentile (0-99,
                        // uniform-normalization epic) rather than the raw
                        // `prob*100`. Fall back to the raw probability when the
                        // percentile map is absent/empty (older cached rows).
                        const pctVal = csPct[key];
                        const pct = pctVal != null ? Math.round(pctVal) : Math.round(prob * 100);
                        const ringBg = `conic-gradient(${color} ${pct * 3.6}deg, #e8e8e8 ${pct * 3.6}deg)`;
                        const isHigh = prob >= 0.80;
                        return (
                          <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
                            <div style={{
                              width: 38, height: 38, borderRadius: '50%',
                              background: ringBg,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <div style={{
                                width: 30, height: 30, borderRadius: '50%',
                                background: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 900, fontSize: '0.85rem',
                                color: isHigh ? border : '#555',
                                fontFamily: "'Georgia', serif",
                              }}>
                                {symbol}
                              </div>
                            </div>
                            <div style={{ fontSize: '0.62rem', color: '#888', marginTop: 4 }}>{name}</div>
                            <div style={{
                              fontSize: '0.78rem', fontWeight: 800,
                              color: isHigh ? border : '#555',
                            }}>
                              {pct}%
                            </div>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => handleTabChange('tab-minerals')}
                        style={{
                          marginLeft: 'auto',
                          background: 'transparent',
                          border: '1px solid #d1d5da',
                          color: '#1565c0',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          padding: '4px 10px',
                          borderRadius: 999,
                          cursor: 'pointer',
                        }}
                      >
                        See all →
                      </button>
                    </div>
                  );
                })()}
              </div>
            </section>
          )}
          {showTab('tab-overview') && <section>
            <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0 }}>Tax &amp; Financial</h3>
            <div className="detail-card">
            {(parcel.assessed_value || parcel.tax_value || parcel.last_sale_price) ? (
              <div className="info-grid">
                <InfoRow label="Assessed Value" value={fmt$(parcel.assessed_value)} />
                <InfoRow label="Assessed Year" value={parcel.assessed_year} />
                <InfoRow label="Tax Value" value={fmt$(parcel.tax_value)} />
                <InfoRow label="Tax Year" value={parcel.tax_year} />
                <InfoRow label="Last Sale Price" value={fmt$(parcel.last_sale_price)} />
                <InfoRow label="Last Sale Date" value={fmtDate(parcel.last_sale_date)} />
              </div>
            ) : (
              <p style={{ fontSize: '0.82rem', color: '#aaa', margin: '0 0 8px 0' }}>No tax or assessment data available.</p>
            )}
            {/* Distressed-seller details (#629). Renders only when a
                foreclosure has been recorded — most parcels skip this
                section entirely. */}
            {parcel.foreclose_code && (
              <div style={{ borderTop: '1px solid #eee', marginTop: 12, paddingTop: 12 }}>
                <div style={{ fontSize: '0.72rem', color: '#b91c1c', marginBottom: 10,
                              fontWeight: 700, letterSpacing: 0.5 }}>
                  DISTRESSED SALE
                </div>
                <div className="info-grid">
                  <InfoRow label="Foreclosure Code" value={parcel.foreclose_code} />
                  <InfoRow label="Auction Date" value={parcel.auction_date ? fmtDate(parcel.auction_date) : null} />
                  <InfoRow label="Record Date" value={parcel.foreclose_record_date ? fmtDate(parcel.foreclose_record_date) : null} />
                  <InfoRow label="File Date" value={parcel.foreclose_file_date ? fmtDate(parcel.foreclose_file_date) : null} />
                  <InfoRow label="Lender" value={parcel.lender_name} />
                  <InfoRow label="Case Number" value={parcel.foreclose_case_num} />
                  <InfoRow label="Lien Count" value={parcel.total_lien_count} />
                  <InfoRow label="Total Lien Balance" value={fmt$(parcel.total_lien_balance)} />
                </div>
              </div>
            )}
            {/* Realie land-valuation breakdown (migration 0040). When
                total_building_value ≈ 0 the parcel is effectively
                land-only regardless of the listing's stated use code —
                we mark it accordingly. */}
            {(parcel.total_land_value != null || parcel.total_building_value != null
              || parcel.total_market_value != null) && (
              <div style={{ borderTop: '1px solid #eee', marginTop: 12, paddingTop: 12 }}>
                <div style={{ fontSize: '0.72rem', color: '#999', marginBottom: 10,
                              display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>VALUATION BREAKDOWN (REALIE)</span>
                  {(parcel.total_building_value != null
                    && parcel.total_building_value <= 1000) && (
                    <span style={{ background: '#10b98120', color: '#065f46',
                                   padding: '2px 8px', borderRadius: 10,
                                   fontSize: '0.66rem', fontWeight: 700,
                                   letterSpacing: 0.3 }}>
                      LAND-ONLY ✓
                    </span>
                  )}
                </div>
                <div className="info-grid">
                  <InfoRow label="Land Value" value={fmt$(parcel.total_land_value)} />
                  <InfoRow label="Building Value" value={fmt$(parcel.total_building_value)} />
                  <InfoRow label="Total Market Value" value={fmt$(parcel.total_market_value)} />
                </div>
              </div>
            )}
            {/* RentCast Value & Rent Estimates */}
            <div style={{ borderTop: '1px solid #eee', marginTop: 12, paddingTop: 12 }}>
              <div style={{ fontSize: '0.72rem', color: '#999', marginBottom: 10 }}>
                RentCast AVM Estimates
                {parcel.rc_estimates_updated_at && ` | Updated ${fmtDate(parcel.rc_estimates_updated_at)}`}
              </div>
            </div>
            {(parcel.rc_value_estimate || parcel.rc_rent_estimate) ? (
              <>
                {/* Value Estimate */}
                {parcel.rc_value_estimate && (
                  <div style={{
                    padding: '12px 14px', borderRadius: 8, marginBottom: 10,
                    background: 'linear-gradient(135deg, #3498db18, #3498db08)',
                    border: '1px solid #3498db40',
                  }}>
                    <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#3498db', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                      Estimated Value
                    </div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#222' }}>
                      {fmt$(parcel.rc_value_estimate)}
                    </div>
                    {(parcel.rc_value_low || parcel.rc_value_high) && (
                      <div style={{ fontSize: '0.82rem', color: '#666', marginTop: 4 }}>
                        Range: {fmt$(parcel.rc_value_low)} &ndash; {fmt$(parcel.rc_value_high)}
                      </div>
                    )}
                    {_parcelAcres > 0 && (
                      <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 2 }}>
                        {fmt$(parcel.rc_value_estimate / _parcelAcres)}/acre
                      </div>
                    )}
                  </div>
                )}
                {/* Rent Estimate */}
                {parcel.rc_rent_estimate && (
                  <div style={{
                    padding: '12px 14px', borderRadius: 8,
                    background: 'linear-gradient(135deg, #27ae6018, #27ae6008)',
                    border: '1px solid #27ae6040',
                  }}>
                    <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#27ae60', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                      Estimated Monthly Rent
                    </div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#222' }}>
                      {fmt$(parcel.rc_rent_estimate)}<span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#888' }}>/mo</span>
                    </div>
                    {(parcel.rc_rent_low || parcel.rc_rent_high) && (
                      <div style={{ fontSize: '0.82rem', color: '#666', marginTop: 4 }}>
                        Range: {fmt$(parcel.rc_rent_low)} &ndash; {fmt$(parcel.rc_rent_high)}/mo
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p style={{ fontSize: '0.82rem', color: '#aaa', margin: 0 }}>No RentCast estimates available for this parcel.</p>
            )}
          </div>
          </section>}
          {/* Uruguay Cadastre — only for state='UY' parcels with a joined
              uy_parcel_attrs row (#1255 M4). */}
          {showTab('tab-overview') && parcel.uy_attrs && (
          <section>
            <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0 }}>Uruguay Cadastre</h3>
          <div className="detail-card">
            <div className="info-grid">
              <InfoRow label="Padrón" value={`${parcel.uy_attrs.departamento_code}-${parcel.uy_attrs.padron}`} />
              {parcel.uy_attrs.seccat != null && (
                <InfoRow label="Sección Catastral" value={parcel.uy_attrs.seccat} />
              )}
              {parcel.uy_attrs.valor_catastral_uyu != null && (
                <InfoRow
                  label="Valor Catastral"
                  value={
                    <>
                      {`UYU ${fmtNum(parcel.uy_attrs.valor_catastral_uyu, 0)}`}
                      {parcel.uy_attrs.assessed_value_usd != null && (
                        <div style={{ fontSize: '0.72rem', color: '#888', fontWeight: 400, marginTop: 2 }}>
                          {`$${fmtNum(parcel.uy_attrs.assessed_value_usd, 0)} USD`}
                          {parcel.uy_attrs.fx_rate_uyu_usd != null && ` @ ${fmtNum(parcel.uy_attrs.fx_rate_uyu_usd, 4)} UYU/USD`}
                          {parcel.uy_attrs.fx_source && ` · ${parcel.uy_attrs.fx_source}`}
                          {parcel.uy_attrs.fx_date && ` · ${parcel.uy_attrs.fx_date}`}
                        </div>
                      )}
                    </>
                  }
                />
              )}
              {parcel.uy_attrs.valor_impuestos_uyu != null && (
                <InfoRow label="Valor Impuestos" value={`UYU ${fmtNum(parcel.uy_attrs.valor_impuestos_uyu, 0)}`} />
              )}
              {parcel.uy_attrs.area_m2 != null && (
                <InfoRow label="Área" value={`${(parcel.uy_attrs.area_m2 / 10000).toFixed(2)} ha`} />
              )}
              {parcel.uy_attrs.coneat_index != null && (
                <InfoRow label="CONEAT Index" value={fmtNum(parcel.uy_attrs.coneat_index, 2)} />
              )}
              {parcel.uy_attrs.source_snapshot && (
                <InfoRow label="Source Snapshot" value={parcel.uy_attrs.source_snapshot} />
              )}
            </div>
          </div>
          </section>)}
          {/* Uruguay water rights + hydrography (#1257). Same overview
              tab and the same UY gate as the cadastre block above. */}
          {showTab('tab-overview') && parcel.uy_water && (
            <UYWaterPanel water={parcel.uy_water} />
          )}
          {/* Listing and Price History — only show when data exists */}
          {showTab('tab-overview') && listingHistory && listingHistory.count > 0 && (
          <section>
            <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0 }}>Listing &amp; Price History</h3>
          <div className="detail-card">
            <p style={{ fontSize: '0.78rem', color: '#888', margin: '0 0 10px 0' }}>
                  {listingHistory.count} record{listingHistory.count !== 1 ? 's' : ''} from RentCast
                </p>
                <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                  {listingHistory.history.map((h, i) => {
                    const statusColors = {
                      'Active': '#2ecc71', 'Pending': '#f39c12', 'Sold': '#3498db',
                      'Closed': '#9b59b6', 'Withdrawn': '#95a5a6', 'Expired': '#e74c3c',
                    };
                    const sc = statusColors[h.listing_status] || '#888';
                    return (
                      <div key={i} style={{
                        padding: '8px 10px', borderRadius: 6,
                        border: '1px solid #eee', background: i === 0 ? '#f8f9fa' : '#fff',
                        marginBottom: 6, position: 'relative',
                        borderLeft: `3px solid ${sc}`,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222' }}>
                              {h.listing_price ? fmt$(h.listing_price) : 'N/A'}
                            </span>
                            {h.price_per_acre && (
                              <span style={{ fontSize: '0.78rem', color: '#888', marginLeft: 6 }}>
                                ({fmt$(h.price_per_acre)}/ac)
                              </span>
                            )}
                          </div>
                          {h.listing_status && (
                            <span style={{
                              fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px',
                              borderRadius: 10, background: sc, color: '#fff',
                            }}>
                              {h.listing_status}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <span>{fmtDate(h.snapshot_date)}</span>
                          {h.days_on_market != null && <span>{fmtDom(h.days_on_market)} on market</span>}
                          {h.listing_type && <span>{h.listing_type}</span>}
                          {h.mls_number && <span>MLS #{h.mls_number}</span>}
                        </div>
                        {h.listing_address && (
                          <div style={{ fontSize: '0.72rem', color: '#999', marginTop: 2, fontStyle: 'italic' }}>
                            {h.listing_address}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
          </div>
          </section>
          )}

          {/* Comparable Parcels */}
          {showTab('tab-investment') && compsData && compsData.comps && compsData.comps.length > 0 && (
            <section>
            <div className="detail-card">
              <h3 id="tab-investment">Comparable Parcels</h3>
              <p style={{ fontSize: '0.78rem', color: '#888', margin: '0 0 8px 0' }}>
                {compsData.summary.count} similar parcels within {compsData.summary.radius_km}km ({compsData.summary.acreage_range} acres)
              </p>
              {compsData.summary.median_price_per_acre && (
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: 12, padding: '8px 12px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0' }}>
                  <div>
                    <span style={{ fontSize: '0.72rem', color: '#6b7280', textTransform: 'uppercase' }}>Median $/ac</span>
                    <div style={{ fontWeight: 700, color: '#166534' }}>{fmt$(compsData.summary.median_price_per_acre)}</div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.72rem', color: '#6b7280', textTransform: 'uppercase' }}>Avg $/ac</span>
                    <div style={{ fontWeight: 700, color: '#166534' }}>{fmt$(compsData.summary.avg_price_per_acre)}</div>
                  </div>
                  {compsData.target.price_per_acre && (
                    <div>
                      <span style={{ fontSize: '0.72rem', color: '#6b7280', textTransform: 'uppercase' }}>This Parcel</span>
                      <div style={{ fontWeight: 700, color: compsData.target.price_per_acre <= compsData.summary.median_price_per_acre ? '#166534' : '#991b1b' }}>
                        {fmt$(compsData.target.price_per_acre)}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {compsData.comps.map((comp, i) => (
                  <Link
                    key={`${comp.parcel_id}|${comp.state || ''}|${comp.county || ''}|${i}`}
                    to={`/parcel/${encodeURIComponent(comp.parcel_id)}`}
                    style={{
                      display: 'block', padding: '8px 10px', borderRadius: 6,
                      border: '1px solid #eee', marginBottom: 6, textDecoration: 'none', color: 'inherit',
                      background: i === 0 ? '#f8f9fa' : '#fff',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#222' }}>
                          {comp.listing_price ? fmt$(comp.listing_price) : 'N/A'}
                        </span>
                        {comp.price_per_acre && (
                          <span style={{ fontSize: '0.78rem', color: '#888', marginLeft: 6 }}>
                            ({fmt$(comp.price_per_acre)}/ac)
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {comp.distance_mi} mi
                      </span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#555', marginTop: 2 }}>
                      {comp.listing_address || comp.parcel_id}
                      <span style={{ color: '#999', marginLeft: 6 }}>
                        {comp.area_acres?.toFixed(1)} ac {comp.county && `- ${comp.county}, ${comp.state}`}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            </section>
          )}
          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 24 }}>Reference</div>
          <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0 12px' }} aria-hidden="true" />
          {/* Neighbors + Same-owner portfolio (#703 / task #130).
              Lazy-loaded on tab open. Compact rows: distance, parcel,
              acres, listing status. Clicking a row navigates to that
              parcel's detail page. */}
          {showTab('tab-overview') && (
            <NeighborsCard
              parcelId={parcelId}
              state={parcel?.state}
              county={parcel?.county}
              navigate={navigate}
            />
          )}
          {showTab('tab-overview') && parcel?.owner && (
            <OwnerPortfolioCard
              parcelId={parcelId}
              owner={parcel.owner}
              state={parcel?.state}
              county={parcel?.county}
              navigate={navigate}
            />
          )}
          {/* "If you like that you might like this" (#769 / INIT-recommender).
              Collapsed by default — additive panel, lazy fetch on expand. */}
          {showTab('tab-overview') && (
            <SimilarParcelsCard parcelId={parcelId} navigate={navigate} />
          )}
        </div>

        {/* --- Right Column --- */}
        <div className="detail-col pd-col-right">

          {/* Commodity Probability — periodic-table-style element cards.
              Per #912 the block is height-budgeted to ~2x the strongest-
              signal card: 6-col compact grid, descriptive prose hidden
              behind InfoHint (#907/#911), distance labels tucked into
              the tile `title` so they don't add a third row of text. */}
          {/* #1288: a state no commodity model covers has no `scores` to draw,
              so the card above would vanish and read as "still loading".
              Say it is not scored, and that this is not a probability of 0. */}
          {showTab('tab-minerals')
            && !(mineralModelApplies(parcel) && mineralModelApplies(commodityScores)) && (
            <section data-testid="pd-mineral-not-scored">
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0 }}>
                <span>Mineral Probability (ML)</span>
              </h3>
              <div className="detail-card">
                <div style={{ fontWeight: 600, fontStyle: 'italic', color: '#6b7280' }}>{NOT_SCORED_LABEL}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4 }}>
                  No mineral model covers {parcel.state || 'this state'}, so there is no probability
                  to show. This is not a probability of 0.
                </div>
              </div>
            </section>
          )}
          {showTab('tab-minerals') && commodityScores && commodityScores.scores && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Mineral Probability (ML)</span>
                <InfoHint label="About mineral probability">
                  Probability from LightGBM models trained on USGS MRDS
                  deposit data + geological features. Higher = more similar
                  to known deposit sites. Distance below each tile = nearest
                  known training-positive deposit (a high probability with
                  no deposit within 200&nbsp;km is the model extrapolating).
                </InfoHint>
              </h3>
            <div className="detail-card">
              {/* No standalone "strongest signal" banner (#931): it ate
                  the vertical space the smaller meter just reclaimed. The
                  dominant commodity is already obvious from the grid, and
                  the per-tile `title` carries the calibration hint on
                  hover — so the summary lives there now. */}
              <div className="pd-mineral-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
                {COMMODITY_TILES.map(({ key, symbol, number, name, color, border }) => {
                  const prob = commodityScores.scores[key];
                  if (prob == null) return null;
                  // #1027: show the server percentile (0-99) to match the
                  // uniform 0-100 normalization epic; fall back to the raw
                  // `prob*100` when the percentile map is absent/empty.
                  const pctVal = commodityScores.percentiles?.[key];
                  const rawPct = pctVal != null ? Math.round(pctVal) : Math.round(prob * 100);
                  const isHigh = prob >= 0.80;
                  const isMed  = prob >= 0.40 && prob < 0.80;
                  const ringBg = `conic-gradient(${color} ${rawPct * 3.6}deg, #e8e8e8 ${rawPct * 3.6}deg)`;
                  // Distance to nearest known training-positive deposit
                  // (USGS MRDS) — folded into the tile's title attribute
                  // (#912) so it surfaces on hover without adding a
                  // dedicated text row and inflating tile height.
                  const km = commodityScores.nearest_known_deposit_km?.[key];
                  let calibText;
                  if (km == null) {
                    calibText = 'No known training-positive deposit within 200 km — treat the probability as extrapolation.';
                  } else {
                    const display = km < 1 ? `${(km * 1000).toFixed(0)} m`
                                   : km < 10 ? `${km.toFixed(1)} km`
                                   : `${km.toFixed(0)} km`;
                    calibText = `Nearest known ${name.toLowerCase()} deposit: ${display}.`;
                  }
                  return (
                    <div
                      key={key}
                      title={`${name} — ${rawPct}% probability. ${calibText}`}
                      style={{
                        position: 'relative',
                        background: isHigh ? `linear-gradient(135deg, ${color}18, ${color}08)` : '#fafafa',
                        border: `2px solid ${isHigh ? color : isMed ? `${color}80` : '#e0e0e0'}`,
                        borderRadius: 8,
                        padding: '6px 4px 4px',
                        textAlign: 'center',
                        cursor: 'default',
                        boxShadow: isHigh ? `0 1px 6px ${color}30` : 'none',
                      }}
                    >
                      {isHigh && (
                        <div style={{
                          position: 'absolute', top: -5, right: -5,
                          background: color, color: '#fff', fontSize: '0.5rem', fontWeight: 800,
                          padding: '1px 4px', borderRadius: 5, border: `1px solid ${border}`,
                          boxShadow: `0 1px 3px ${color}40`,
                        }}>HIGH</div>
                      )}
                      <div style={{
                        width: 32, height: 32, margin: '0 auto 2px',
                        borderRadius: '50%', background: ringBg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{
                          width: 26, height: 26, borderRadius: '50%',
                          background: isHigh ? `linear-gradient(135deg, ${color}30, #fff)` : '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 900, fontSize: '0.8rem', color: isHigh ? border : '#555',
                          fontFamily: "'Georgia', serif",
                        }}>
                          {symbol}
                        </div>
                      </div>
                      <div style={{
                        fontSize: '0.78rem', fontWeight: 800, lineHeight: 1,
                        color: isHigh ? border : isMed ? '#555' : '#999',
                      }}>
                        {rawPct}%
                      </div>
                      <div style={{ fontSize: '0.58rem', color: '#888', marginTop: 1, lineHeight: 1 }}>
                        {name}
                      </div>
                    </div>
                  );
                })}
              </div>
              {commodityScores.model_version && (
                <div style={{ fontSize: '0.62rem', color: '#bbb', marginTop: 6, textAlign: 'center' }}>
                  Model: {commodityScores.model_version}
                  {commodityScores.scored_at && ` | Scored: ${new Date(commodityScores.scored_at).toLocaleDateString()}`}
                </div>
              )}
            </div>
            </section>
          )}
          {/* Fair-value price-estimator mini-card (#779 / INIT-fair-value).
              Per-LISTING model estimate (q50) + 80% confidence interval
              (q10/q90) vs the actual listed price. Renders when the
              parcel has an active listing AND the model has scored it
              (fair_value_estimate is non-NULL).

              IMPORTANT CAVEAT (echoed in the tooltip below): the model
              is trained on LISTED prices, not sold prices — 100% of
              `listings.listing_status` is NULL in our DB so we have no
              transition-to-sold labels. Useful as an outlier detector
              ("listed N% above what comparable parcels are listed at")
              and for the "underpriced listings" search filter, NOT a
              market-clearing valuation. The training script docstring,
              the metadata.json, and this tooltip all repeat this
              caveat so anyone landing here without context sees it.

              Sits between Listing Quality (#775) and Sale Likelihood
              (#777) on the detail page because all three are
              per-LISTING attributes the buyer is scanning together. */}
          {parcel.fair_value_estimate != null && parcel.listing_price != null && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Fair Value (Model)</span>
                <InfoHint label="About Fair Value model">
                  LightGBM quantile-regression estimate vs comparable listings.
                  Trained on LISTED prices (not sold prices — we lack transition
                  labels in the DB), so this is an outlier detector, NOT a
                  market-clearing valuation. See scripts/train_fair_value_model.py
                  for the full algorithm + caveat.
                </InfoHint>
              </h3>
            <div className="detail-card" data-testid="pd-fair-value-card">
              {(() => {
                const listed = parcel.listing_price;
                const est = parcel.fair_value_estimate;
                const lo = parcel.fair_value_lower_80;
                const hi = parcel.fair_value_upper_80;
                const version = parcel.fair_value_model_version || 'unknown';

                // Deal comparison: green/under, amber/in-band, red/over.
                // "In band" = listed price falls inside the model's 80%
                // CI. Outside the band carries the directional sign.
                let dealLabel, dealColor, dealPct;
                if (lo != null && hi != null && listed >= lo && listed <= hi) {
                  dealLabel = 'In Band';
                  dealColor = { bg: '#fff8e1', fg: '#b78103' };
                  dealPct = null;
                } else if (listed < est) {
                  const pct = Math.round(((est - listed) / est) * 100);
                  dealLabel = `Under by ${pct}%`;
                  dealColor = { bg: '#e8f5e9', fg: '#2e7d32' };
                  dealPct = pct;
                } else {
                  const pct = Math.round(((listed - est) / est) * 100);
                  dealLabel = `Over by ${pct}%`;
                  dealColor = pct >= 50
                    ? { bg: '#ffcdd2', fg: '#b71c1c' }
                    : { bg: '#ffe0b2', fg: '#d35400' };
                  dealPct = pct;
                }

                const fmt = (n) => n == null
                  ? '—'
                  : `$${Math.round(n).toLocaleString()}`;

                return (
                  <>
                    {/* Headline: model estimate + comparison badge */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      padding: '14px 16px', borderRadius: 8, marginBottom: 12,
                      background: dealColor.bg,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '0.72rem', textTransform: 'uppercase',
                          letterSpacing: 0.5, color: dealColor.fg, fontWeight: 700,
                        }}>
                          Model fair value
                        </div>
                        <div style={{
                          fontSize: '1.6rem', fontWeight: 800, color: '#222',
                          marginTop: 2,
                        }} data-testid="pd-fair-value-est">
                          {fmt(est)}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 2 }}>
                          80% CI: {fmt(lo)} – {fmt(hi)}
                        </div>
                      </div>
                      <div style={{
                        padding: '10px 14px', borderRadius: 8,
                        background: '#fff',
                        border: `2px solid ${dealColor.fg}`,
                        textAlign: 'center', minWidth: 110,
                      }} data-testid="pd-fair-value-badge">
                        <div style={{
                          fontSize: '0.7rem', textTransform: 'uppercase',
                          letterSpacing: 0.5, color: dealColor.fg, fontWeight: 700,
                        }}>
                          Listed at
                        </div>
                        <div style={{
                          fontSize: '1.1rem', fontWeight: 800, color: '#222',
                          marginTop: 2,
                        }}>
                          {fmt(listed)}
                        </div>
                        <div style={{
                          fontSize: '0.78rem', fontWeight: 700, color: dealColor.fg,
                          marginTop: 4,
                        }}>
                          {dealLabel}
                        </div>
                      </div>
                    </div>
                    {/* Model-version tooltip — surfaced inline so the
                        reader can correlate a stale estimate with the
                        artifact version that produced it. */}
                    <div style={{ fontSize: '0.7rem', color: '#999' }}
                         title={`Model artifact version. Generated by score_fair_value.py from the boosters in /app/landai/models/fair_value/${version}/.`}>
                      Model version: <code>{version}</code>
                    </div>
                  </>
                );
              })()}
            </div>
            </section>
          )}
          {/* Sale-probability mini-card (#777 / INIT-sale-probability).
              Per-LISTING probabilities for 30/60/90-day sale. Renders
              when the parcel has an active scored listing — three
              percentage chips ordered shortest-horizon first. Lives
              next to Listing Quality because both are per-listing
              attributes; the buyer scans them as a pair when deciding
              whether to act now or wait. NULL on any single horizon
              renders as "—" so the card still appears for partial
              backfills. */}
          {(parcel.sale_probability_30d != null
            || parcel.sale_probability_60d != null
            || parcel.sale_probability_90d != null) && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Sale Likelihood</span>
                <InfoHint label="About Sale Likelihood">
                  Per-listing probability the listing transitions from active
                  to sold within the horizon. Heuristic over days-on-market,
                  listing quality, price-vs-comparable, and aesthetics. See
                  scripts/compute_sale_probability.py for the formula.
                </InfoHint>
              </h3>
            <div className="detail-card" data-testid="pd-sale-probability-card">
              <h3 style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>Sale Likelihood</h3>
              {(() => {
                // Traffic-light bands match Listing Quality so the two
                // mini-cards read as the same intensity scale. Bands are
                // intentionally narrow at the top — anything above 50% on
                // a 60d horizon is already "likely".
                const probColor = (p) => {
                  if (p == null) return { bg: '#f0f0f0', fg: '#999' };
                  if (p >= 0.50)  return { bg: '#e8f5e9', fg: '#2e7d32' };
                  if (p >= 0.35)  return { bg: '#e8f5e9', fg: '#43a047' };
                  if (p >= 0.20)  return { bg: '#fff8e1', fg: '#b78103' };
                  if (p >= 0.10)  return { bg: '#ffe0b2', fg: '#d35400' };
                  return            { bg: '#ffcdd2', fg: '#b71c1c' };
                };
                const HORIZONS = [
                  ['30d', parcel.sale_probability_30d],
                  ['60d', parcel.sale_probability_60d],
                  ['90d', parcel.sale_probability_90d],
                ];
                return (
                  <div style={{
                    display: 'flex', gap: 12, flexWrap: 'wrap',
                  }}>
                    {HORIZONS.map(([label, p]) => {
                      const c = probColor(p);
                      const pct = p != null ? Math.round(p * 100) : null;
                      return (
                        <div key={label} data-testid={`pd-sale-prob-${label}`} style={{
                          flex: '1 1 80px',
                          padding: '12px 14px', borderRadius: 8,
                          background: c.bg,
                          border: `1px solid ${c.fg}33`,
                          textAlign: 'center',
                        }}>
                          <div style={{
                            fontSize: '0.7rem', textTransform: 'uppercase',
                            letterSpacing: 0.5, color: c.fg, fontWeight: 700,
                          }}>{label}</div>
                          <div style={{
                            fontSize: '1.4rem', fontWeight: 800, color: c.fg,
                            marginTop: 2,
                          }}>{pct == null ? '—' : `${pct}%`}</div>
                          <div style={{ fontSize: '0.7rem', color: '#666' }}>
                            sale chance
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            </section>
          )}
          {/* Expected-yield mini-card (#785 / INIT-yield-calc).
              Per-LISTING headline 5-year annualised yield + low/high
              band + per-scenario chips. Renders only when the parcel
              has an active scored listing (expected_5y_yield_pct is
              non-NULL). Sits next to the Fair Value card because both
              are per-listing economic signals the buyer scans together;
              fair-value answers "is this priced right?", yield answers
              "what will I make holding it 5 years?".

              The headline is rule-based (NOT ML) — every chip below it
              corresponds to a documented formula in
              scripts/compute_investment_yield.py and the migration
              0114 audit-JSON shape. Reader can argue with any
              assumption by inspecting the chip and the docstring it
              points to. */}
          {parcel.expected_5y_yield_pct != null && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Expected 5y Yield</span>
                <InfoHint label="About Expected 5y Yield">
                  Rule-based annualised total return over a 5-year hold.
                  Combines state appreciation, fair-value gap closure,
                  climate-risk discount, and any active revenue scenarios
                  (solar/wind/timber/ag/hunting). See
                  scripts/compute_investment_yield.py for the formula.
                </InfoHint>
              </h3>
            <div className="detail-card" data-testid="pd-expected-yield-card">
              {(() => {
                const headline = parcel.expected_5y_yield_pct;
                const low = parcel.expected_5y_yield_low_pct;
                const high = parcel.expected_5y_yield_high_pct;
                const payload = parcel.yield_scenarios_json || {};
                const scenarios = Array.isArray(payload.scenarios) ? payload.scenarios : [];
                const appreciation = payload.appreciation || {};
                const fairValueGap = payload.fair_value_gap || {};
                const climateDiscount = payload.climate_discount || {};

                // Traffic-light colour band aligned with sale-likelihood
                // chip semantics so a buyer scans the two mini-cards
                // with a single colour grammar.
                const yieldColor = (y) => {
                  if (y == null) return { bg: '#f0f0f0', fg: '#999' };
                  if (y >= 12)  return { bg: '#e8f5e9', fg: '#2e7d32' };
                  if (y >= 8)   return { bg: '#e8f5e9', fg: '#43a047' };
                  if (y >= 5)   return { bg: '#fff8e1', fg: '#b78103' };
                  if (y >= 2)   return { bg: '#ffe0b2', fg: '#d35400' };
                  return          { bg: '#ffcdd2', fg: '#b71c1c' };
                };
                const c = yieldColor(headline);
                const fmtPct = (n) => n == null ? '—' : `${n.toFixed(1)}%`;

                return (
                  <>
                    {/* Headline: annualised yield + low-high range badge */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      padding: '14px 16px', borderRadius: 8, marginBottom: 12,
                      background: c.bg,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '0.72rem', textTransform: 'uppercase',
                          letterSpacing: 0.5, color: c.fg, fontWeight: 700,
                        }}>
                          Expected 5y yield (annualised)
                        </div>
                        <div style={{
                          fontSize: '1.6rem', fontWeight: 800, color: '#222',
                          marginTop: 2,
                        }} data-testid="pd-expected-yield-headline">
                          {fmtPct(headline)}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 2 }}>
                          Range: {fmtPct(low)} – {fmtPct(high)}
                        </div>
                      </div>
                    </div>
                    {/* Component chips: appreciation, FV gap, climate
                        discount. Each shows its per-year contribution
                        and the source the formula docstring points to. */}
                    <div style={{
                      display: 'flex', gap: 8, flexWrap: 'wrap',
                      marginBottom: scenarios.length ? 12 : 0,
                    }}>
                      {appreciation.pct_per_yr != null && (
                        <div data-testid="pd-yield-chip-appreciation" style={{
                          padding: '6px 10px', borderRadius: 14,
                          background: '#e3f2fd', fontSize: '0.78rem',
                          color: '#1565c0', fontWeight: 600,
                        }} title={`State base appreciation rate for ${appreciation.state || '—'}. Source: scripts/compute_investment_yield.py STATE_BASE_RATE_CAGR table.`}>
                          Appreciation +{appreciation.pct_per_yr.toFixed(1)}%/yr
                        </div>
                      )}
                      {fairValueGap.pct_per_yr != null && fairValueGap.pct_per_yr !== 0 && (
                        <div data-testid="pd-yield-chip-fair-value-gap" style={{
                          padding: '6px 10px', borderRadius: 14,
                          background: '#e8f5e9', fontSize: '0.78rem',
                          color: '#2e7d32', fontWeight: 600,
                        }} title="Fair-value gap closure (#779). 50% of underpricing closes by year 5.">
                          Under-priced +{fairValueGap.pct_per_yr.toFixed(1)}%/yr
                        </div>
                      )}
                      {climateDiscount.pct_per_yr != null && climateDiscount.pct_per_yr < 0 && (
                        <div data-testid="pd-yield-chip-climate" style={{
                          padding: '6px 10px', borderRadius: 14,
                          background: '#ffe0b2', fontSize: '0.78rem',
                          color: '#d35400', fontWeight: 600,
                        }} title={`Climate-risk discount (#773). Hazard score: ${climateDiscount.climate_risk_total_0_100}.`}>
                          Climate drag {climateDiscount.pct_per_yr.toFixed(1)}%/yr
                        </div>
                      )}
                    </div>
                    {/* Revenue-scenario chips — one per fired scenario.
                        Each chip surfaces the scenario name + annualised
                        contribution + a tooltip with the trigger details. */}
                    {scenarios.length > 0 && (
                      <div>
                        <div style={{
                          fontSize: '0.72rem', textTransform: 'uppercase',
                          letterSpacing: 0.5, color: '#666', fontWeight: 700,
                          marginBottom: 6,
                        }}>
                          Revenue scenarios fired
                        </div>
                        <div style={{
                          display: 'flex', gap: 8, flexWrap: 'wrap',
                        }} data-testid="pd-yield-scenarios">
                          {scenarios.map((s, idx) => (
                            <div key={`${s.name}-${idx}`} data-testid={`pd-yield-scenario-${s.name}`} style={{
                              padding: '6px 10px', borderRadius: 14,
                              background: '#f3e5f5', fontSize: '0.78rem',
                              color: '#6a1b9a', fontWeight: 600,
                            }} title={`Trigger: ${s.trigger}. Source: ${s.source || 'see formula docstring'}.`}>
                              {s.name} +{s.pct_per_yr?.toFixed(1) ?? '?'}%/yr
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            </section>
          )}
          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 24 }}>Research</div>
          <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0 12px' }} aria-hidden="true" />

          {/* Mineral Rights / Federal Land Status. Prose collapsed
              behind InfoHint per #907/#911 — keep the badge that
              communicates "Federal / Not Federal" at a glance. */}
          {showTab('tab-minerals') && <section>
            <div className="detail-card">
            <h3 id="tab-minerals" style={{ display: 'flex', alignItems: 'center' }}>
              <span>Mineral Rights</span>
              {parcel.federal_land_agency ? (
                <InfoHint label="About federal-land mineral rights">
                  On federal surface land mineral rights are typically retained
                  by the federal government (split estate). Mining activity
                  requires permits and compliance with federal regulations
                  (General Mining Law of 1872, FLPMA, NEPA). Unpatented mining
                  claims may be staked on BLM-managed land for locatable
                  minerals. Source: BLM Surface Management Agency (SMA) dataset
                  — informational only; always verify rights via title search.
                </InfoHint>
              ) : (
                <InfoHint label="About mineral rights coverage">
                  This parcel does not overlap any federal Surface Management
                  Agency boundary, so surface and mineral rights are more
                  likely to be privately held — title search recommended.
                  Coverage: 8 western states (CA, CO, ID, MT, NM, NV, OR, UT,
                  WY). AZ data pending.
                </InfoHint>
              )}
            </h3>
            {parcel.federal_land_agency ? (() => {
              const agencyCode = parcel.federal_land_agency;
              const agencyName = AGENCY_LABELS[agencyCode] || agencyCode;
              const agencyColor = AGENCY_COLORS[agencyCode] || '#888';
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', borderRadius: 8,
                  background: `linear-gradient(135deg, ${agencyColor}18, ${agencyColor}08)`,
                  border: `2px solid ${agencyColor}60`,
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 8,
                    background: agencyColor, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '0.75rem', flexShrink: 0,
                  }}>
                    {agencyCode}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222' }}>
                      Federal Land &mdash; {agencyName}
                    </div>
                    {parcel.federal_land_unit && (
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 2 }}>
                        {parcel.federal_land_unit}
                      </div>
                    )}
                  </div>
                </div>
              );
            })() : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 8,
                background: '#f0faf0', border: '1px solid #c3e6c3',
              }}>
                <span style={{ fontSize: '1.1rem' }}>&#9745;</span>
                <span style={{ fontWeight: 600, color: '#2d7a2d' }}>Not on federal land</span>
              </div>
            )}
          </div>
          </section>}
          {/* BLM Mining Claims */}
          {showTab('tab-minerals') && miningClaims && miningClaims.count > 0 && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>BLM Mining Claims</span>
                <InfoHint label="About BLM Mining Claims source">
                  Source: BLM Mining Claims (MLRS). Active claims only. This
                  does not include state-level or county-recorded mineral rights.
                </InfoHint>
              </h3>
            <div className="detail-card">
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 8,
                background: 'linear-gradient(135deg, #E74C3C18, #E74C3C08)',
                border: '1px solid #E74C3C40',
                marginBottom: 12,
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 32, height: 32, borderRadius: 6,
                  background: '#E74C3C', color: '#fff', fontWeight: 800, fontSize: '0.85rem',
                }}>{miningClaims.count}</span>
                <span style={{ fontWeight: 600, color: '#c0392b', fontSize: '0.9rem' }}>
                  Active mining claim{miningClaims.count !== 1 ? 's' : ''} overlap this parcel
                </span>
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {miningClaims.claims.map((c, i) => {
                  const typeColors = {
                    'LODE CLAIM': '#E74C3C', 'PLACER CLAIM': '#F39C12',
                    'MILL SITE': '#9B59B6', 'TUNNEL SITE': '#3498DB',
                  };
                  const color = typeColors[c.claim_type] || '#888';
                  return (
                    <div key={c.id || i} style={{
                      padding: '8px 10px', borderRadius: 6,
                      border: `1px solid ${color}30`,
                      background: `${color}08`,
                      marginBottom: 6,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#222' }}>
                          {c.claim_name || 'Unnamed Claim'}
                        </span>
                        <span style={{
                          fontSize: '0.7rem', padding: '2px 6px', borderRadius: 4,
                          background: color, color: '#fff', fontWeight: 600,
                        }}>{c.claim_type || 'Unknown'}</span>
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span>Serial: {c.serial_nr || 'N/A'}</span>
                        {/* #1586: never case_acres, a legal case size. BLM polygons are coarse and shared, hence "Mapped". */}
                        {c.geometry_acres != null && (
                          <span>Mapped area: {Number(c.geometry_acres).toFixed(1)} ac</span>
                        )}
                        {c.disposition && <span>{c.disposition}</span>}
                      </div>
                      {(c.is_patented || c.is_excluded || c.is_conveyed) && (
                        <div style={{ fontSize: '0.75rem', marginTop: 4, display: 'flex', gap: 8 }}>
                          {c.is_patented && <span style={{ color: '#E74C3C', fontWeight: 600 }}>Patented</span>}
                          {c.is_excluded && <span style={{ color: '#E67E22', fontWeight: 600 }}>Excluded</span>}
                          {c.is_conveyed && <span style={{ color: '#27AE60', fontWeight: 600 }}>Conveyed</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </section>
          )}
          {/* Federal Mineral-Rights Records (#133). Authoritative
              evidence that the mineral estate was kept separate when
              surface land was conveyed. Much stronger signal than
              the listing-text extractor — these are federal records
              of the conveyance itself. Renders only when at least
              one record overlaps the parcel. */}
          {showTab('tab-minerals') && parcel.mineral_rights_federal_records
            && parcel.mineral_rights_federal_records.count > 0 && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Federal Mineral-Rights Records</span>
                <InfoHint label="About Federal Mineral-Rights Records">
                  <strong>Diligence:</strong> the surface conveyance may not
                  include the subsurface estate. Request the federal record
                  before making an offer if mineral value matters for your
                  use case. Source: BLM and USFS federal mineral-rights
                  records. The date shown is when the source last confirmed
                  them, not when the estate was severed.
                </InfoHint>
              </h3>
            <div className="detail-card">
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 8,
                background: 'linear-gradient(135deg, #C0392B18, #C0392B08)',
                border: '1px solid #C0392B40',
                marginBottom: 12,
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 32, height: 32, borderRadius: 6,
                  background: '#C0392B', color: '#fff', fontWeight: 800, fontSize: '0.85rem',
                }}>{parcel.mineral_rights_federal_records.count}</span>
                <span style={{ fontWeight: 600, color: '#922b21', fontSize: '0.9rem' }}>
                  Federal record{parcel.mineral_rights_federal_records.count !== 1 ? 's' : ''} of severed mineral rights on this parcel
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                {parcel.mineral_rights_federal_records.has_outstanding_right && (
                  <span style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem',
                    background: '#fde8e8', color: '#c0392b', fontWeight: 600,
                  }}>Outstanding right (severed)</span>
                )}
                {parcel.mineral_rights_federal_records.has_reservation && (
                  <span style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem',
                    background: '#fff3e0', color: '#e67e22', fontWeight: 600,
                  }}>Reservation (severed)</span>
                )}
              </div>
              {parcel.mineral_rights_federal_records.sample_case_name && (
                <div style={{
                  fontSize: '0.82rem', color: '#444',
                  padding: '8px 10px', borderRadius: 6,
                  background: '#fafafa', border: '1px solid #eee',
                }}>
                  Example case on record:{' '}
                  <strong style={{ color: '#222' }}>
                    {parcel.mineral_rights_federal_records.sample_case_name}
                  </strong>
                </div>
              )}
              <FederalRecordEvidenceAge records={parcel.mineral_rights_federal_records} />
            </div>
            </section>
          )}
          {/* FEMA National Risk Index */}
          {/* PR-6 (#704): `pd-right-sticky` class hook — at ≥1025px
              desktop the Hazards card is sticky in place. Phone +
              tablet behavior unchanged. */}
          {showTab('tab-hazards') && parcel.fema_risk && (
            <section>
            <div className="detail-card pd-right-sticky" data-testid="pd-hazards-card">
              <h3 id="tab-hazards" style={{ display: 'flex', alignItems: 'center' }}>
                <span>Hazard Risk (FEMA NRI)</span>
                <InfoHint label="About FEMA NRI hazard scores">
                  Source: FEMA National Risk Index, census tract {parcel.fema_risk.census_tract}.
                  Scores 0-100; higher means greater risk based on historical events,
                  exposure, and social vulnerability.
                </InfoHint>
              </h3>
              {(() => {
                const RiskBar = ({ label, value, hue }) => {
                  if (value == null) return null;
                  const v = Math.max(0, Math.min(100, value));
                  return (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                                    fontSize: '0.85rem', marginBottom: 2 }}>
                        <span>{label}</span>
                        <strong>{v.toFixed(0)}</strong>
                      </div>
                      <div style={{ background: '#eee', borderRadius: 3, height: 8 }}>
                        <div style={{
                          width: `${v}%`,
                          height: '100%',
                          background: `hsl(${hue}, 70%, ${100 - v * 0.3}%)`,
                          borderRadius: 3,
                        }} />
                      </div>
                    </div>
                  );
                };
                const fr = parcel.fema_risk;
                // Hazard groups (#131 / #704). Collapsed by default — the
                // suite went from 5 to 18 bars and was eating too much
                // vertical space. Each group's chip shows the worst bar
                // in the group as a colored severity tag; click to expand.
                const HAZARD_GROUPS = [
                  { id: 'fire_heat', label: 'Fire & Heat', hue: 20, bars: [
                      { label: 'Wildfire',  v: fr.wildfire_score,  hue: 15 },
                      { label: 'Heat Wave', v: fr.heat_wave_score, hue: 20 },
                      { label: 'Drought',   v: fr.drought_score,   hue: 35 },
                  ]},
                  { id: 'water', label: 'Water', hue: 210, bars: [
                      { label: 'Flood (any)',   v: fr.flood_score,         hue: 210 },
                      { label: 'Coastal Flood', v: fr.coastal_flood_score, hue: 195 },
                      { label: 'Hurricane',     v: fr.hurricane_score,     hue: 180 },
                      { label: 'Tsunami',       v: fr.tsunami_score,       hue: 200 },
                  ]},
                  { id: 'cold', label: 'Cold & Winter', hue: 195, bars: [
                      { label: 'Cold Wave',      v: fr.cold_wave_score,      hue: 190 },
                      { label: 'Winter Weather', v: fr.winter_weather_score, hue: 205 },
                      { label: 'Ice Storm',      v: fr.ice_storm_score,      hue: 185 },
                  ]},
                  { id: 'earth', label: 'Earth', hue: 30, bars: [
                      { label: 'Earthquake', v: fr.earthquake_score, hue: 45 },
                      { label: 'Landslide',  v: fr.landslide_score,  hue: 30 },
                      { label: 'Volcanic',   v: fr.volcanic_score,   hue: 10 },
                  ]},
                  { id: 'severe', label: 'Severe Weather', hue: 270, bars: [
                      { label: 'Tornado',     v: fr.tornado_score,     hue: 280 },
                      { label: 'Strong Wind', v: fr.strong_wind_score, hue: 250 },
                      { label: 'Hail',        v: fr.hail_score,        hue: 220 },
                      { label: 'Lightning',   v: fr.lightning_score,   hue: 50  },
                  ]},
                ];
                // Severity-tag styling: empty if no data, then green/
                // yellow/orange/red bands. Tight color contrast so a
                // glance tells you the group's worst hazard.
                const severityTag = (max) => {
                  if (max == null) return { text: '—', bg: '#f0f0f0', fg: '#999' };
                  if (max < 25)    return { text: max.toFixed(0), bg: '#e8f5e9', fg: '#2e7d32' };
                  if (max < 50)    return { text: max.toFixed(0), bg: '#fff8e1', fg: '#f57f17' };
                  if (max < 75)    return { text: max.toFixed(0), bg: '#ffe0b2', fg: '#e65100' };
                  return                  { text: max.toFixed(0), bg: '#ffcdd2', fg: '#b71c1c' };
                };
                const groupMax = (group) => {
                  const vals = group.bars.map(b => b.v).filter(v => v != null);
                  return vals.length ? Math.max(...vals) : null;
                };
                const toggleGroup = (id) => setExpandedHazardGroups(
                  prev => ({ ...prev, [id]: !prev[id] })
                );
                return (
                  <>
                    {/* Overall composite — always shown prominently above the groups */}
                    {fr.overall_risk_score != null && (
                      <div style={{ marginBottom: 10 }}>
                        <RiskBar label="Overall" value={fr.overall_risk_score} hue={0} />
                      </div>
                    )}
                    {HAZARD_GROUPS.map(group => {
                      const max = groupMax(group);
                      const tag = severityTag(max);
                      const expanded = !!expandedHazardGroups[group.id];
                      const populatedBars = group.bars.filter(b => b.v != null);
                      return (
                        <div key={group.id} style={{ marginBottom: 6 }}>
                          <div
                            onClick={() => toggleGroup(group.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 8px', borderRadius: 4,
                              background: '#fafafa', cursor: 'pointer',
                              fontSize: '0.88rem', userSelect: 'none',
                            }}
                          >
                            <span style={{ width: 14, color: '#888' }}>{expanded ? '▾' : '▸'}</span>
                            <span style={{ flex: 1, fontWeight: 500 }}>{group.label}</span>
                            <span style={{
                              fontSize: '0.72rem', color: '#888',
                            }}>worst:</span>
                            <span style={{
                              minWidth: 28, padding: '2px 8px', borderRadius: 10,
                              background: tag.bg, color: tag.fg,
                              fontSize: '0.78rem', fontWeight: 700,
                              textAlign: 'center',
                            }}>{tag.text}</span>
                          </div>
                          {expanded && populatedBars.length > 0 && (
                            <div style={{ paddingLeft: 22, paddingTop: 4, paddingBottom: 4 }}>
                              {populatedBars.map(b => (
                                <RiskBar key={b.label} label={b.label} value={b.v} hue={b.hue} />
                              ))}
                            </div>
                          )}
                          {expanded && populatedBars.length === 0 && (
                            <p style={{ paddingLeft: 22, fontSize: '0.78rem', color: '#999', margin: '4px 0' }}>
                              No data yet for this group (backfill pending).
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
            </section>
          )}
          {/* Climate Risk consolidator (#773 / INIT-climate-risk).
              Reads the consolidated 0-100 headline + per-family
              subscore dict computed by scripts/compute_climate_risk.py
              and persisted on feature_vectors (migration 0108).
              Additive — sits beneath the existing FEMA NRI Hazards
              card and shares the same `tab-hazards` tab. Renders only
              when the API returned a populated headline (NULL when
              the parcel has no NRI tract match yet). */}
          {showTab('tab-hazards') && parcel.climate_risk_total_0_100 != null && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Climate Risk</span>
                <InfoHint label="About Climate Risk">
                  Consolidated 0-100 score (higher = riskier) from the FEMA NRI
                  hazard suite, weighted by how hard each hazard is to mitigate.
                  Green ≤30 · Yellow 30-60 · Orange 60-80 · Red &gt;80.
                </InfoHint>
              </h3>
            <div className="detail-card" data-testid="pd-climate-risk-card">
              <h3 style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>Climate Risk</h3>
              {(() => {
                // Traffic-light color from headline score. Matches the
                // bands documented in the issue + scoring script.
                const headlineColor = (v) => {
                  if (v == null) return { bg: '#f0f0f0', fg: '#999', band: '—' };
                  if (v <= 30)   return { bg: '#e8f5e9', fg: '#2e7d32', band: 'Low' };
                  if (v <= 60)   return { bg: '#fff8e1', fg: '#f57f17', band: 'Moderate' };
                  if (v <= 80)   return { bg: '#ffe0b2', fg: '#e65100', band: 'High' };
                  return            { bg: '#ffcdd2', fg: '#b71c1c', band: 'Very High' };
                };
                const v = parcel.climate_risk_total_0_100;
                const c = headlineColor(v);
                // Subscores: human-readable family labels in display order.
                // Order matches the migration column order — flood/wildfire
                // first since they carry the most weight in the composite.
                const FAMILY_LABELS = [
                  ['flood',      'Flood',       'Riverine + coastal flood (worse-of)'],
                  ['wildfire',   'Wildfire',    'FEMA NRI wildfire RISKS'],
                  ['hurricane',  'Hurricane',   'Hurricane + strong-wind (worse-of)'],
                  ['earthquake', 'Earthquake',  'FEMA NRI earthquake RISKS'],
                  ['heat',       'Heat Wave',   'FEMA NRI heat-wave RISKS'],
                  ['drought',    'Drought',     'FEMA NRI drought RISKS'],
                  ['snow',       'Winter',      'Cold-wave / winter-weather / ice-storm (worst-of)'],
                  ['hail',       'Hail',        'FEMA NRI hail RISKS'],
                ];
                const subs = parcel.climate_risk_subscores || {};
                // Show all 8 rows; degrade to "—" for null subscores so the
                // user sees the full breakdown intent rather than guessing
                // why a family is missing.
                return (
                  <>
                    {/* Headline donut + band label */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      padding: '14px 16px', borderRadius: 8, marginBottom: 12,
                      background: c.bg,
                    }}>
                      <div style={{
                        width: 64, height: 64, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#fff',
                        border: `4px solid ${c.fg}`,
                        fontSize: '1.5rem', fontWeight: 800, color: c.fg,
                      }}>{v.toFixed(0)}</div>
                      <div>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5, color: c.fg, fontWeight: 700 }}>
                          Climate Risk
                        </div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#222' }}>{c.band}</div>
                        <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 2 }}>
                          Composite score (0-100, higher = riskier)
                        </div>
                      </div>
                    </div>
                    {/* Subscore breakdown — collapsible */}
                    <div style={{ marginTop: 4 }}>
                      <div style={{ fontSize: '0.85rem', color: '#1565c0', fontWeight: 600, padding: '4px 0' }}>Sub-component breakdown</div>
                      <div style={{ paddingTop: 8 }}>
                        {FAMILY_LABELS.map(([key, label, sub]) => {
                          const raw = subs[key];
                          // #225 — raw FEMA NRI subscores cluster 80-100 for
                          // most parcels in our footprint, so the row chip
                          // colors carry no information. Rescale via the
                          // population percentile lookup so the chip reflects
                          // "how this parcel compares to all 5.5M scored."
                          // Raw is preserved in the title tooltip for audit.
                          const display = rescaleByPercentile(raw, key);
                          const cc = headlineColor(display);
                          const tip = raw == null
                            ? null
                            : `Raw FEMA score: ${raw.toFixed(1)} → percentile ${display.toFixed(0)} (vs. 5.5M parcels)`;
                          return (
                            <div key={key} style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '4px 0', fontSize: '0.85rem',
                            }}>
                              <span style={{ flex: 1 }}>
                                <span style={{ fontWeight: 500 }}>{label}</span>
                                <span style={{ display: 'block', fontSize: '0.7rem', color: '#999' }}>
                                  {sub}
                                </span>
                              </span>
                              <span title={tip || undefined} style={{
                                minWidth: 38, padding: '2px 8px', borderRadius: 10,
                                background: cc.bg, color: cc.fg,
                                fontSize: '0.78rem', fontWeight: 700,
                                textAlign: 'center',
                                cursor: tip ? 'help' : 'default',
                              }}>
                                {display == null ? '—' : display.toFixed(0)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            </section>
          )}

          {/* Ground Stability (OPP-3 / REQ-003 / REQ-004) — InSAR
              surface-displacement card. Renders from the API's
              geo_signals.ground_stability block; absent when the
              backend did not return geo_signals (pre-indexed parcels).
              The card handles its own no-coverage and
              insufficient-data states internally. */}
          {showTab('tab-hazards') && parcel.geo_signals?.ground_stability && (
            <section>
              <GroundStabilityCard
                groundStability={parcel.geo_signals.ground_stability}
              />
            </section>
          )}

          {/* Buildability / Access — rule-based extraction from the
              listing description (issue #106). The card renders only
              when the per-parcel buildable-retreat lookup returned a
              row; absence usually just means no listing description
              was scraped for the extractor to run over. */}
          {showTab('tab-land') && buildability && buildability.results && buildability.results.length > 0 && (() => {
            const b = buildability.results[0];
            const phrases = b.extracted_phrases || {};
            const phraseCategories = Object.keys(phrases).filter(
              (k) => Array.isArray(phrases[k]) && phrases[k].length > 0,
            );
            return (
              <section>
                <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                  <span>Buildability / Access</span>
                  <InfoHint label="About Buildability / Access">
                    Rule-based extraction from the listing description. Signals are
                    best-effort and should be verified.
                    {b.extractor_version ? ` Extractor ${b.extractor_version}.` : ''}
                  </InfoHint>
                </h3>
              <div className="detail-card">
                {b.retreat_score != null && (
                  <div style={{
                    padding: '12px 16px', borderRadius: 8, marginBottom: 12,
                    background: 'linear-gradient(135deg, #2e7d3218, #2e7d3208)',
                    border: '1px solid #2e7d3240',
                  }}>
                    <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#2e7d32', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                      Retreat Score
                    </div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1b5e20' }}>
                      {b.retreat_score}<span style={{ fontSize: '0.9rem', color: '#888' }}> / 100</span>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
                  <BuildabilityChip label="Access" value={b.access_confidence} />
                  <BuildabilityChip label="Water" value={b.water_confidence} />
                  <BuildabilityChip label="Power" value={b.power_confidence} />
                  <BuildabilityChip label="Build Site" value={b.build_site_confidence} />
                </div>
                {phraseCategories.length > 0 ? (
                  <div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: 6 }}>
                      Evidence
                    </div>
                    {phraseCategories.map((cat) => (
                      <div key={cat} style={{ fontSize: '0.8rem', marginBottom: 6 }}>
                        <strong style={{ textTransform: 'capitalize' }}>
                          {cat.replace(/_/g, ' ')}:
                        </strong>{' '}
                        {phrases[cat].map((snippet, i) => (
                          <span
                            key={i}
                            style={{
                              display: 'inline-block',
                              background: '#f5f5f5',
                              border: '1px solid #e5e7eb',
                              borderRadius: 4,
                              padding: '1px 6px',
                              margin: '2px 4px 2px 0',
                              color: '#374151',
                            }}
                          >
                            “{trimExcerptToWords(snippet)}”
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: '0.78rem', color: '#888', margin: 0 }}>
                    No evidence snippets recorded by the extractor.
                  </p>
                )}
              </div>
              </section>
            );
          })()}
          {/* Elevation & Terrain */}
          {showTab('tab-land') && parcel.elevation_mean != null && (
            <section>
              <h3 id="tab-land" style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Elevation &amp; Terrain</span>
                <InfoHint label="About elevation source">
                  Source: USGS 3DEP 1-arc-second DEM (~30 m resolution).
                  3D view: WebGL mesh of the parcel polygon extruded to
                  local elevations. Hillshade view: server-rendered
                  preview with parcel boundary outlined.
                </InfoHint>
              </h3>
            <div className="detail-card">
              {(() => {
                const M_TO_FT = 3.28084;
                const minFt = Math.round(parcel.elevation_min * M_TO_FT);
                const maxFt = Math.round(parcel.elevation_max * M_TO_FT);
                const meanFt = Math.round(parcel.elevation_mean * M_TO_FT);
                const rangeFt = Math.round((parcel.elevation_range || 0) * M_TO_FT);
                const minM = Math.round(parcel.elevation_min);
                const maxM = Math.round(parcel.elevation_max);
                const meanM = Math.round(parcel.elevation_mean);
                const hasRange = rangeFt > 10;
                return (
                  <>
                    <div style={{
                      padding: '14px 16px', borderRadius: 8, marginBottom: 10,
                      background: 'linear-gradient(135deg, #8B691418, #8B691408)',
                      border: '1px solid #8B691440',
                    }}>
                      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8B6914', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                        {hasRange ? 'Elevation Range' : 'Elevation'}
                      </div>
                      {hasRange ? (
                        <>
                          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#222' }}>
                            {fmtNum(minFt, 0)} &ndash; {fmtNum(maxFt, 0)} ft
                          </div>
                          <div style={{ fontSize: '0.82rem', color: '#666', marginTop: 4 }}>
                            {fmtNum(minM, 0)} &ndash; {fmtNum(maxM, 0)} m &middot; {fmtNum(rangeFt, 0)} ft relief
                          </div>
                          <div style={{ marginTop: 10, position: 'relative', height: 28 }}>
                            <div style={{
                              height: 6, background: 'linear-gradient(90deg, #228B22, #8B6914, #CD853F)',
                              borderRadius: 3, position: 'absolute', top: 11, left: 0, right: 0,
                            }} />
                            {(() => {
                              const pct = parcel.elevation_range > 0
                                ? ((parcel.elevation_mean - parcel.elevation_min) / parcel.elevation_range) * 100
                                : 50;
                              return (
                                <div style={{
                                  position: 'absolute', top: 3, left: `calc(${pct}% - 6px)`,
                                  width: 12, height: 22, display: 'flex', flexDirection: 'column', alignItems: 'center',
                                }}>
                                  <div style={{
                                    width: 0, height: 0,
                                    borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                                    borderTop: '6px solid #8B6914',
                                  }} />
                                  <div style={{ fontSize: '0.65rem', color: '#8B6914', fontWeight: 700, marginTop: 6, whiteSpace: 'nowrap' }}>
                                    avg {fmtNum(meanFt, 0)}'
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#222' }}>
                            {fmtNum(meanFt, 0)} ft
                          </div>
                          <div style={{ fontSize: '0.82rem', color: '#666', marginTop: 4 }}>
                            {fmtNum(meanM, 0)} m
                          </div>
                        </>
                      )}
                    </div>
                    {/* 3D WebGL terrain (default) with hillshade fallback.
                        - User toggles between modes via the 3D / Hillshade
                          pill below.
                        - Hillshade is also the implicit fallback when the
                          parcel has no elevation grid loaded (or is too
                          flat to make WebGL interesting). It used to live
                          in its own oversized Terrain card on the left —
                          folded in here at 3D-canvas size to free that
                          column. ParcelCakeView (SVG prism) is the
                          last-resort fallback if the hillshade 404s. */}
                    {(() => {
                      const has3d = !!(elevationGrid && elevationGrid.cells);
                      const mode = terrainView === 'hillshade' || !has3d ? 'hillshade' : '3d';
                      // Hillshade preview — the explicit "Hillshade" tab
                      // AND the automatic fallback when the WebGL terrain
                      // can't render. Defined once so it can be reused as
                      // the LocalErrorBoundary fallback below (#1054): if
                      // ParcelTerrain3D throws "Error creating WebGL
                      // context" (headless browser, or a real browser that
                      // exhausted its ~16-context limit after navigating
                      // several parcels), the boundary swaps to this
                      // instead of letting the error bubble to the
                      // top-level ErrorBoundary and crash the whole page
                      // — which is what used to hide the listing photo
                      // gallery behind "Something went wrong".
                      const hillshadeFallback = (
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '1 / 1',
                            maxHeight: 360,
                            borderRadius: 6,
                            overflow: 'hidden',
                            background: '#222',
                          }}
                          data-testid="pd-terrain-hillshade"
                        >
                          <img
                            src={`/api/v1/parcels/${encodeURIComponent(parcelId)}/preview.png`}
                            alt={`Hillshade terrain preview of ${parcelId}`}
                            loading="lazy"
                            onError={(e) => {
                              // Hide image on 404 and let the SVG cake
                              // show through as last-resort fallback.
                              e.currentTarget.style.display = 'none';
                            }}
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                          {!has3d && (
                            <div
                              aria-hidden="true"
                              style={{
                                position: 'absolute',
                                inset: 'auto 8px 8px auto',
                                fontSize: '0.62rem',
                                color: 'rgba(255,255,255,0.7)',
                                background: 'rgba(0,0,0,0.35)',
                                padding: '2px 6px',
                                borderRadius: 4,
                              }}
                            >
                              3D loading…
                            </div>
                          )}
                        </div>
                      );
                      return (
                        <>
                          <div
                            role="tablist"
                            aria-label="Terrain view"
                            style={{
                              display: 'inline-flex',
                              gap: 0,
                              marginBottom: 8,
                              border: '1px solid #d1d5da',
                              borderRadius: 999,
                              padding: 2,
                              background: '#fafbfc',
                            }}
                          >
                            {['3d', 'hillshade'].map((m) => {
                              const active = mode === m;
                              const disabled = m === '3d' && !has3d;
                              return (
                                <button
                                  key={m}
                                  type="button"
                                  role="tab"
                                  aria-selected={active}
                                  disabled={disabled}
                                  onClick={() => setTerrainView(m)}
                                  data-testid={`pd-terrain-toggle-${m}`}
                                  style={{
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    padding: '4px 12px',
                                    borderRadius: 999,
                                    border: 'none',
                                    background: active ? '#1565c0' : 'transparent',
                                    color: active ? '#fff' : disabled ? '#bbb' : '#555',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    transition: 'background 0.15s, color 0.15s',
                                  }}
                                >
                                  {m === '3d' ? '3D' : 'Hillshade'}
                                </button>
                              );
                            })}
                          </div>
                          {mode === '3d' && has3d ? (
                            <LocalErrorBoundary
                              label="parcel-terrain-3d"
                              resetKey={parcelId}
                              fallback={hillshadeFallback}
                            >
                              <ParcelTerrain3D
                                geometry={parcel.geometry}
                                elevationRange={parcel.elevation_range}
                                elevationGrid={elevationGrid}
                              />
                            </LocalErrorBoundary>
                          ) : (
                            hillshadeFallback
                          )}
                        </>
                      );
                    })()}
                    {elevationProfile && elevationProfile.profile && elevationProfile.profile.length > 2 && (
                      <div style={{
                        padding: '12px 14px', borderRadius: 8, marginBottom: 10,
                        background: 'linear-gradient(135deg, #8B691410, #8B691406)',
                        border: '1px solid #8B691430',
                      }}>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8B6914', fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>
                          Elevation Profile
                        </div>
                        <div style={{ width: '100%', height: 120 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={elevationProfile.profile.map(p => ({
                              distance: Math.round(p.distance_m * 3.28084),
                              elevation: Math.round(p.elevation_m * 3.28084),
                            }))} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="elevGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#8B6914" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#8B6914" stopOpacity={0.05} />
                                </linearGradient>
                              </defs>
                              <XAxis dataKey="distance" tick={{ fontSize: 10, fill: '#999' }} tickFormatter={v => `${(v / 5280).toFixed(1)}mi`} />
                              <YAxis tick={{ fontSize: 10, fill: '#999' }} domain={['dataMin - 20', 'dataMax + 20']} tickFormatter={v => `${v.toLocaleString()}'`} />
                              <Tooltip
                                contentStyle={{ fontSize: '0.78rem', borderRadius: 6, border: '1px solid #ddd' }}
                                formatter={(val) => [`${val.toLocaleString()} ft`, 'Elevation']}
                                labelFormatter={(val) => `${(val / 5280).toFixed(2)} mi`}
                              />
                              <Area type="monotone" dataKey="elevation" stroke="#8B6914" strokeWidth={1.5} fill="url(#elevGrad)" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#999', marginTop: 4 }}>
                          <span>Gain: {fmtNum(elevationProfile.elevation_gain_m * 3.28084, 0)} ft</span>
                          <span>Loss: {fmtNum(elevationProfile.elevation_loss_m * 3.28084, 0)} ft</span>
                          <span>Length: {fmtNum(elevationProfile.length_m * 3.28084 / 5280, 2)} mi</span>
                        </div>
                      </div>
                    )}
                    {parcel.slope_mean != null && (
                      <div style={{
                        padding: '12px 14px', borderRadius: 8,
                        background: 'linear-gradient(135deg, #6B8E2318, #6B8E2308)',
                        border: '1px solid #6B8E2340',
                      }}>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#6B8E23', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                          Slope / Grade
                        </div>
                        <div style={{ display: 'flex', gap: 20, alignItems: 'baseline' }}>
                          <div>
                            <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#222' }}>
                              {fmtNum(parcel.slope_mean, 1)}
                            </span>
                            <span style={{ fontSize: '0.8rem', color: '#666' }}> avg</span>
                          </div>
                          {parcel.slope_max != null && (
                            <div>
                              <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#222' }}>
                                {fmtNum(parcel.slope_max, 1)}
                              </span>
                              <span style={{ fontSize: '0.8rem', color: '#666' }}> max</span>
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>
                          {parcel.slope_mean < 5 ? 'Gentle terrain' :
                           parcel.slope_mean < 15 ? 'Moderate terrain' :
                           parcel.slope_mean < 30 ? 'Steep terrain' : 'Very steep terrain'}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            </section>
          )}
          {/* Forest Cover & Loss (Hansen Global Forest Change, migration 0094;
              current-canopy columns migration 0099). The "Year-2000" badge is
              historical context. The "Current canopy (2021)" and "Estimated
              present" rows are the now-numbers added 2026-06-02 because the
              Hansen gain layer froze at 2012 and the UI was unintentionally
              implying canopy data ended there. Card hides when none of the
              forest fields populated (e.g. desert parcel or no ingest yet). */}
          {showTab('tab-land') && features && (
            features.forest_cover_2000_pct != null ||
            features.forest_loss_pct != null ||
            features.forest_loss_year_most_recent != null ||
            features.forest_regrowth_pct != null ||
            features.forest_cover_present_estimate_pct != null ||
            features.nlcd_tcc_2021_pct != null
          ) && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Forest Cover &amp; Loss</span>
                <InfoHint label="About Forest Cover sources">
                  Sources: Hansen Global Forest Change (UMD/Google, GFC-2024-v1.12)
                  for the 2000 baseline + loss-through-2024; USGS NLCD Tree Canopy
                  Cover 2021 (USDA Forest Service / USGS, CONUS, 30 m) for the
                  independent current-canopy estimate. Hansen "forest" =
                  canopy ≥30% at the year-2000 baseline.
                </InfoHint>
              </h3>
            <div className="detail-card">
              {(() => {
                const cover = features.forest_cover_2000_pct;
                const loss = features.forest_loss_pct;
                const lossYearRecent = features.forest_loss_year_most_recent;
                const lossYearMaj = features.forest_loss_year_majority;
                const regrowth = features.forest_regrowth_pct;
                const nlcd2021 = features.nlcd_tcc_2021_pct;
                const presentEst = features.forest_cover_present_estimate_pct;
                const recentClearCut = loss != null && loss >= 50 && lossYearRecent != null && lossYearRecent >= (new Date().getFullYear() - 6);
                return (
                  <>
                    {/* Current canopy (NLCD 2021). Top of the card because
                        this is the "what is it NOW" number users expect. */}
                    {nlcd2021 != null && (
                      <div style={{
                        padding: '14px 16px', borderRadius: 8, marginBottom: 10,
                        background: 'linear-gradient(135deg, #1b5e2018, #1b5e2008)',
                        border: '1px solid #1b5e2040',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#1b5e20', fontWeight: 700, letterSpacing: 0.5 }}>
                            Current Canopy (2021)
                          </div>
                          <span
                            title="USGS NLCD Tree Canopy Cover 2021 (USDA Forest Service / USGS, CONUS, 30 m raster). Mean canopy % within the parcel polygon. This is an independent measurement from Hansen — NLCD is reissued every 2-3 years; 2021 is the latest reference year."
                            style={{ fontSize: '0.62rem', background: '#1b5e2020', color: '#1b5e20', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}
                          >
                            USGS NLCD 2021
                          </span>
                        </div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#222' }}>
                          {Math.round(nlcd2021)}%
                          <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#666', marginLeft: 8 }}>
                            {nlcd2021 < 5 ? '— bare'
                              : nlcd2021 < 25 ? '— sparse cover'
                              : nlcd2021 < 60 ? '— partial cover'
                              : nlcd2021 < 85 ? '— heavily forested'
                              : '— mature forest'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Estimated present (Hansen-derived). Shown when NLCD
                        is missing (e.g. non-CONUS parcel) OR alongside it
                        as a sanity check. */}
                    {presentEst != null && (
                      <div style={{
                        padding: '12px 14px', borderRadius: 8, marginBottom: 10,
                        background: 'linear-gradient(135deg, #4caf5018, #4caf5008)',
                        border: '1px solid #4caf5030',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#2e7d32', fontWeight: 700, letterSpacing: 0.5 }}>
                            Estimated Present (Hansen-derived)
                          </div>
                          <span
                            title="Derived: max(0, min(100, year-2000 cover − cumulative loss through 2024 + regrowth through 2012)). Hansen's gain (regrowth) dataset is a one-time 2000-2012 study and has not been updated, so this estimate misses any post-2012 regrowth on the parcel. Hansen loss is current through 2024. Treat as a floor estimate; cross-check with the NLCD 2021 number above."
                            style={{ fontSize: '0.62rem', background: '#4caf5025', color: '#2e7d32', padding: '2px 8px', borderRadius: 10, fontWeight: 600, cursor: 'help' }}
                          >
                            derived · 2012 ceiling
                          </span>
                        </div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#222' }}>
                          {Math.round(presentEst)}%
                          {nlcd2021 != null && Math.abs(presentEst - nlcd2021) >= 15 && (
                            <span style={{ fontSize: '0.72rem', fontWeight: 400, color: '#888', marginLeft: 8 }}>
                              · differs {Math.round(presentEst - nlcd2021)} pts from NLCD 2021
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#888', marginTop: 4 }}>
                          Estimate. Hansen regrowth layer froze at 2012; post-2012 regrowth is invisible to this number.
                        </div>
                      </div>
                    )}

                    {/* Baseline cover badge */}
                    <div style={{
                      padding: '14px 16px', borderRadius: 8, marginBottom: 10,
                      background: 'linear-gradient(135deg, #2e7d3218, #2e7d3208)',
                      border: '1px solid #2e7d3240',
                    }}>
                      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#2e7d32', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                        Year-2000 Forest Cover
                      </div>
                      {cover != null ? (
                        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#222' }}>
                          {Math.round(cover)}%
                          <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#666', marginLeft: 8 }}>
                            {cover < 5 ? '— bare/desert'
                              : cover < 25 ? '— sparse'
                              : cover < 60 ? '— partial cover'
                              : cover < 85 ? '— heavily forested'
                              : '— mature forest'}
                          </span>
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.9rem', color: '#888' }}>not computed</div>
                      )}
                    </div>

                    {/* Loss / clear-cut signal */}
                    {loss != null && (
                      <div style={{
                        padding: '12px 14px', borderRadius: 8, marginBottom: 10,
                        background: recentClearCut
                          ? 'linear-gradient(135deg, #c0392b18, #c0392b08)'
                          : 'linear-gradient(135deg, #d3556318, #d3556308)',
                        border: `1px solid ${recentClearCut ? '#c0392b40' : '#d3556330'}`,
                      }}>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: recentClearCut ? '#c0392b' : '#a9482a', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                          {recentClearCut ? '⚠ Recent Clear-Cut' : 'Forest Loss Since 2001'}
                        </div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#222' }}>
                          {Math.round(loss)}% of parcel lost
                          {lossYearRecent != null && (
                            <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#666', marginLeft: 8 }}>
                              · most recent {lossYearRecent}
                            </span>
                          )}
                        </div>
                        {lossYearMaj != null && lossYearMaj !== lossYearRecent && (
                          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>
                            Largest single year: {lossYearMaj}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Regrowth (legacy 2000-2012 gain layer) */}
                    {regrowth != null && regrowth >= 1 && (
                      <div style={{
                        padding: '12px 14px', borderRadius: 8,
                        background: 'linear-gradient(135deg, #27ae6018, #27ae6008)',
                        border: '1px solid #27ae6040',
                      }}>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#27ae60', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                          Regrowth (2000-2012)
                        </div>
                        <div style={{ fontSize: '1.0rem', fontWeight: 700, color: '#222' }}>
                          {Math.round(regrowth)}% gained
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#999', marginTop: 4 }}>
                          Hansen has not refreshed the gain layer since 2012
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            </section>
          )}
          {/* Nearby Mineral Sites */}
          {showTab('tab-minerals') && nearbyMines && nearbyMines.mines && nearbyMines.mines.length > 0 && (
            <section>
            <div className="detail-card">
              <h3>Nearby Mineral Sites ({nearbyMines.count} within {fmtNum(nearbyMines.radius_km * KM_TO_MI, 0)} mi)</h3>
              {/* maxHeight + overflowY:auto to match the Comparable
                  Parcels section above — keeps the page from
                  ballooning when a parcel has 30+ nearby mines (#151). */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
                {nearbyMines.mines.map((mine, idx) => {
                  const statusColors = {
                    'Producer':      { bg: '#27ae60', text: '#fff' },
                    'Past Producer': { bg: '#e67e22', text: '#fff' },
                    'Prospect':      { bg: '#3498db', text: '#fff' },
                    'Occurrence':    { bg: '#9b59b6', text: '#fff' },
                    'Plant':         { bg: '#95a5a6', text: '#fff' },
                  };
                  const sc = (mine.dev_status && statusColors[mine.dev_status]) || { bg: '#bdc3c7', text: '#333' };
                  const allCommodities = [mine.commodity, mine.commod2, mine.commod3].filter(Boolean);
                  return (
                    <div key={mine.id || idx} style={{
                      padding: '8px 12px', borderRadius: 6,
                      background: idx === 0 ? '#fef9f0' : '#fafafa',
                      border: idx === 0 ? '1px solid #f0e0c0' : '1px solid #eee',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#222' }}>
                          {mine.site_name || 'Unnamed Site'}
                        </div>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#e94560' }}>
                          {fmtNum(mine.distance_km * KM_TO_MI, 1)} mi
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap', fontSize: '0.8rem' }}>
                        {mine.dev_status && (
                          <span style={{ background: sc.bg, color: sc.text, padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>
                            {mine.dev_status}
                          </span>
                        )}
                        {allCommodities.map((c, i) => (
                          <span key={i} style={{ background: i === 0 ? '#eef' : '#f5f0ff', padding: '1px 8px', borderRadius: 10, color: i === 0 ? '#446' : '#669' }}>
                            {c}
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 3 }}>
                        {[mine.oper_type, mine.dep_type, (mine.county && mine.state) ? `${mine.county}, ${mine.state}` : null]
                          .filter(Boolean).join(' \u00b7 ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            </section>
          )}
          {/* News in this state — pulled from /dashboard/news?state= */}
          {showTab('tab-documents') && stateNews && stateNews.events && stateNews.events.length > 0 && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Mining news in {parcel.state}</span>
                <InfoHint label="About Mining news source">
                  Last 30 days, mining/metals RSS feeds. Headlines mentioning {parcel.state} as a
                  state-level location.
                </InfoHint>
              </h3>
            <div className="detail-card">
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {stateNews.events.slice(0, 5).map(ev => (
                  <li key={ev.id} style={{
                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}>
                    <a
                      href={ev.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '0.9rem', fontWeight: 600,
                        color: '#1565c0', textDecoration: 'none',
                      }}
                    >
                      {ev.title}
                    </a>
                    <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 2 }}>
                      {ev.source}
                      {ev.published_at &&
                        ` · ${new Date(ev.published_at).toLocaleDateString()}`}
                      {ev.commodities && ev.commodities.length > 0 &&
                        ` · ${ev.commodities.join(', ')}`}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            </section>
          )}
          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 24 }}>Reference</div>
          <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0 12px' }} aria-hidden="true" />
          {/* Encumbrances — liens / easements / water rights / mineral
              rights / covenants. Per #95 the schema is in place; the
              per-county / per-state ingestion is incremental, so this
              section often shows "no data" for a given parcel. We
              still render it because the *absence* of recorded liens
              is itself a positive signal worth surfacing. */}
          {showTab('tab-minerals') && (parcel.has_lien != null || parcel.has_easement != null
            || parcel.water_rights_acft != null || parcel.development_rights != null
            || (parcel.encumbrances && parcel.encumbrances.length > 0)) && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0 }}>Encumbrances</h3>
            <div className="detail-card">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                {parcel.has_lien != null && (
                  <span style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem',
                    background: parcel.has_lien ? '#fde8e8' : '#e8f5e9',
                    color: parcel.has_lien ? '#c62828' : '#2e7d32',
                    fontWeight: 600,
                  }}>
                    {parcel.has_lien ? 'Lien on record' : 'No lien on record'}
                  </span>
                )}
                {parcel.has_easement != null && (
                  <span style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem',
                    background: parcel.has_easement ? '#fff3e0' : '#e8f5e9',
                    color: parcel.has_easement ? '#e65100' : '#2e7d32',
                    fontWeight: 600,
                  }}>
                    {parcel.has_easement ? 'Easement on record' : 'No easement'}
                  </span>
                )}
                {parcel.water_rights_acft != null && parcel.water_rights_acft > 0 && (
                  <span style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem',
                    background: '#e3f2fd', color: '#1565c0', fontWeight: 600,
                  }}>
                    💧 {parcel.water_rights_acft.toFixed(1)} acft/yr water rights
                  </span>
                )}
                {parcel.development_rights != null && (
                  <span style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem',
                    background: parcel.development_rights ? '#fff8e1' : '#e8f5e9',
                    color: parcel.development_rights ? '#f57f17' : '#2e7d32',
                    fontWeight: 600,
                  }}>
                    {parcel.development_rights ? 'Development rights mentioned' : 'No dev-rights signal'}
                  </span>
                )}
              </div>
              {parcel.encumbrances && parcel.encumbrances.length > 0 ? (
                <EncumbrancesTable encumbrances={parcel.encumbrances} />
              ) : (
                <p style={{ fontSize: '0.78rem', color: '#888', margin: 0 }}>
                  No encumbrance details ingested for this parcel yet.
                </p>
              )}
            </div>
            </section>
          )}
          {/* Listing-quality predictor (#775 / INIT-listing-quality).
              Per-LISTING composite 0-100 + signals JSONB. Renders
              when the parcel has an active scored listing; the
              `listing_quality_signals` payload drives the expandable
              breakdown with the 6 sub-scores + their buckets so the
              buyer sees why the score is what it is.

              Lives on the parcel-detail page (not the hazards tab)
              because it's a listing-attribute summary, not a hazard
              risk. We slot it under the listing/price info already
              shown by the existing listing card; if there's no active
              listing the score is NULL and we skip the whole card. */}
          {parcel.listing_quality_score_0_100 != null && (
            <section>
              <h3 style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}>
                <span>Listing Quality</span>
                <InfoHint label="About Listing Quality">
                  Composite 0-100 score over photos, description, price
                  reasonableness, freshness, and completeness. Higher = better
                  listing. Computed by scripts/compute_listing_quality.py.
                </InfoHint>
              </h3>
            <div className="detail-card" data-testid="pd-listing-quality-card">
              {(() => {
                // Traffic-light bands inverted from climate-risk (higher
                // = better here). Matches the colors on the PropertyCard
                // chip so the badge + the headline donut read as the
                // same signal.
                const headlineColor = (v) => {
                  if (v == null) return { bg: '#f0f0f0', fg: '#999', band: '—' };
                  if (v >= 75)   return { bg: '#e8f5e9', fg: '#2e7d32', band: 'Excellent' };
                  if (v >= 60)   return { bg: '#e8f5e9', fg: '#43a047', band: 'Good' };
                  if (v >= 45)   return { bg: '#fff8e1', fg: '#b78103', band: 'Fair' };
                  if (v >= 30)   return { bg: '#ffe0b2', fg: '#d35400', band: 'Poor' };
                  return            { bg: '#ffcdd2', fg: '#b71c1c', band: 'Very Poor' };
                };
                const v = parcel.listing_quality_score_0_100;
                const c = headlineColor(v);
                // Signal display order matches the docstring weights —
                // highest-weight first so the user sees the dominant
                // contributors at the top of the breakdown.
                const SIGNAL_LABELS = [
                  ['aesthetic',       'Photo Aesthetic',  'Computed by ML photo scorer (PR-758)'],
                  ['photo_count',     'Photo Count',      'Number of listing photos'],
                  ['description',     'Description',      'Length + generic-phrase quality'],
                  ['price',           'Price',            'Vs comparable median for state + acre decile'],
                  ['days_on_market',  'Days on Market',   'Fresh listings score higher'],
                  ['critical_fields', 'Critical Fields',  'Price, address, centroid present'],
                ];
                const sigs = parcel.listing_quality_signals || {};
                return (
                  <>
                    {/* Headline pill */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      padding: '14px 16px', borderRadius: 8, marginBottom: 12,
                      background: c.bg,
                    }}>
                      <div style={{
                        width: 64, height: 64, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#fff',
                        border: `4px solid ${c.fg}`,
                        fontSize: '1.5rem', fontWeight: 800, color: c.fg,
                      }}>{Math.round(v)}</div>
                      <div>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5, color: c.fg, fontWeight: 700 }}>
                          Listing Quality
                        </div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#222' }}>{c.band}</div>
                        <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 2 }}>
                          Composite score (0-100, higher = better)
                        </div>
                      </div>
                    </div>
                    {/* Sub-score breakdown — collapsible */}
                    <div style={{ marginTop: 4 }}>
                      <div style={{ fontSize: '0.85rem', color: '#1565c0', fontWeight: 600, padding: '4px 0' }}>Sub-component breakdown</div>
                      <div style={{ paddingTop: 8 }}>
                        {SIGNAL_LABELS.map(([key, label, blurb]) => {
                          const sig = sigs[key];
                          const score = sig && typeof sig.score === 'number' ? sig.score : null;
                          const bucket = sig && sig.bucket ? sig.bucket : null;
                          const cc = headlineColor(score);
                          return (
                            <div key={key} style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '4px 0', fontSize: '0.85rem',
                            }}>
                              <span style={{ flex: 1 }}>
                                <span style={{ fontWeight: 500 }}>{label}</span>
                                <span style={{ display: 'block', fontSize: '0.7rem', color: '#999' }}>
                                  {blurb}{bucket ? ` · ${bucket}` : ''}
                                </span>
                              </span>
                              <span style={{
                                minWidth: 38, padding: '2px 8px', borderRadius: 10,
                                background: cc.bg, color: cc.fg,
                                fontSize: '0.78rem', fontWeight: 700,
                                textAlign: 'center',
                              }}>
                                {score == null ? '—' : Math.round(score)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            </section>
          )}
          {/* Deep (max) property record — sourced public-records dossier (#1059) */}
          {showTab('tab-documents') && (
            <section>
              <DeepRecordSection maxRecord={maxRecord} />
            </section>
          )}
          {/* External links */}
          {showTab('tab-documents') && <section>
            <div className="detail-card">
            <h3 id="tab-documents">External Links</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {hasCoords && (
                <>
                  <a href={`https://maps.apple.com/?ll=${lat},${lon}&q=${lat},${lon}`}
                    target="_blank" rel="noopener noreferrer" className="ext-link">
                    Apple Maps
                  </a>
                  <a href={`https://id.land/search?query=${lat.toFixed(6)},${lon.toFixed(6)}`}
                    target="_blank" rel="noopener noreferrer" className="ext-link">
                    id.land Parcel Search
                  </a>
                  {(parcel.listing_zip || parcel.state) && (
                    <a
                      href={
                        parcel.listing_zip
                          ? `https://www.redfin.com/zipcode/${encodeURIComponent(parcel.listing_zip)}/filter/property-type=land`
                          : `https://www.redfin.com/state/${encodeURIComponent(parcel.state)}/filter/property-type=land`
                      }
                      target="_blank" rel="noopener noreferrer" className="ext-link">
                      Redfin Search ({parcel.listing_zip ? `zip ${parcel.listing_zip}` : parcel.state})
                    </a>
                  )}
                  <a href={`https://mrdata.usgs.gov/mrds/map-us.html?x=${lon}&y=${lat}&z=12`}
                    target="_blank" rel="noopener noreferrer" className="ext-link">
                    USGS Mineral Resources Near Here
                  </a>
                  <a href={`https://blm-egis.maps.arcgis.com/apps/webappviewer/index.html?id=a2c2cd4f479a4c8eaf498b1e7bcca6b4&center=${lon},${lat}&level=14`}
                    target="_blank" rel="noopener noreferrer" className="ext-link">
                    BLM Mining Claims
                  </a>
                </>
              )}
              {parcel.mls_number && (
                <a href={`https://www.redfin.com/zipcode/${encodeURIComponent(parcel.listing_zip || '')}/filter/property-type=land`}
                  target="_blank" rel="noopener noreferrer" className="ext-link">
                  Redfin MLS #{parcel.mls_number}
                </a>
              )}
            </div>
          </div>
          </section>}
        </div>
      </div>

      {/* "Why this parcel?" — auto-generated narrative summary */}
      {showTab('tab-investment') && whyThisParcel}

      {/* =================== Bottom Section: Resource Scores & Feature Data =================== */}
      {/* Opportunity Score headline badge (#171). The (i) breaks the
          composite into its five weighted factors so the owner can see
          which are actually scored for this parcel (#995). Factor
          sub-scores are read from the detail endpoint's correct
          per-model columns (parcel.value_score / mineral_score /
          geothermal_score, parcels.py:3462-3492) + commodityScores +
          geo_features infrastructure_score — NOT the masked MV. The
          headline number is still served from mv_top_opportunities,
          which currently under-counts Value/Mineral/Commodity (single-
          latest-model-row masking, #995; commodity join, #994); the
          "live composite" line reconciles the two. */}
      {showTab('tab-investment') && parcel.opportunity_score != null && (() => {
        const csRoot = commodityScores || {};
        const cs = csRoot.scores || csRoot;
        const commodityVals = ['gold', 'copper', 'silver', 'uranium', 'lithium', 'ree']
          .map((k) => cs[k] ?? cs[`${k}_prob`])
          .filter((v) => v != null);
        const commodityMax = commodityVals.length ? Math.max(...commodityVals) : null;
        // Mineral-rights penalty mirrors mv_top_opportunities (migration
        // 0039): included -> 1.0, excluded -> 0.10, outstanding federal
        // right -> 0.20, else 1.0. Applies to the mineral + commodity
        // components only.
        const sig = parcel.mineral_rights_signal;
        const hasOutstanding = parcel.mineral_rights_federal_records?.has_outstanding_right === true;
        const mrMult = sig === 'included' ? 1.0 : sig === 'excluded' ? 0.10 : (hasOutstanding ? 0.20 : 1.0);
        // #1288: in a state no mineral model covers, the mineral and commodity
        // factors are "not scored", which is not the same as "no data yet":
        // they never will be, so they leave the blend instead of counting as 0.
        const modeled = mineralModelApplies(parcel);
        const factors = [
          { key: 'value', name: 'Land value', weight: 0.35, score: parcel.value_score, mult: 1, model: 'value_v3 county baseline' },
          { key: 'mineral', name: 'Mineral potential', weight: 0.25, score: parcel.mineral_score, mult: mrMult, model: 'mineral_potential_v1', notScored: !modeled },
          { key: 'commodity', name: 'Top commodity prob.', weight: 0.20, score: commodityMax, mult: mrMult, model: 'commodity_scores (max of 6)', notScored: !modeled },
          { key: 'geothermal', name: 'Geothermal', weight: 0.10, score: parcel.geothermal_score, mult: 1, model: 'geothermal_v1_heuristic' },
          { key: 'access', name: 'Infrastructure access', weight: 0.10, score: parcel.infrastructure_score, mult: 1, model: 'geo_features proximity' },
        ];
        const anyNotScored = factors.some((f) => f.notScored);
        const blendWeight = anyNotScored
          ? factors.reduce((s, f) => s + (f.notScored ? 0 : f.weight), 0)
          : 1;
        const liveComposite = factors.reduce(
          (s, f) => s + (!f.notScored && f.score != null ? f.score * f.mult * f.weight : 0), 0) / blendWeight;
        const anyMissing = factors.some((f) => !f.notScored && f.score == null);
        return (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(90deg, #1a2a1a 0%, #1e3a1e 100%)',
          border: '1px solid #2ecc71', borderRadius: 8, padding: '12px 20px',
          marginTop: 16,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Opportunity Score
              <InfoHint label="Opportunity Score factors" width={360} inline>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {anyNotScored
                    ? 'Weighted blend of the model factors that cover this parcel:'
                    : 'Weighted blend of five model factors for this parcel:'}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                  <thead>
                    <tr style={{ color: '#888', textAlign: 'right' }}>
                      <th style={{ textAlign: 'left', fontWeight: 500, paddingBottom: 3 }}>Factor</th>
                      <th style={{ fontWeight: 500, paddingBottom: 3 }}>Weight</th>
                      <th style={{ fontWeight: 500, paddingBottom: 3 }}>Score</th>
                      <th style={{ fontWeight: 500, paddingBottom: 3 }}>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factors.map((f) => {
                      if (f.notScored) {
                        return (
                          <tr key={f.key} data-testid={`opp-factor-${f.key}-not-scored`} title={NOT_SCORED_HINT}>
                            <td style={{ padding: '2px 8px 2px 0' }}>{f.name}</td>
                            <td style={{ padding: '2px 8px', color: '#888', textAlign: 'right' }}>&mdash;</td>
                            <td style={{ padding: '2px 8px', textAlign: 'right', color: '#9ca3af', fontStyle: 'italic' }}>
                              {NOT_SCORED_LABEL.toLowerCase()}
                            </td>
                            <td style={{ padding: '2px 0 2px 8px', textAlign: 'right', color: '#aaa' }}>&mdash;</td>
                          </tr>
                        );
                      }
                      const scored = f.score != null;
                      const contrib = scored ? f.score * f.mult * f.weight : 0;
                      return (
                        <tr key={f.key}>
                          <td style={{ padding: '2px 8px 2px 0' }}>{f.name}</td>
                          <td style={{ padding: '2px 8px', color: '#888', textAlign: 'right' }}>{Math.round(f.weight * 100)}%</td>
                          <td style={{ padding: '2px 8px', textAlign: 'right', color: scored ? '#2ecc71' : '#e0803a' }}>
                            {scored ? f.score.toFixed(2) : 'no data'}
                          </td>
                          <td style={{ padding: '2px 0 2px 8px', textAlign: 'right', color: '#aaa' }}>
                            {scored ? `+${Math.round(contrib * 100)}` : '+0'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 6, paddingTop: 4, borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                  <span>Live composite</span>
                  <span>{Math.round(liveComposite * 100)}/100</span>
                </div>
                {mrMult < 1.0 && !anyNotScored && (
                  <div style={{ marginTop: 4, color: '#e0803a' }}>
                    Mineral + commodity discounted &times;{mrMult} (mineral-rights: {sig || 'severed estate'}).
                  </div>
                )}
                {anyNotScored && (
                  <div style={{ marginTop: 4, color: '#888' }} data-testid="opp-not-scored-note">
                    &ldquo;not scored&rdquo; = no mineral model covers this state; those factors
                    are left out of the blend, not counted as 0.
                  </div>
                )}
                {anyMissing && (
                  <div style={{ marginTop: 4, color: '#888' }}>
                    &ldquo;no data&rdquo; = not yet scored for this parcel; contributes 0.
                  </div>
                )}
              </InfoHint>
            </div>
            <div style={{ fontSize: '0.68rem', color: '#888', marginTop: 2 }}>
              Value 35% · Mineral 25% · Commodity 20% · Geothermal 10% · Access 10%
            </div>
          </div>
          {(() => {
            // #1017: opportunity_score now arrives already normalized to a
            // 0-100 percentile from the backend, so we render it directly.
            // The client-side min-max rescale (rescaleToReference /
            // OPPORTUNITY_REFERENCE_MAX) and the #994/#995 divergence banner
            // it compensated for are removed.
            const display = Math.round(parcel.opportunity_score);
            return (
              <div
                style={{ textAlign: 'right' }}
                title={`Composite opportunity score: ${display}/100 (0-100 percentile). Higher = stronger relative to the scored parcel population.`}
              >
                <span style={{ fontWeight: 700, fontSize: '2rem', color: '#2ecc71' }}>
                  {display}
                </span>
                <span style={{ fontSize: '1rem', color: '#888' }}>/100</span>
              </div>
            );
          })()}
        </div>
        );
      })()}
      {/* Investment thesis tags (#771 / INIT-thesis-tagger). Multi-label
          synthesis layer over the resource scores below — surfaces 0-N
          rule-based tags for how buyers actually shop (off-grid /
          timber / solar-lease / mineral / hunting / cash-rent ag).
          Clicking a chip filters SearchPage by that tag. Lazy fetch
          on mount; renders nothing if no tags hit the >=40 floor. */}
      {showTab('tab-investment') && (
        <ThesisTagsCard parcelId={parcelId} navigate={navigate} />
      )}
      {/* #932 TASK-330: reduced gap 16→10 (matches 10px column rhythm) + marginTop 16→10 */}
      {showTab('tab-investment') && <div className="pd-investment-grid" data-testid="pd-investment-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
        {/* Resource scores */}
        <div className="detail-card">
          <h3>Resource Scores</h3>
          {(() => {
            const ms = scores?.mineral_score?.value ?? scores?.mineral_score ?? parcel.mineral_score;
            const gs = scores?.geothermal_score?.value ?? scores?.geothermal_score ?? parcel.geothermal_score;
            const vs = scores?.value_score?.value ?? scores?.value_score ?? parcel.value_score;
            const cs = scores?.commodity_score?.value ?? scores?.commodity_score ?? null;
            const is = scores?.infrastructure_score?.value ?? scores?.infrastructure_score ?? null;
            // Solar feasibility (#124 / migration 0085). #1017: bind to the
            // server-normalized 0-100 `solar_pct` rather than the raw
            // `solar_score`, matching wind_pct / hydro_pct below. Pulls from
            // the features dict that get_parcel populates.
            const ss = (features && features.solar_pct) ?? null;
            const ghi = (features && features.solar_ghi_kwh_m2_day) ?? null;
            // Wind feasibility (#126 / migration 0086). #1017: bind to the
            // server-normalized 0-100 `wind_pct` rather than the floored
            // `wind_score`. Underlying physical estimate is the mean wind
            // speed at 80m hub height in m/s.
            const ws = (features && features.wind_pct) ?? null;
            const wspd = (features && features.wind_speed_ms_80m) ?? null;
            // Composite wind VIABILITY score (#755 / migration 0102).
            // Distinct from `wind_score` above (wind-speed-only). The
            // composite folds in parcel area, slope (terrain ruggedness
            // proxy), land-use eligibility, and transmission proximity
            // so the number reflects whether a parcel could *actually*
            // host a utility turbine, not just whether the atmospheric
            // resource is good. NULL-safe: surfaces only when the
            // upstream wind_speed_ms_80m is populated.
            const wv = (features && features.wind_score_0_100) ?? null;
            // Hydro feasibility (#127 / migration 0087). Stored as a
            // 0-100 score on the same scale as the other ScoreBars,
            // alongside the underlying physical estimate (peak kW)
            // and distance to nearest stream (meters). Today the
            // backend script ships as a NO-OP because no NHD stream
            // layer is ingested yet, so this bar will be hidden for
            // every parcel until the follow-up "ingest USGS NHD
            // flowlines" issue lands. Once it does, no frontend
            // change is required — the score appears automatically.
            // #1017: bind to the server-normalized 0-100 `hydro_pct` rather
            // than the NULL-heavy `hydro_score`.
            const hys = (features && features.hydro_pct) ?? null;
            const hykw = (features && features.hydro_kw_potential) ?? null;
            const hydist = (features && features.hydro_distance_to_stream_m) ?? null;
            // Composite hydro VIABILITY score (#757 / migration 0103).
            // Distinct from `hydro_score` above (kW-only physical potential).
            // The composite folds in distance-to-stream, parcel area,
            // elevation range, land-use eligibility, and the state water-
            // rights regime so the number reflects whether a parcel could
            // *actually* host a micro-hydro install (physics + permittability).
            // NULL-safe: when the underlying nhd_flowlines ingest hasn't
            // landed for the parcel's state, this still surfaces a
            // capped (<=50) score derived from elevation + area + land-use.
            const hyv = (features && features.hydro_score_0_100) ?? null;
            const hasScores = !mineralModelApplies(parcel) || ms != null || gs != null || vs != null || cs != null || is != null || ss != null || ws != null || wv != null || hys != null || hyv != null;
            return hasScores ? (
              <>
                {/* #1017: value / mineral / commodity / geothermal now
                    arrive already on a 0-100 axis from the backend. */}
                <ScoreBar label="Value Score" value={vs} scale="0-100" color="#2ecc71" />
                <ScoreBar label="Mineral Potential" value={ms} scale="0-100" color="#e94560" notScored={!mineralModelApplies(parcel)} />
                <ScoreBar label="Commodity Potential" value={cs} scale="0-100" color="#9b59b6" notScored={!mineralModelApplies(parcel)} />
                <ScoreBar label="Geothermal Potential" value={gs} scale="0-100" color="#e67e22" />
                <ScoreBar
                  label="Solar Potential"
                  value={ss}
                  scale="0-100"
                  color="#f1c40f"
                  /* Tooltip surfaces the input signals — issue #754
                     Stage-1.5 added slope + land-use modifiers on top
                     of the GHI base. Title attr on the wrapper span
                     so the hover hint shows on the bar without a new
                     dedicated Tooltip component. */
                  title="Solar viability 0-100: GHI estimate × slope penalty (>15° terrain) × land-use modifier (built-up parcels penalized). Stage-1 GHI from NREL NSRDB regional averages; Stage-2 raster lookup pending."
                />
                {ghi != null && (
                  <div
                    style={{ fontSize: '0.72rem', color: '#888', marginTop: -6, marginBottom: 8 }}
                    title="Global Horizontal Irradiance — the unmodified solar resource at this location. The 0-100 score above also reflects slope and land-use modifiers (issue #754)."
                  >
                    {ghi.toFixed(2)} kWh/m²/day annual mean GHI (Stage-1.5: GHI + slope + land-use)
                  </div>
                )}
                <ScoreBar label="Wind Potential" value={ws} scale="0-100" color="#1abc9c" />
                {wspd != null && (
                  <div style={{ fontSize: '0.72rem', color: '#888', marginTop: -6, marginBottom: 8 }}>
                    {wspd.toFixed(1)} m/s avg @ 80m hub height (Stage-1 estimate)
                  </div>
                )}
                {/* Composite wind VIABILITY badge (#755 / migration 0102).
                    Distinct from the "Wind Potential" bar above (which is
                    wind-speed-only). Folds in area, slope, land-use, and
                    transmission proximity so the user sees the difference
                    between "great wind here" and "this parcel could host a
                    turbine". Rendered as a small inline badge rather than a
                    second ScoreBar so it doesn't visually compete with the
                    primary atmospheric resource bar. */}
                {wv != null && (
                  <div
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      marginTop: -2,
                      marginBottom: 10,
                      borderRadius: 10,
                      background: '#1abc9c',
                      color: '#fff',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                    }}
                    title="Composite wind viability 0-100: 55% wind speed + 15% parcel area + 10% terrain slope + 10% land-use eligibility + 10% transmission proximity. A high number means the parcel could actually host a utility-class turbine; a high Wind Potential bar above without a high viability badge means the resource is good but the site isn't (too small, too built-up, or far from transmission)."
                  >
                    Wind viability: {Math.round(wv)}/100
                  </div>
                )}
                {hys != null && (
                  <>
                    <ScoreBar label="Hydro Potential" value={hys} scale="0-100" color="#1f6feb" />
                    {/* Water-rights caveat — PRODUCT-CRITICAL. Surface
                        diversion for hydropower requires a state
                        permit independent of the land deed; a high
                        hydro score is *physical* potential only.
                        Removing or de-emphasizing this caveat creates
                        a real buyability misrepresentation. */}
                    <div style={{ fontSize: '0.72rem', color: '#f39c12', marginTop: -6, marginBottom: 4 }}>
                      ⚠ Verify water rights — diversions typically require a permit
                    </div>
                    {(hykw != null || hydist != null) && (
                      <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 8 }}>
                        {hykw != null && `${hykw.toFixed(2)} kW peak`}
                        {hykw != null && hydist != null && ' · '}
                        {hydist != null && `${Math.round(hydist)}m to nearest stream`}
                        {' (Stage-1 estimate)'}
                      </div>
                    )}
                  </>
                )}
                {/* Composite Hydro Viability badge (#757 / migration 0103).
                    Sits BELOW the physical Hydro Potential bar so users
                    see the physics first, then the composite verdict that
                    bakes in stream-distance comfort, area, elevation
                    range, land-use eligibility, and the state water-
                    rights regime. NULL-safe: hidden when the score
                    couldn't be computed at all. The existing water-
                    rights caveat above covers this badge too — we
                    deliberately do NOT duplicate it here. */}
                {hyv != null && (
                  <div
                    style={{
                      display: 'inline-block',
                      fontSize: '0.78rem',
                      padding: '3px 9px',
                      borderRadius: 12,
                      background: hyv >= 70 ? '#1f6feb' : hyv >= 40 ? '#3a5fd9' : '#34495e',
                      color: '#fff',
                      marginBottom: 10,
                      marginTop: hys == null ? 4 : 0,
                    }}
                    title="Composite micro-hydro siting viability: kW potential + stream proximity + parcel area + elevation drop + land-use eligibility + state water-rights regime. 0-100 (higher = more viable). When the underlying NHD stream layer isn't ingested for this state, the score is capped at 50 and reflects elevation + area + land-use only."
                  >
                    Hydro Viability: {Math.round(hyv)}/100
                  </div>
                )}
                <ScoreBar label="Infrastructure Access" value={is} scale="0-100" color="#3498db" />
                {scores?.mineral_score?.model_name && (
                  <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: 4 }}>
                    Model: {scores.mineral_score.model_name} v{scores.mineral_score.version}
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: '#999', fontSize: '0.9rem' }}>No resource scores available yet.</p>
            );
          })()}
        </div>

        {/* Ground & Vegetation — derived from Sentinel-1 SAR backscatter
            (#177 / migration 0092). Each score is 0-100 derived from
            the radar inputs by scripts/compute_sar_derived_features.py;
            see that file's docstring for per-score physics. Land-cover
            class is a rule-based hierarchical classifier from the same
            inputs. Hidden when the parcel has no SAR data (existing
            InSAR coverage gap — #709) so the card doesn't render a
            row of empties. */}
        <div className="detail-card">
          <h3>Ground & Vegetation</h3>
          {(() => {
            const vd = (features && features.vegetation_density_score) ?? null;
            const bu = (features && features.built_up_likelihood) ?? null;
            const rd = (features && features.recent_disturbance_score) ?? null;
            const lcc = (features && features.land_cover_class) ?? null;
            const snow = (features && features.seasonal_snow_pct) ?? null;
            const hasAny = vd != null || bu != null || rd != null || lcc != null || snow != null;
            if (!hasAny) {
              return (
                <p style={{ color: '#999', fontSize: '0.9rem' }}>
                  No SAR data ingested for this parcel — derived ground-cover
                  signals will appear once the Sentinel-1 coverage gap (#709)
                  is closed for this region.
                </p>
              );
            }
            // ScoreBar takes 0-1 input; our scores are 0-100. Divide
            // before passing so the bar fill and "%" label are correct.
            const CLASS_COLORS = {
              forest: '#2d7a3a', grassland: '#a8b86c', bare: '#d4a574',
              urban: '#888', mixed: '#888', water: '#5ba',
            };
            const lccColor = lcc && CLASS_COLORS[lcc] ? CLASS_COLORS[lcc] : '#888';
            return (
              <>
                {lcc && (
                  <div
                    title="Derived from Sentinel-1 SAR backscatter — VV/VH polarization ratio + cross-pol intensity"
                    style={{
                      display: 'inline-block', padding: '4px 10px',
                      background: lccColor, color: '#fff', borderRadius: 12,
                      fontSize: '0.8rem', fontWeight: 600, textTransform: 'capitalize',
                      marginBottom: 12,
                    }}
                  >
                    {lcc}
                  </div>
                )}
                <ScoreBar label="Vegetation Density" value={vd != null ? vd / 100 : null} color="#2d7a3a" />
                <ScoreBar label="Built-up Likelihood" value={bu != null ? bu / 100 : null} color="#7d5a3a" />
                <ScoreBar label="Recent Disturbance" value={rd != null ? rd / 100 : null} color="#c0392b" />
                {snow != null && (
                  <div style={{ fontSize: '0.85rem', color: '#444', marginTop: 6 }}>
                    Seasonal snow cover: <strong>{Math.round(snow * 100)}%</strong> of year
                  </div>
                )}
                <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 8 }}>
                  Derived from Sentinel-1 SAR. Built-up is *likelihood* — high
                  VV also fires on rough rock outcrop; confirm with land-use
                  code or aerial imagery.
                  {rd == null && (
                    <> Disturbance score requires Sentinel-1 monthly time series
                    (not yet ingested for this parcel).</>
                  )}
                </div>
              </>
            );
          })()}
        </div>

        {/* Geophysics — USGS magnetic anomaly (national 1km RTP mosaic,
            optional Earth MRI ~50m overlay; task #170-magnetic-coverage).
            Standalone card so the magnetic value reads independently
            from the mineral score it feeds — buyers can sanity-check
            the score against the underlying geophysical evidence.
            Sister branches (SAR ground-cover, Hansen forest change)
            may extend this card with additional geophysical rows once
            they land — the heading is intentionally generic.
            #932 TASK-330: span 2 columns so the third card does not
            sit alone in column 1 of row 2 (lopsided). */}
        <div className="detail-card" data-testid="pd-geophysics-card" style={{ gridColumn: '1 / -1' }}>
          <h3>Geophysics</h3>
          {(() => {
            const magMean = (features && features.magnetic_anomaly_mean) ?? null;
            const magMax = (features && features.magnetic_anomaly_max) ?? null;
            const magSrc = (features && features.magnetic_anomaly_source) ?? null;
            if (magMean == null && magMax == null) {
              return (
                <p style={{ color: '#999', fontSize: '0.9rem' }}>
                  No geophysical data ingested for this parcel yet.
                </p>
              );
            }
            // Categorical strength based on |max|. Thresholds calibrated
            // for the USGS RTP grid: <50 nT = background; 50-150 = a real
            // anomaly worth noting; 150-500 = strong; >500 = very strong,
            // typically magnetite-bearing or major intrusion.
            // See scripts/ingest_magnetic_anomaly.py docstring.
            const abs = magMax != null ? Math.abs(magMax) : Math.abs(magMean);
            let label = 'Quiet';
            let color = '#7a8a9a';
            let signal = 'typical background — sedimentary or low-magnetic bedrock';
            if (abs >= 500) {
              label = 'Very Strong';
              color = '#c0392b';
              signal = 'likely magnetite-bearing rock or major intrusion';
            } else if (abs >= 150) {
              label = 'Strong';
              color = '#e67e22';
              signal = 'iron-rich or intrusive bedrock signature';
            } else if (abs >= 50) {
              label = 'Moderate';
              color = '#d4a72c';
              signal = 'detectable magnetic source in the subsurface';
            }
            // Sign matters: positive = ferromagnetic in normal polarity;
            // negative = reverse-polarized volcanics or a magnetic low
            // (sometimes a hydrothermally altered zone).
            const signLabel = magMean != null
              ? (magMean > 0 ? 'positive' : magMean < 0 ? 'negative' : 'neutral')
              : null;
            const srcLabel = magSrc === 'earth_mri'
              ? 'USGS Earth MRI (~50 m)'
              : magSrc === 'us_national'
              ? 'USGS National Mosaic (~1 km)'
              : 'USGS';
            const srcTitle = magSrc === 'earth_mri'
              ? 'USGS Earth MRI per-survey grid (~50 m line spacing). High-resolution airborne survey over Western US mineral belts.'
              : 'USGS Magnetic and related derivative GeoTIFF grids for the US and Canada (McCafferty et al., 2025). Conterminous-US 1 km mosaic, reduced-to-pole.';
            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span
                    title={`Magnetic anomaly strength: ${signal}`}
                    style={{
                      display: 'inline-block', padding: '4px 10px',
                      background: color, color: '#fff', borderRadius: 12,
                      fontSize: '0.8rem', fontWeight: 600,
                    }}
                  >
                    {label}{signLabel ? ` (${signLabel})` : ''}
                  </span>
                  <span
                    title={srcTitle}
                    style={{
                      display: 'inline-block', padding: '3px 8px',
                      background: '#1f2937', color: '#9ca3af',
                      border: '1px solid #374151', borderRadius: 8,
                      fontSize: '0.7rem',
                    }}
                  >
                    {srcLabel}
                  </span>
                </div>
                <div className="info-grid">
                  {magMean != null && (
                    <InfoRow
                      label="Magnetic Anomaly (centroid)"
                      value={`${magMean >= 0 ? '+' : ''}${magMean.toFixed(1)} nT`}
                    />
                  )}
                  {magMax != null && magMax !== magMean && (
                    <InfoRow
                      label="Magnetic Anomaly (peak in parcel)"
                      value={`${magMax >= 0 ? '+' : ''}${magMax.toFixed(1)} nT`}
                    />
                  )}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 8 }}>
                  Reduced-to-pole total magnetic intensity anomaly.
                  Strong magnetic signatures often indicate iron-rich rock,
                  metallic mineralization, or igneous intrusions in the
                  subsurface — these feed the mineral score above. The
                  signal is also a coarse proxy for geothermal potential
                  (intrusions = heat sources).
                  {magSrc === 'us_national' && (
                    <> The 1 km national mosaic shows regional trend; for
                    a parcel-scale measurement, a follow-up Earth MRI
                    survey (where available) would be needed.</>
                  )}
                </div>
              </>
            );
          })()}
        </div>

        {/* Feature data (mineral / geology / infrastructure).
            Bucket 4 (debug-only) — raw feature-vector dump. Gated
            behind `?debug=1` so the default product UI isn't dominated
            by a wall of internal feature keys. */}
        {isDebug && (<div className="detail-card">
          <h3>Feature Data</h3>
          {features && Object.keys(features).length > 0 ? (
            <div className="info-grid">
              {Object.entries(features).filter(([k]) => !HIDDEN_FEATURE_KEYS.has(k)).map(([k, v]) => (
                <InfoRow key={k} label={FEATURE_LABELS[k] || k.replace(/_/g, ' ')} value={formatFeatureValue(k, v)} />
              ))}
            </div>
          ) : parcel.features && Object.keys(parcel.features).length > 0 ? (
            <div className="info-grid">
              {Object.entries(parcel.features).filter(([k]) => !HIDDEN_FEATURE_KEYS.has(k)).map(([k, v]) => (
                <InfoRow key={k} label={FEATURE_LABELS[k] || k.replace(/_/g, ' ')} value={formatFeatureValue(k, v)} />
              ))}
            </div>
          ) : (
            <p style={{ color: '#999', fontSize: '0.9rem' }}>No feature data yet. Mineral, geology, and infrastructure features will appear here once the data pipeline ingests them.</p>
          )}
        </div>)}
      </div>}

      {/* Source info — Phase C PR 11: surface listing_id when the
          backend supplies it so the new listings table is visible
          end-to-end. listing_id only appears for parcels that have
          a matched listings row (Realie-attached today; RC- shims
          are still listings.parcel_id IS NULL). */}
      <div style={{ textAlign: 'center', fontSize: '0.75rem', color: '#bbb', marginTop: 24, paddingBottom: 24 }}>
        Source: {parcel.source_id || 'realie'}
        {parcel.created_at && ` | Added: ${fmtDate(parcel.created_at)}`}
        {parcel.rentcast_id && ` | RentCast ID: ${parcel.rentcast_id}`}
        {parcel.listing_id && ` | Listing #${parcel.listing_id}`}
      </div>

      {/* In-app report viewer modal. Mounted at the page root so its
          fixed-position overlay covers the full viewport. */}
      {showReportViewer && (
        <ReportViewer
          parcelId={parcelId}
          onClose={() => setShowReportViewer(false)}
        />
      )}

      {/* Flag-bad-data modal. Same root-level placement as
          ReportViewer for the same reason — fixed-position overlay.
          onFlagSubmitted optimistically bumps the local counter
          and then re-fetches the canonical count so the indicator
          stays correct even if multiple users submit concurrently. */}
      {showFlagModal && (
        <FlagDataModal
          parcelId={parcelId}
          onClose={() => setShowFlagModal(false)}
          onFlagSubmitted={() => {
            setFlagCounts((prev) => ({
              count: prev.count + 1,
              unresolved: prev.unresolved + 1,
            }));
            // Re-fetch to reconcile with the server in case another
            // tab/user submitted at the same time.
            listDataFlagsForParcel(parcelId)
              .then((fl) => setFlagCounts({
                count: fl?.count ?? 0,
                unresolved: fl?.unresolved_count ?? 0,
              }))
              .catch(() => {});
          }}
        />
      )}

      {/* Agent trajectory transcript modal (#122). Backed by
          GET /api/v1/parcels/research/{id}/transcript — shows the
          play-by-play: what the agent searched, what URLs it tried,
          which ones succeeded, and the final structured extract. */}
      {showTranscript && (
        <div
          onClick={() => setShowTranscript(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, padding: 20,
              maxWidth: 900, width: '90%', maxHeight: '85vh',
              overflowY: 'auto', boxShadow: '0 6px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Research Transcript</h3>
              <button className="btn btn-outline" onClick={() => setShowTranscript(null)}>Close</button>
            </div>
            {transcriptData === 'loading' && <p style={{ color: '#666' }}>Loading transcript…</p>}
            {transcriptData && transcriptData._error && (
              <p style={{ color: '#a33' }}>Failed to load: {transcriptData._error}</p>
            )}
            {transcriptData && typeof transcriptData === 'object' && !transcriptData._error && (
              <>
                <div style={{ fontSize: '0.82rem', color: '#666', marginBottom: 12 }}>
                  <strong>{transcriptData.parcel_id}</strong> · {transcriptData.county}, {transcriptData.state} ·{' '}
                  {transcriptData.model || 'claude-opus-4-7'} ·{' '}
                  {transcriptData.auth_mode === 'max_oauth' ? 'Max OAuth' : 'API key'} ·{' '}
                  {transcriptData.elapsed_sec?.toFixed(1)}s ·{' '}
                  {transcriptData.tool_call_count} tool calls ·{' '}
                  {transcriptData.input_tokens?.toLocaleString()} in / {transcriptData.output_tokens?.toLocaleString()} out tokens
                </div>
                <div style={{ marginBottom: 12, padding: 8, background: '#f8fafc', borderRadius: 4, fontSize: '0.85rem' }}>
                  <strong>Outcome:</strong>{' '}
                  <span style={{ color: transcriptData.found ? '#0d8e3a' : '#a33' }}>
                    {transcriptData.found ? 'Found' : 'Not found'}
                  </span>
                  {transcriptData.confidence && <> · confidence <strong>{transcriptData.confidence}</strong></>}
                  {transcriptData.source_site && (
                    <div style={{ marginTop: 4, fontSize: '0.78rem', color: '#666' }}>
                      Source: <a href={transcriptData.source_url_used || transcriptData.source_site} target="_blank" rel="noopener noreferrer">{transcriptData.source_site}</a>
                    </div>
                  )}
                </div>
                <h4 style={{ marginTop: 16, marginBottom: 8 }}>Timeline</h4>
                <ol style={{ fontSize: '0.82rem', fontFamily: 'ui-monospace, monospace', paddingLeft: 28, lineHeight: 1.5 }}>
                  {(transcriptData.timeline || []).map((e, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      <span style={{
                        display: 'inline-block', minWidth: 78,
                        padding: '0 6px', borderRadius: 3, marginRight: 6,
                        fontSize: '0.72rem', fontWeight: 600,
                        background: e.tool === 'web_search' ? '#fff0c2' :
                                    e.tool === 'http_get'   ? '#cfe9ff' :
                                                              '#e6e6e6',
                      }}>{e.tool}</span>
                      {e.query && <span>"{e.query}"</span>}
                      {e.url && (
                        <span>
                          <a href={e.url} target="_blank" rel="noopener noreferrer" style={{ color: '#0a58ca', wordBreak: 'break-all' }}>{e.url}</a>
                          {e.status != null && (
                            <span style={{ marginLeft: 6, color: e.status >= 400 ? '#a33' : '#0d8e3a' }}>
                              → {e.status}
                              {e.bytes != null && <span style={{ color: '#999' }}> ({e.bytes.toLocaleString()}b)</span>}
                            </span>
                          )}
                          {e.error && <span style={{ marginLeft: 6, color: '#a33' }}>· {e.error}</span>}
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
                {transcriptData.final_text && (
                  <>
                    <h4 style={{ marginTop: 16, marginBottom: 8 }}>Agent's final reasoning</h4>
                    <pre style={{
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      fontSize: '0.82rem', padding: 10, background: '#f8fafc',
                      borderRadius: 4, maxHeight: 300, overflowY: 'auto',
                    }}>{transcriptData.final_text}</pre>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParcelDetail;
